import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@es-tools/shared';
import Header from '../components/Header';
import growthReportingMap from '../assets/growth-reporting-map.svg';
import googleLogo from '../assets/source-logos/google.svg';
import metaLogo from '../assets/source-logos/meta.svg';
import shopifyLogo from '../assets/source-logos/shopify.svg';
import {
  fetchAutomationBoard,
  submitAutomationRequest,
  type AutomationBoardItem,
  type AutomationRequestDraft,
} from '../services/automationBoard';
import {
  askSchemaExplorer,
  fetchSchemaCatalog,
  type SchemaAskResponse,
  type SchemaCatalogResponse,
} from '../services/schemaExplorer';
import dataPlatformMetadata from '../content/data-platform-metadata.json';
import '../styles/data-platform-guide.css';

type OverviewCard = {
  title: string;
  description: string;
  accent: string;
};

type QuestionCard = {
  question: string;
  source: string;
  guidance: string;
};

type WarehouseCard = {
  title: string;
  tables: string[];
  purpose: string;
  useWhen: string;
  avoidWhen: string;
};

type AccessCard = {
  title: string;
  items: string[];
};

type SecretFamilyRow = {
  family: string;
  examples: string;
  usedFor: string;
  whenNeeded: string;
  notes: string;
};

type AwsCard = {
  title: string;
  body: string;
};

type LinkCard = {
  title: string;
  description: string;
  url: string;
  label: string;
  tab?: TabId;
  sectionId?: string;
};

type LinkSection = {
  title: string;
  description: string;
  links: LinkCard[];
};

type SupersetDashboard = {
  title: string;
  owner: string;
  url: string;
  slug?: string;
  sensitive: boolean;
  group: 'Finance' | 'Operations' | 'Retention' | 'Marketing' | 'Executive';
};

type RoleCard = {
  role: string;
  useFor: string;
  startWith: string;
  avoid: string;
  linkedReport?: string;
};

type RuleCard = {
  title: string;
  body: string;
};

type CaveatCard = {
  title: string;
  body: string;
  severity: 'Important' | 'In progress' | 'Planned';
};

type ClaudeStep = {
  step: string;
  title: string;
  body: string;
};

type ClaudeSurface = {
  title: string;
  useFor: string;
  notFor: string;
};

type Platform = 'Sheets' | 'Custom Dashboard' | 'Superset' | 'Slack' | 'Internal App' | 'Notion';

type ReportCard = {
  title: string;
  category: string;
  purpose: string;
  status: 'Live' | 'In progress' | 'Needs QA';
  owner: string;
  statusDate: string;
  platform: Platform;
  url?: string;
};

type SourceRow = {
  source: string;
  direct: string;
  intermediary: string;
  freshness: string;
  intradayTrust: 'High' | 'Medium' | 'Low';
  canonicalUse: string;
};

type CanonicalSourceRow = {
  sourceSystem: string;
  steward: string;
  canonicalTables: string[];
  notes: string;
};

type DetailedSourceRow = {
  sourceSystem: string;
  category: string;
  primaryData: string;
  implementationSurface: string;
  cadence: string;
  status: string;
};

type OperationalJob = {
  job: string;
  domain: 'Growth' | 'Attribution' | 'Retention' | 'CX / Ops' | 'Finance' | 'Platform';
  businessOutput: string;
  cadence: string;
};

type MorningReadinessRow = {
  sourceOrPipeline: string;
  expectedWindow: string;
  safeByNine: 'Yes' | 'Conditional' | 'No';
  notes: string;
};

type SlackSurface = {
  surface: string;
  domain: string;
  purpose: string;
  cadence: string;
};

type SourceFamilyCard = {
  title: string;
  systems: string[];
  warehouse: string[];
  useFor: string;
};

type AggregatedSourceProfile = {
  title: string;
  sourceType: string;
  contains: string;
  bestFor: string;
  freshness: string;
  notFor: string;
};

type DataPathCard = {
  title: string;
  examples: string[];
  bestFor: string;
  tradeoff: string;
  notIdealFor: string;
};

type SourceEcosystemGroup = {
  title: string;
  blurb: string;
  sources: string[];
};

type SourceMark = {
  label: string;
  image?: string;
  accent: string;
  kind: 'Direct source' | 'Modeled source' | 'Delivery surface' | 'System input';
  targetTab: TabId;
  targetSection: string;
  textColor?: string;
};

type AiPillar = {
  title: string;
  body: string;
};


type AiWorkflowCard = {
  title: string;
  steps: string[];
};

type AiOnboardingTrack = {
  title: string;
  audience: string;
  steps: string[];
  outcome: string;
};


type UnifiedToolCard = {
  id: string;
  title: string;
  tagline: string;
  plainEnglish: string;
  bestFor: string;
  accessNote: string;
  howToStart?: string;
  launchUrl?: string;
  launchLabel?: string;
  connectedSystems: string;
  defaultPackage: string;
  avoidFor: string;
  expandedByDefault: boolean;
};

type ExternalResource = {
  title: string;
  description: string;
  url: string;
};

type DownloadArtifact = {
  title: string;
  description: string;
  href: string;
  label: string;
};

type OnPageResource = {
  id: string;
  title: string;
  description: string;
  href: string;
  category: 'Quickstart' | 'Template' | 'Form' | 'Reference';
  audience: string;
};

type PublicDocResource = OnPageResource & {
  downloadLabel: string;
  resourceGroup: 'starter' | 'onboarding' | 'reporting';
  includeInPagePreview: boolean;
};


type AiFaqCard = {
  question: string;
  answer: string;
};


type AiLaunchContext = {
  pageUrl: string;
  llmsUrl: string;
  llmsFullUrl: string;
  aiQuickstartUrl: string;
  awsGuideUrl: string;
  dataCatalogUrl: string;
  reportingTrackerUrl: string;
  brainTrackerUrl: string;
};

type AiLaunchCard = {
  id: 'chatgpt' | 'claude' | 'claude-code' | 'codex';
  title: string;
  category: 'Web assistant' | 'Coding agent';
  description: string;
  helper: string;
  actionLabel: string;
  completionLabel: string;
  openUrl?: string;
  buildPrompt: (context: AiLaunchContext) => string;
};

type TabId = 'start' | 'sources' | 'dashboards' | 'ai' | 'technical';

type AiCenterNavigationHandlers = {
  openHome?: () => void;
  openDepartments?: () => void;
  openBuilderPatterns?: () => void;
};

type DataPlatformGuideProps = {
  theme?: 'portal' | 'marsmen';
  headerSlot?: React.ReactNode;
  heroKicker?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  footerText?: string;
  footerLinks?: Array<{ label: string; href: string }>;
  aiCenterNavigationHandlers?: AiCenterNavigationHandlers;
};

const PAGE_LAST_REVIEWED = dataPlatformMetadata.pageLastReviewed;
const aiPublicDocs = dataPlatformMetadata.aiPublicDocs as PublicDocResource[];
const reportingPublicDocs = dataPlatformMetadata.reportingPublicDocs as PublicDocResource[];
const stripPublicDocResource = ({
  downloadLabel: _downloadLabel,
  resourceGroup: _resourceGroup,
  includeInPagePreview: _includeInPagePreview,
  ...resource
}: PublicDocResource): OnPageResource => resource;

const overviewCards: OverviewCard[] = [
  {
    title: 'Source systems',
    description: 'These are the systems where the business event actually happens: Meta, Google Ads, Shopify, Amazon, Stay, Gorgias, Tatari, and other direct platforms.',
    accent: 'var(--brand-green)',
  },
  {
    title: 'Data lake + warehouse',
    description: 'We land raw and modeled data in AWS so it can be cataloged once, queried consistently, and reused across dashboards, Slack jobs, and internal apps.',
    accent: 'var(--mars-men-orange)',
  },
  {
    title: 'Reporting surfaces',
    description: 'Sheets, Superset, the Brain Dashboard, Slack drops, and internal tools are delivery surfaces. They should consume the right truth layer, not invent new business logic.',
    accent: 'var(--hollow-socks-blue)',
  },
  {
    title: 'Claude Code',
    description: 'Claude Code should help the team trace jobs, query Athena, inspect code, and document decisions, but only inside explicit source-of-truth and access boundaries.',
    accent: 'var(--primary-black)',
  },
];

const sourceEcosystemGroups: SourceEcosystemGroup[] = [
  {
    title: 'Paid media',
    blurb: 'Direct ad-platform truth and modeled media layers.',
    sources: ['Meta Ads', 'Google Ads', 'Amazon Ads', 'Axon / AppLovin', 'Tatari', 'Northbeam', 'Triple Whale'],
  },
  {
    title: 'Commerce and retention',
    blurb: 'Order truth, customer lifecycle, and messaging systems.',
    sources: ['Shopify', 'Amazon SP-API', 'Klaviyo', 'Postscript', 'Stay AI', 'HAM', 'Recharge'],
  },
  {
    title: 'CX and operations',
    blurb: 'Shipment visibility, support, and operational follow-up.',
    sources: ['Gorgias', 'TrackingMore', 'ShipHero', 'Mission Control', 'Junip'],
  },
  {
    title: 'Finance and signals',
    blurb: 'Financial controls, banking, and incrementality inputs.',
    sources: ['Bill.com', 'Teller', 'KNO', 'Haus'],
  },
  {
    title: 'Offline media and experiments',
    blurb: 'CTV, podcast, surveys, and incrementality-support systems that need tighter source contracts.',
    sources: ['Tatari', 'Podscale', 'Podscribe', 'KNO', 'Haus'],
  },
];

const sourceMarks: SourceMark[] = [
  { label: 'Meta', image: metaLogo, accent: '#EAF2FF', kind: 'Direct source', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Google Ads', image: googleLogo, accent: '#F3F8FF', kind: 'Direct source', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Shopify', image: shopifyLogo, accent: '#EEF8EA', kind: 'Direct source', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Amazon', accent: '#FFF4E6', kind: 'Direct source', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Northbeam', accent: '#F1EDFF', kind: 'Modeled source', targetTab: 'sources', targetSection: 'modeled-sources' },
  { label: 'Triple Whale', accent: '#E8FBFF', kind: 'Modeled source', targetTab: 'sources', targetSection: 'modeled-sources' },
  { label: 'Axon / AppLovin', accent: '#FFF1F1', kind: 'Direct source', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Tatari', accent: '#FFF4F8', kind: 'Direct source', targetTab: 'sources', targetSection: 'detailed-source-inventory-section' },
  { label: 'Podscale / Podscribe', accent: '#F5F1FF', kind: 'System input', targetTab: 'sources', targetSection: 'known-caveats-section' },
  { label: 'Klaviyo', accent: '#ECFBF7', kind: 'System input', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Postscript', accent: '#F8F3FF', kind: 'System input', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Stay AI', accent: '#EEF3FF', kind: 'System input', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'HAM', accent: '#F7F0E8', kind: 'System input', targetTab: 'sources', targetSection: 'source-chooser' },
  { label: 'Gorgias', accent: '#EEF7FF', kind: 'System input', targetTab: 'sources', targetSection: 'detailed-source-inventory-section' },
  { label: 'TrackingMore', accent: '#F4F7FB', kind: 'System input', targetTab: 'sources', targetSection: 'detailed-source-inventory-section' },
  { label: 'ShipHero', accent: '#FFF2EA', kind: 'System input', targetTab: 'sources', targetSection: 'detailed-source-inventory-section' },
  { label: 'Bill.com', accent: '#EDF8FF', kind: 'System input', targetTab: 'sources', targetSection: 'detailed-source-inventory-section' },
  { label: 'Teller', accent: '#EEF6F6', kind: 'System input', targetTab: 'sources', targetSection: 'detailed-source-inventory-section' },
  { label: 'Junip', accent: '#FFF7E9', kind: 'System input', targetTab: 'dashboards', targetSection: 'slack-reports-section' },
  { label: 'Mission Control', accent: '#F3F1FF', kind: 'Delivery surface', targetTab: 'dashboards', targetSection: 'current-report-surfaces-section' },
  { label: 'KNO', accent: '#FFF8E8', kind: 'System input', targetTab: 'sources', targetSection: 'known-caveats-section' },
  { label: 'Haus', accent: '#F8F8F2', kind: 'System input', targetTab: 'sources', targetSection: 'known-caveats-section' },
];

const awsCards: AwsCard[] = [
  {
    title: 'AWS is the shared operating layer',
    body: 'AWS is where we land, store, catalog, query, and deliver the reporting data. It is usually not where the original business event happens. Meta, Google Ads, Shopify, Axon, and Northbeam are the source systems. AWS is the shared layer that makes those sources reusable.',
  },
  {
    title: 'S3 is the data lake',
    body: 'S3 holds raw and lightly processed files over time. This is where landed exports and ingestion outputs live so we can keep historical data even when a source UI changes.',
  },
  {
    title: 'Glue + Athena are the warehouse/query layer',
    body: 'Glue catalogs the datasets and Athena lets the team and Claude Code query them. When someone says "query the warehouse," this is usually what they mean.',
  },
  {
    title: 'Amazon Bedrock is the recommended model runtime',
    body: 'If the team wants Claude or other foundation models inside AWS, Bedrock should be the default path. It keeps model access inside IAM, billing, and account governance instead of scattering vendor-specific access across local-only setups.',
  },
  {
    title: 'Lambda / ECS / EventBridge run the jobs',
    body: 'These services pull data from APIs, transform it, refresh reporting tables, and keep sheets or dashboards up to date. The Brain reporting runtime now runs on ECS with morning scheduling, while EventBridge still handles the orchestration clock.',
  },
  {
    title: 'API Gateway + analytics-brain Lambda power the Brain app',
    body: 'The Brain frontend is its own internal app, but its data comes from authenticated Brand API routes under `/analytics/brain-dashboard/v1/*`, served by the `marsmen-analytics-brain-production` Lambda.',
  },
  {
    title: 'The Brain is now a two-phase Step Functions workflow',
    body: 'The live Brain reporting path is EventBridge Scheduler -> Step Functions -> ECS RefreshPhase -> ECS PublishPhase. That split replaced the old single-runtime pattern and is now the canonical morning reporting control plane.',
  },
  {
    title: 'Mission Control is also a Step Functions + ECS refresh surface',
    body: 'Mission Control is not just a frontend app. Its dataset refresh path runs through a Standard Step Functions state machine that launches an ECS task on the shared batch cluster, separate from the Brain runtimes.',
  },
  {
    title: 'Raw ingestion and reporting orchestration are separate concerns',
    body: 'Raw source landing belongs in the data-lake layer. Reporting marts, Brain refresh logic, and delivery contracts belong in the reporting layer. Keeping those responsibilities separate makes the stack easier to reason about and safer to change.',
  },
];

const aiPillars: AiPillar[] = [
  {
    title: 'AI should compress work, not create new truth',
    body: 'At Mars Men, AI is most useful when it speeds up analysis, documentation, operations, and creative iteration. It should not silently invent numbers, redefine source truth, or replace direct system ownership.',
  },
  {
    title: 'Claude is a working layer, not just a chatbot',
    body: 'Claude becomes useful when it can inspect repo code, query Athena, open dashboards, trace workflows, and write back better documentation. That is an operating model, not just a Q&A surface.',
  },
  {
    title: 'The best AI use cases are connected',
    body: 'The biggest gains come when AI is connected to AWS, Sheets, Superset, Slack, Notion, and the codebase. Standalone chat is useful, but connected context is where the workflow becomes durable.',
  },
  {
    title: 'Every AI workflow needs guardrails',
    body: 'The team should be explicit about which workflows are read-only, which can draft, and which can execute. Access packages and source-of-truth rules matter more than clever prompts.',
  },
];


const unifiedToolCards: UnifiedToolCard[] = [
  {
    id: 'claude',
    title: 'Claude',
    tagline: 'Everyone has access',
    plainEnglish: 'The easiest starting point. Ask questions, draft documents, summarize notes, or think through a problem. If you can use a search engine, you can use Claude.',
    bestFor: 'Writing, summarization, structured thinking, quick research, and light business analysis.',
    accessNote: 'Everyone on the team already has access. Just go to claude.ai and sign in.',
    launchUrl: 'https://claude.ai/new',
    launchLabel: 'Open Claude',
    connectedSystems: 'Docs, screenshots, copied context, and manual links.',
    defaultPackage: 'No repo or AWS required.',
    avoidFor: 'Assuming it has live access to internal systems or production data without explicit connectors.',
    expandedByDefault: false,
  },
  {
    id: 'cowork',
    title: 'Claude CoWork',
    tagline: 'Everyone has access',
    plainEnglish: 'A shared team workspace where multiple people can reason through the same problem together and keep the context in one place. Think of it as a Claude session the whole team can see.',
    bestFor: 'Collaborative reasoning, async working sessions, dashboards, Slack integration, and team-visible thinking.',
    accessNote: 'Everyone on the team already has access through the shared workspace. No setup needed.',
    launchUrl: 'https://claude.ai/new',
    launchLabel: 'Open CoWork',
    connectedSystems: 'Shared context, collaborative workspaces, Slack, Notion, and approved connector surfaces.',
    defaultPackage: 'Collaborative workspace plus approved connector access.',
    avoidFor: 'Repo-local engineering tasks, terminal-heavy debugging, or AWS-heavy implementation work.',
    expandedByDefault: false,
  },
  {
    id: 'claude-code',
    title: 'Claude Code',
    tagline: 'Install required',
    plainEnglish: 'An AI teammate that works inside a real codebase. It can open files, read docs, run commands, query Athena, and help implement changes — from the terminal or your IDE.',
    bestFor: 'Repo work, Athena queries, code changes, workflow debugging, data investigation, and internal tooling.',
    accessNote: 'Requires a one-time local install. Works on Mac, Windows, and Linux.',
    howToStart: 'Install: npm install -g @anthropic-ai/claude-code. Then clone the repo and run claude in the project folder. It reads the repo-level CLAUDE.md for context automatically.',
    launchLabel: 'Copy install command',
    connectedSystems: 'Local repo, AWS via local profile, Athena, Bedrock, docs, scripts, and configured connectors/MCPs.',
    defaultPackage: 'Local repo access plus the read-only analytics AWS package through marsmen-direct.',
    avoidFor: 'Unbounded production mutation or acting like a source of business truth by itself.',
    expandedByDefault: true,
  },
  {
    id: 'bedrock',
    title: 'Amazon Bedrock',
    tagline: 'AWS access required',
    plainEnglish: 'The AWS-approved way to use Claude programmatically. Use this when you want to build AI into an app, workflow, or automation without passing around separate vendor API keys.',
    bestFor: 'Embedding Claude in internal tools, automations, and production workflows inside the Mars Men AWS boundary.',
    accessNote: 'Requires the marsmen-direct AWS profile and Bedrock model access. Ask Hunter if you need this.',
    howToStart: 'Use the marsmen-direct profile with bedrock:InvokeModel in us-east-1. Prototype with the Bedrock runtime first.',
    launchLabel: 'Open AWS Console',
    launchUrl: 'https://631046354185.signin.aws.amazon.com/console',
    connectedSystems: 'AWS IAM, Bedrock model access, Claude-family models, and other foundation models exposed through Bedrock.',
    defaultPackage: 'AWS access with bedrock:InvokeModel and approved model entitlement.',
    avoidFor: 'Treating the model runtime like a data source or bypassing source-of-truth rules.',
    expandedByDefault: false,
  },
];

const businessResources: ExternalResource[] = [
  {
    title: 'Claude 101',
    description: 'Anthropic Academy course — learn Claude\'s core features, practical workflows, and best practices for real work. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/claude-101',
  },
  {
    title: 'AI Fluency: Framework and Foundations',
    description: 'Anthropic course co-created with university professors. Teaches the "4D Workflow" — a repeatable process for breaking down tasks, delegating to Claude, directing output, and delivering results. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/ai-fluency-framework-foundations',
  },
  {
    title: 'The Future of AI at Work: Introducing CoWork',
    description: 'Official Anthropic webinar from Jan 30, 2026. Explains the team collaboration surface.',
    url: 'https://www.anthropic.com/webinars/future-of-ai-at-work-introducing-cowork',
  },
  {
    title: 'Best Practices: Claude for Excel and PowerPoint',
    description: 'Official Anthropic webinar from Mar 18, 2026. Directly relevant for anyone on the team working in spreadsheets.',
    url: 'https://www.anthropic.com/webinars/best-practices-for-claude-in-excel-and-powerpoint',
  },
  {
    title: 'Getting Started with Claude',
    description: 'Official Claude help center onboarding guide.',
    url: 'https://support.claude.com/en/articles/8114491-get-started-with-claude',
  },
  {
    title: 'Anthropic Learn Hub',
    description: 'Central page for all Anthropic learning resources — start here if unsure.',
    url: 'https://www.anthropic.com/learn',
  },
  {
    title: 'Claude Code in Action',
    description: 'Anthropic Academy course — learn Claude Code workflows, not just for developers. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/claude-code-in-action',
  },
  {
    title: 'Claude Code in an Hour',
    description: 'Official Anthropic webinar from Mar 4, 2026 — live demo from install to shipping a commit. Good intro for anyone curious.',
    url: 'https://www.anthropic.com/webinars/claude-code-in-an-hour-a-developers-intro',
  },
  {
    title: 'Claude Code Docs',
    description: 'Official docs — overview, setup, what you can do with it.',
    url: 'https://code.claude.com/docs/en/overview',
  },
];

const developerResources: ExternalResource[] = [
  {
    title: 'Claude Code in Action (Academy)',
    description: 'Using Claude Code to accelerate real dev workflows. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/claude-code-in-action',
  },
  {
    title: 'Building with the Claude API',
    description: 'Full API coverage — streaming, auth, structured output, production patterns. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/claude-with-the-anthropic-api',
  },
  {
    title: 'Claude in Amazon Bedrock',
    description: 'Deploying Claude through Bedrock — tool use, RAG, agents. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/claude-in-amazon-bedrock',
  },
  {
    title: 'Claude with Google Cloud Vertex AI',
    description: 'Claude on Vertex — relevant if we ever use GCP. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/claude-with-google-vertex',
  },
  {
    title: 'Introduction to Model Context Protocol',
    description: 'Building MCP servers and clients with the Python SDK. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/',
  },
  {
    title: 'Introduction to Agent Skills',
    description: 'Building, configuring, and sharing Skills in Claude Code. Free, with certificate.',
    url: 'https://anthropic.skilljar.com/introduction-to-agent-skills',
  },
  {
    title: 'Claude Code in an Hour: A Developer\'s Intro (webinar)',
    description: 'Official Anthropic webinar from Mar 4, 2026.',
    url: 'https://www.anthropic.com/webinars/claude-code-in-an-hour-a-developers-intro',
  },
  {
    title: 'Claude Code Advanced Patterns: Subagents, MCP, and Scaling (webinar)',
    description: 'Official Anthropic webinar from Mar 24, 2026.',
    url: 'https://www.anthropic.com/webinars/claude-code-advanced-patterns',
  },
  {
    title: 'Ship Code Faster with Claude Code on Vertex AI (webinar)',
    description: 'Official Anthropic webinar from Apr 2, 2026.',
    url: 'https://www.anthropic.com/webinars/ship-code-faster-with-claude-code-on-vertex-ai',
  },
  {
    title: 'Building Products on Claude Opus 4.6 (webinar)',
    description: 'Shortcut + Hex case study, Feb 19, 2026.',
    url: 'https://www.anthropic.com/webinars/building-products-on-claude-opus-4-6-a-customer-success-story-with-shortcut-and-hex',
  },
  {
    title: 'Claude Code Docs',
    description: 'Official docs — overview, setup, configuration, usage.',
    url: 'https://code.claude.com/docs/en/overview',
  },
  {
    title: 'Claude API Docs',
    description: 'Full API reference, models overview, release notes.',
    url: 'https://platform.claude.com/docs/en/home',
  },
  {
    title: 'Anthropic Courses (GitHub)',
    description: 'All educational courses as Jupyter notebooks.',
    url: 'https://github.com/anthropics/courses',
  },
  {
    title: 'Claude Code on Bedrock: Getting Started',
    description: 'AWS Builder walkthrough — setting up Claude Code with Bedrock.',
    url: 'https://builder.aws.com/content/39M5ctlGOnaUTx5qNZNgqjh0oou/claude-code-on-bedrock-part-1-getting-started',
  },
  {
    title: 'Bedrock Code Interpreter (video)',
    description: 'Creating an agent with Claude that writes and runs Python in a sandbox.',
    url: 'https://aws.amazon.com/video/watch/c2927e6ad3a/',
  },
  {
    title: 'AWS re:Invent 2025 On-Demand',
    description: 'All recorded sessions — search "Bedrock" for AIM-series deep dives.',
    url: 'https://reinvent.awsevents.com/on-demand/',
  },
  {
    title: 'Prompt Engineering with Claude on AWS',
    description: 'AWS Workshop — hands-on prompt engineering on Bedrock.',
    url: 'https://catalog.us-east-1.prod.workshops.aws/workshops/0644c9e9-5b82-45f2-8835-3b5aa30b1848/en-US',
  },
  {
    title: 'MCP Official Docs',
    description: 'Getting started guide, spec, and tutorials.',
    url: 'https://modelcontextprotocol.io/docs/getting-started/intro',
  },
  {
    title: 'MCP GitHub',
    description: 'SDKs, reference servers, and the protocol spec.',
    url: 'https://github.com/modelcontextprotocol',
  },
];

const onboardingSurfaceOptions = ['Claude', 'CoWork', 'Claude Code', 'Bedrock', 'Connector workspace'];
const onboardingAccessOptions = ['Read-only AWS', 'Bedrock invocation', 'Secrets Manager', 'Repo access', 'Connector access'];

const aiWorkflowCards: AiWorkflowCard[] = [
  {
    title: 'Data investigation',
    steps: [
      'Start with the business question and truth layer.',
      'Use Claude Code to inspect the catalog and relevant warehouse family.',
      'Query Athena or open the relevant surface.',
      'Write back the decision, caveat, or finding into docs.',
    ],
  },
  {
    title: 'Reporting QA',
    steps: [
      'Open the target sheet or dashboard.',
      'Trace the upstream source family and job cadence.',
      'Compare direct source truth versus modeled or aggregated output.',
      'Document the mismatch and fix the consumer or the pipeline.',
    ],
  },
  {
    title: 'Operational support',
    steps: [
      'Use Slack or dashboard context to identify the problem surface.',
      'Trace the owning job, table, or delivery surface.',
      'Use Claude Code for repo and AWS-side debugging.',
      'Escalate to the human owner when the issue is source or business logic.',
    ],
  },
  {
    title: 'Creative and strategy support',
    steps: [
      'Use Claude for planning, analysis framing, and documentation.',
      'Use connected tools for campaign data, reporting context, and historical notes.',
      'Keep human decision-makers on final spend, attribution, and business-logic calls.',
    ],
  },
];

const aiOnboardingTracks: AiOnboardingTrack[] = [
  {
    title: 'AI user',
    audience: 'Teammates who mainly need chat, summarization, drafting, and structured thinking.',
    steps: [
      'Get invited to the approved Claude workspace or equivalent team surface.',
      'Read the AI COE page so you know the source-of-truth and access rules.',
      'Start with lightweight workflows: summaries, briefs, notes, and planning support.',
      'Escalate to connector-backed or AWS-backed access only when the work actually needs it.',
    ],
    outcome: 'A safe starting point for people who need AI help without local repo or AWS access.',
  },
  {
    title: 'AI analyst / operator',
    audience: 'People who need AI plus dashboards, docs, Sheets, Slack, or internal app context.',
    steps: [
      'Start with the approved chat or collaborative surface.',
      'Get invited to the required shared workspaces and connector-backed tools.',
      'Use the dashboard, documentation, and source-policy sections before asking AI to answer business questions.',
      'Treat connected AI as an access layer, not as the source of truth itself.',
    ],
    outcome: 'A business operator who can work faster across connected systems without overreaching into engineering or infra mutation.',
  },
  {
    title: 'Claude Code / builder',
    audience: 'Engineers, analysts, or operators who need local repo access, shell access, and AWS-backed investigation.',
    steps: [
      'Clone the repo and read the repo-level instruction files first.',
      'Get the read-only analytics AWS package, usually through the `marsmen-direct` profile.',
      'Verify Athena, Glue, S3 query-results, and Bedrock access before asking for any broader permissions.',
      'Only request Secrets Manager or write access when the exact workflow justifies it.',
    ],
    outcome: 'A builder who can inspect code, query Athena, and trace jobs safely inside explicit access boundaries.',
  },
  {
    title: 'Bedrock API / integration builder',
    audience: 'People building app, API, or automation workflows that invoke Claude through AWS.',
    steps: [
      'Start with the read-only analytics package and confirm the correct AWS account/profile.',
      'Request `bedrock:InvokeModel` and the specific approved model access you need in `us-east-1`.',
      'Prototype with the Bedrock runtime first instead of scattering vendor API keys.',
      'Document the runtime, model IDs, and allowed use cases so the team can support it later.',
    ],
    outcome: 'A governed path for using Claude-family models inside the AWS permission and billing boundary.',
  },
];


const aiDownloadArtifacts: DownloadArtifact[] = [
  {
    title: 'llms.txt index',
    description: 'Short machine-readable index for the data-platform guide, public docs, and AI COE resources.',
    href: '/llms.txt',
    label: 'Open llms.txt',
  },
  {
    title: 'llms-full.txt handbook',
    description: 'Longer machine-readable handbook with the operating model, source rules, schedules, and current public resources.',
    href: '/llms-full.txt',
    label: 'Open llms-full.txt',
  },
  ...aiPublicDocs
    .filter((resource) => resource.resourceGroup === 'starter')
    .map((resource) => ({
      title: resource.title,
      description: resource.description,
      href: resource.href,
      label: resource.downloadLabel,
    })),
];

const aiOnboardingForms: DownloadArtifact[] = aiPublicDocs
  .filter((resource) => resource.resourceGroup === 'onboarding')
  .map((resource) => ({
    title: resource.title,
    description: resource.description,
    href: resource.href,
    label: resource.downloadLabel,
  }));

const aiOnPageResources: OnPageResource[] = aiPublicDocs
  .filter((resource) => resource.includeInPagePreview)
  .map(stripPublicDocResource);

const operationalOnPageResources: OnPageResource[] = reportingPublicDocs
  .filter((resource) => resource.includeInPagePreview)
  .map(stripPublicDocResource);

const portalReadableResources: OnPageResource[] = [
  ...aiOnPageResources,
  ...operationalOnPageResources,
];


const defaultAutomationIdeas: AutomationBoardItem[] = [
  {
    id: 'example-1',
    title: 'Morning pacing summary',
    automationStatus: 'Not started',
    workflowStatus: 'Placeholder',
    department: 'Marketing',
    priority: 'High',
    brands: ['Mars Men'],
    problem: 'Someone opens several sheets, copies numbers into Slack, and adds context by hand.',
    overview: 'Intake example: a repeatable morning summary with the right freshness checks and source notes.',
    summary: 'A repeatable morning summary with the right freshness checks and source notes.',
    url: '',
    createdAt: '2026-04-05',
    updatedAt: '2026-04-05',
  },
  {
    id: 'example-2',
    title: 'Shipment issue escalation queue',
    automationStatus: 'In progress',
    workflowStatus: 'In Progress',
    department: 'Ops',
    priority: 'Medium',
    brands: ['Mars Men'],
    problem: 'The team checks Mission Control, Slack, and tracking updates separately.',
    overview: 'Intake example: rank which shipment issues are most worth escalation first.',
    summary: 'A single ranked queue or daily summary showing the issues most worth action.',
    url: '',
    createdAt: '2026-04-05',
    updatedAt: '2026-04-05',
  },
];

const automationDepartmentOptions = ['General / Admin', 'Product', 'Ops', 'Finance', 'Marketing'];
const automationPriorityOptions = ['High', 'Medium', 'Low'];
const automationBrandOptions = ['Mars Men', 'Hollow'];
const automationStatusOrder = ['Not started', 'In progress', 'Live'];


const aiFaqCards: AiFaqCard[] = [
  {
    question: 'Do I need AWS access to use AI at all?',
    answer: 'No. Most people can start with Claude or Claude CoWork for writing, planning, and summarization. AWS access only becomes necessary when the work needs live data, a repo, or a programmatic AI integration.',
  },
  {
    question: 'What is the difference between Claude and Claude Code?',
    answer: 'Claude is the easier, general-purpose chat surface. Claude Code is the technical working surface that can open files, run commands, and investigate code or AWS-backed workflows.',
  },
  {
    question: 'Why does Bedrock matter if we already have Claude?',
    answer: 'Bedrock is how the company can use Claude programmatically inside AWS. It matters when AI needs to live inside an app, API, Lambda, or automation instead of a person typing into chat.',
  },
  {
    question: 'Can I ask AI to answer a business question directly?',
    answer: 'Yes, but the answer is only as good as the source it uses. AI should help find, compare, summarize, and explain the right data. It should not silently invent the source of truth.',
  },
  {
    question: 'What if I do not know whether something is automatable?',
    answer: 'That is exactly what the automation request section is for. Describe the repetitive work in plain language, and the team can decide whether it should stay manual, become a report, or become an automation.',
  },
];


const questionCards: QuestionCard[] = [
  {
    question: 'What did we actually spend or sell?',
    source: 'Direct platform sources',
    guidance: 'Use Meta, Google Ads, Shopify, Axon, Amazon, and other direct APIs when the question is platform truth or operational truth.',
  },
  {
    question: 'What should finance or marketing finance trust?',
    source: 'Direct sources first, warehouse second',
    guidance: 'Use direct-source dashboards and warehouse tables that are clearly sourced from platform truth. Do not use attribution systems for finance answers.',
  },
  {
    question: 'What does attribution or one-day click say?',
    source: 'Northbeam parity datasets',
    guidance: 'Use the parity-safe Northbeam warehouse family and the 1DC daily/hourly reporting surfaces, not the broad exploratory Northbeam tables.',
  },
  {
    question: 'What should the Brain Daily Brief default to?',
    source: 'Approved source policy per metric',
    guidance: 'Use one default truth path per metric on the brief: direct or holistic business reads for operating metrics, attribution only where the metric is explicitly attribution-shaped.',
  },
  {
    question: 'What is useful for ad hoc exploration?',
    source: 'Broad warehouse families',
    guidance: 'Use the generic warehouse tables when the question is exploratory or cross-cutting, but do not assume they mirror an in-platform report exactly.',
  },
];

const reportCards: ReportCard[] = [
  {
    title: 'Finance Daily Dashboard',
    category: 'Finance',
    purpose: 'Finance-oriented lens on top of the stable paid-media workbook. Treat this as a reporting view inside the paid-media environment, not as a separate raw-ingest automation target.',
    status: 'Live',
    owner: 'Finance team',
    statusDate: '2026-03-26',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1HtpIGwsjWS-iB5vkQs0hM_oJ00YnultHe_nEZWaao0k/edit',
  },
  {
    title: 'Paid Media Dashboard',
    category: 'Marketing finance',
    purpose: 'Stable multi-day marketing-finance workbook with regional dashboards, raw tabs, and the finance-lens reporting environment.',
    status: 'Live',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-03-26',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1HtpIGwsjWS-iB5vkQs0hM_oJ00YnultHe_nEZWaao0k/edit',
  },
  {
    title: 'Paid Media Dashboard Daily',
    category: 'Marketing finance',
    purpose: 'Current-day hourly Central-time workbook for intraday pacing and operating decisions. The EventBridge refresh is enabled every 10 minutes, Meta/AppLovin/Shopify/Stay-backed blocks are live, and Google / YT / CTV are still the main open direct-source gap.',
    status: 'In progress',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-04-05',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1UIuj-Lk0W2-hz6GoUhuR9dY6qILGzMQvcTrTy1Pfu78/edit',
  },
  {
    title: 'Northbeam 1DC Daily by Region',
    category: 'Attribution',
    purpose: 'Split daily one-day-click accrual workbook by region. This remains a reporting surface under QA, not the current raw Northbeam export landing sheet. The live EventBridge refresh is enabled daily at 11:45 UTC, so the workbook should be populated before the 9:00 CT Brain window.',
    status: 'Needs QA',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-04-05',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1yFKl_DEY6ea1nvoKLNnAqceo9DeS2O9KQbUr5YUZw9A/edit?gid=0#gid=0',
  },
  {
    title: 'Northbeam 1DC Hourly by Region',
    category: 'Real-time attribution monitoring',
    purpose: 'Split hourly one-day-click accrual view used for monitoring and scaling decisions. Its upstream raw Northbeam export still lands in the primary operations workbook, and the live refresh cadence is every 15 minutes through an active EventBridge rule.',
    status: 'Live',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-04-05',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1XEZc3DyMsoVLdiQtBuwM-XnYRq8rq5FTfQlaaRc-D5s/edit?gid=0#gid=0',
  },
  {
    title: 'Northbeam Operations Workbook',
    category: 'Attribution operations',
    purpose: 'Current live workbook for direct Northbeam daily and hourly exports, including the raw tabs that feed downstream hourly and creative Northbeam views.',
    status: 'Live',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-03-26',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1tBtzgCXQqSzILbOeM4roGfc9l1D09XGO6tFvF3TF4ps/edit?gid=1079106200#gid=1079106200',
  },
  {
    title: 'NB Meta Creative Optimization API Sheet',
    category: 'Creative optimization',
    purpose: 'Northbeam-backed creative decision workbook with top-level consumer tabs and a now-restored authoritative YTD raw feed. The supporting refresh rule is enabled hourly at minute 35 UTC.',
    status: 'Needs QA',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-04-05',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1w8MLMh-kJRzlGHqNG9LcSp6dedPLooysFAJTZdKcUNs/edit?gid=793568703#gid=793568703',
  },
  {
    title: 'NB Meta Creative Raw YTD',
    category: 'Creative raw / ops',
    purpose: 'Authoritative YTD raw tab inside the creative workbook. This is the canonical landed feed behind the creative optimization consumer views and is kept fresh by the hourly creative refresh path.',
    status: 'Live',
    owner: 'Hunter Sneed / Daniel Wallock',
    statusDate: '2026-04-05',
    platform: 'Sheets',
    url: 'https://docs.google.com/spreadsheets/d/1w8MLMh-kJRzlGHqNG9LcSp6dedPLooysFAJTZdKcUNs/edit?gid=1318622644#gid=1318622644',
  },
  {
    title: 'Mission Control',
    category: 'Operations / package tracking',
    purpose: 'Internal operating surface for package tracking, shipment visibility, and ops exception monitoring. This is the primary destination for TrackingMore / fulfillment-adjacent operating views and is refreshed by its own Step Functions + ECS runtime.',
    status: 'Live',
    owner: 'CX / Ops',
    statusDate: '2026-03-24',
    platform: 'Internal App',
    url: 'https://automations.mengotomars.com/mission-control',
  },
  {
    title: 'Brain Dashboard',
    category: 'Executive / cross-department',
    purpose: 'Cross-department daily brief, metrics health, and anomaly surface. The frontend lives at `/brain-dashboard`, the data API lives under `/analytics/brain-dashboard/v1/*`, and the morning data path is the ECS-backed Brain reporting runtime plus the anomaly runtime.',
    status: 'Live',
    owner: 'Hunter Sneed',
    statusDate: '2026-04-05',
    platform: 'Internal App',
    url: 'https://automations.mengotomars.com/brain-dashboard',
  },
  {
    title: 'Haus Roadmap',
    category: 'Incrementality',
    purpose: 'Qualitative and experimental roadmap for incrementality interpretation.',
    status: 'Live',
    owner: 'Daniel Wallock',
    statusDate: '2026-03-24',
    platform: 'Notion',
  },
  {
    title: 'KNO Survey Data',
    category: 'Incrementality support',
    purpose: 'Survey-based post-purchase signal that can inform mix interpretation.',
    status: 'In progress',
    owner: 'Hunter Sneed',
    statusDate: '2026-03-24',
    platform: 'Sheets',
  },
];

const sourceRows: SourceRow[] = [
  {
    source: 'Meta / Facebook Ads',
    direct: 'Meta Marketing API',
    intermediary: 'Northbeam, Triple Whale, warehouse derivatives',
    freshness: 'Direct API is near-real-time. Warehouse and modeled layers are batch-refreshed.',
    intradayTrust: 'High',
    canonicalUse: 'Direct API for spend and platform truth; modeled tools for attribution only.',
  },
  {
    source: 'Google Ads / YouTube / CTV',
    direct: 'Google Ads API',
    intermediary: 'Northbeam, Triple Whale, warehouse derivatives',
    freshness: 'Direct API is the freshest practical source. Warehouse rollups are hourly and attribution layers are slower.',
    intradayTrust: 'High',
    canonicalUse: 'Direct API for spend truth. Attribution systems can reinterpret it, but they are not finance truth.',
  },
  {
    source: 'Shopify',
    direct: 'Shopify Admin API / webhooks',
    intermediary: 'Warehouse rollups, downstream dashboards',
    freshness: 'Webhooks and direct API are the fastest path. Warehouse views depend on refresh cadence.',
    intradayTrust: 'High',
    canonicalUse: 'Direct source for gross sales, customer-state logic, returns, and operational commerce truth.',
  },
  {
    source: 'Axon / AppLovin',
    direct: 'Axon reporting API',
    intermediary: 'Northbeam, downstream warehouse tables',
    freshness: 'Direct API is preferred for current-day truth. Downstream modeled layers are slower.',
    intradayTrust: 'High',
    canonicalUse: 'Direct source for spend truth. Warehouse is useful once the direct pull is landed cleanly.',
  },
  {
    source: 'Tatari',
    direct: 'Tatari S3 data share',
    intermediary: 'Warehouse daily spend marts and Brain blended rollups',
    freshness: 'Shared files land later in the day. Treat yesterday as the intended read and never zero-fill missing same-day coverage.',
    intradayTrust: 'Medium',
    canonicalUse: 'Use for blended TV/CTV context and US-only spend until Tatari provides explicit country fields.',
  },
  {
    source: 'Northbeam',
    direct: 'Northbeam report-style exports / API',
    intermediary: 'Parity and broad warehouse families',
    freshness: 'Modeled and batch-refreshed. Hourly pacing exists, but it is not direct platform truth.',
    intradayTrust: 'Medium',
    canonicalUse: 'Use parity-safe paths for report mirroring. Use broad warehouse tables for exploration, not parity.',
  },
  {
    source: 'Triple Whale',
    direct: 'Triple Whale API',
    intermediary: 'Downstream dashboards only',
    freshness: 'Aggregated and batch-refreshed. Good for rollups, not the fastest source for current-day truth.',
    intradayTrust: 'Low',
    canonicalUse: 'Useful as an intermediary modeled surface, not the source of truth for platform truth questions.',
  },
  {
    source: 'KNO / surveys',
    direct: 'KNO / survey system',
    intermediary: 'Warehouse and reporting outputs',
    freshness: 'Survey and modeled context update on reporting cadences, not live operational cadences.',
    intradayTrust: 'Low',
    canonicalUse: 'Use for incrementality support, not for platform truth or finance truth.',
  },
  {
    source: 'Podscale / Podscribe',
    direct: 'Pending final contract; likely Podscribe API or scheduled export',
    intermediary: 'Warehouse landing not yet locked',
    freshness: 'Unknown until the podcast source contract is finalized.',
    intradayTrust: 'Low',
    canonicalUse: 'Treat as unresolved. Do not include in direct-source CAC logic until the spend contract is explicit.',
  },
  {
    source: 'Stay AI / HAM',
    direct: 'Stay AI lifecycle tables plus HAM operational subscription views',
    intermediary: 'Retention marts and Brain source policy',
    freshness: 'Daily refresh cadence. Stay is now the canonical Brain subscription source; HAM remains useful as an operator lens.',
    intradayTrust: 'Medium',
    canonicalUse: 'Use Stay for canonical subscriber metrics in the Brain. Use HAM for comparison and workflow debugging where needed.',
  },
];

const canonicalSourceRows: CanonicalSourceRow[] = [
  {
    sourceSystem: 'Shopify',
    steward: 'Growth / Commerce',
    canonicalTables: [
      'shopify_orders_enriched',
      'marsmen_analytics_production.shopify_orders_mvp_latest',
      'marsmen_aggregated_production.shopify_order_offers_daily',
      'marsmen_aggregated_production.shopify_revenue_last_7d_hourly',
    ],
    notes: 'Use the direct Shopify API or webhooks for final order and gross-sales truth. The warehouse is the reusable analytics layer.',
  },
  {
    sourceSystem: 'Amazon SP-API',
    steward: 'Growth / Marketplace',
    canonicalTables: [
      'marsmen_analytics_production.amazon_spapi_orders_daily',
      'marsmen_analytics_production.amazon_spapi_order_items_daily',
      'marsmen_analytics_production.amazon_spapi_commerce_daily',
    ],
    notes: 'Marketplace truth should come from SP-API-derived marts, not Northbeam.',
  },
  {
    sourceSystem: 'Amazon Ads',
    steward: 'Paid media',
    canonicalTables: ['growth_reporting.growth_report_ads_parquet'],
    notes: 'Currently lands inside the broad paid-media family rather than its own dedicated mart.',
  },
  {
    sourceSystem: 'Meta Ads',
    steward: 'Paid media',
    canonicalTables: ['growth_reporting.growth_report_ads_parquet'],
    notes: 'Use Meta API for platform truth. Use the warehouse for cross-platform rollups only after checking freshness.',
  },
  {
    sourceSystem: 'Google Ads',
    steward: 'Paid media',
    canonicalTables: [
      'growth_reporting.growth_report_ads_parquet',
      'growth_reporting.growth_report_hourly_parquet',
    ],
    notes: 'Use Google Ads API for source truth. Warehouse tables are the shared reporting layer.',
  },
  {
    sourceSystem: 'Northbeam',
    steward: 'Hunter Sneed / Daniel Wallock',
    canonicalTables: [
      'growth_reporting.northbeam_parity_ads_parquet',
      'growth_reporting.northbeam_parity_daily_parquet',
      'growth_reporting.northbeam_growth_report_ads_parquet',
      'growth_reporting.northbeam_growth_report_daily_parquet',
    ],
    notes: 'Parity tables are for UI-safe reporting. Growth-report tables are for broader exploratory work.',
  },
  {
    sourceSystem: 'Triple Whale',
    steward: 'Growth / Daniel Wallock',
    canonicalTables: [
      'growth_reporting.growth_report_hourly_parquet',
      'growth_reporting.growth_report_ads_parquet',
    ],
    notes: 'Treat Triple Whale as an aggregator and interpretation layer, not direct platform truth.',
  },
  {
    sourceSystem: 'Axon / AppLovin',
    steward: 'Paid media',
    canonicalTables: ['Direct API only'],
    notes: 'Direct path is currently preferred. No canonical Athena table is locked yet.',
  },
  {
    sourceSystem: 'Tatari',
    steward: 'Growth / CTV',
    canonicalTables: [
      'marsmen_analytics_production.tatari_linear_spend_and_impressions_latest',
      'marsmen_analytics_production.tatari_spot_level_metrics_latest',
      'marsmen_analytics_production.tatari_linear_spend_daily_latest',
    ],
    notes: 'Tatari is now landed via the S3 data share. Treat the current share as US-only for country-aware logic until Tatari exposes explicit market fields.',
  },
  {
    sourceSystem: 'Klaviyo',
    steward: 'Lifecycle / Retention',
    canonicalTables: [
      'marsmen_analytics_production.retention_growth_daily_mart',
      'marsmen_raw_production.klaviyo_raw',
    ],
    notes: 'Use the mart for daily reporting and the raw table for deeper debugging.',
  },
  {
    sourceSystem: 'Postscript',
    steward: 'Lifecycle / Retention',
    canonicalTables: [
      'marsmen_analytics_production.retention_growth_daily_mart',
      'marsmen_raw_production.postscript_raw',
    ],
    notes: 'Same operating pattern as Klaviyo.',
  },
  {
    sourceSystem: 'Gorgias',
    steward: 'Jarvous Freeman / CX',
    canonicalTables: [
      'marsmen_analytics_production.gorgias_tickets_materialized',
      'marsmen_analytics_production.gorgias_messages_materialized',
      'marsmen_analytics_production.gorgias_events_materialized',
    ],
    notes: 'Materialized Gorgias tables are the preferred reporting surface and feed Superset.',
  },
  {
    sourceSystem: 'Junip',
    steward: 'Avita Ramdass / Brand & CX',
    canonicalTables: [
      'marsmen_analytics_production.junip_product_reviews_materialized',
      'marsmen_analytics_production.junip_store_reviews_materialized',
    ],
    notes: 'Canonical review and testimonial reporting surface.',
  },
  {
    sourceSystem: 'Stay AI',
    steward: 'Retention',
    canonicalTables: [
      'marsmen_aggregated_production.stay_orders_daily',
      'marsmen_aggregated_production.stay_subscriptions_daily',
    ],
    notes: 'Canonical subscription and recurring-order truth for Brain retention metrics.',
  },
  {
    sourceSystem: 'HAM',
    steward: 'Chad Ross / Retention',
    canonicalTables: [
      'marsmen_analytics_production.ham_subscription_growth_daily',
      'marsmen_analytics_production.ham_active_subs_daily',
      'marsmen_analytics_production.ham_churn_timeseries_daily',
    ],
    notes: 'Separate retention source family with its own ingestion and refresh path. Useful operationally, but not the default Brain source of truth for subscriber metrics.',
  },
  {
    sourceSystem: 'ShipHero',
    steward: 'CX / Ops',
    canonicalTables: [
      'marsmen_analytics_production.shiphero_shipping_labels',
      'marsmen_aggregated_production.shiphero_warehouse_products_daily',
    ],
    notes: 'Use labels for shipment analytics and warehouse products for inventory/value snapshots.',
  },
  {
    sourceSystem: 'TrackingMore',
    steward: 'CX / Ops',
    canonicalTables: [
      'marsmen_analytics_production.trackingmore_latest_status',
      'marsmen_analytics_production.trackingmore_webhook_events',
    ],
    notes: 'Primary shipment-tracking reporting surface.',
  },
  {
    sourceSystem: 'Bill.com',
    steward: 'Jacob Karmel / Finance',
    canonicalTables: [
      'marsmen_aggregated_production.billcom_bills_daily',
      'marsmen_aggregated_production.billcom_invoices_daily',
      'marsmen_aggregated_production.billcom_payments_daily',
    ],
    notes: 'Canonical AP and AR reporting tables.',
  },
  {
    sourceSystem: 'Teller',
    steward: 'Jacob Karmel / Finance',
    canonicalTables: ['marsmen_aggregated_production.teller_metrics_daily'],
    notes: 'Canonical banking snapshot table.',
  },
  {
    sourceSystem: 'Mission Control',
    steward: 'CX / Ops',
    canonicalTables: ['marsmen_analytics_production.mission_control_dashboard_vw'],
    notes: 'Canonical operating view for the package-tracking and exception-management surface.',
  },
];

const detailedSourceRows: DetailedSourceRow[] = [
  {
    sourceSystem: 'Shopify',
    category: 'Commerce / orders',
    primaryData: 'Orders, revenue, refunds, payments-adjacent metrics, product costs',
    implementationSurface: 'Data platform ingestion + reporting',
    cadence: 'Event-driven webhooks plus daily/hourly warehouse refreshes depending on table.',
    status: 'Core source; heavily used in paid media and commerce reporting.',
  },
  {
    sourceSystem: 'Amazon SP-API',
    category: 'Commerce / marketplace',
    primaryData: 'Orders, order items, sales and traffic reports',
    implementationSurface: 'Data platform ingestion',
    cadence: 'Scheduled batch ingestion; not treated as high-speed intraday truth in this workstream.',
    status: 'Live source; intraday limitations still matter for some use cases.',
  },
  {
    sourceSystem: 'Amazon Ads',
    category: 'Paid media',
    primaryData: 'Spend and ad performance',
    implementationSurface: 'Reporting-side credentials present',
    cadence: 'Not locked as an active first-class intraday pipeline here yet.',
    status: 'Available, but not the current phase-1 intraday path.',
  },
  {
    sourceSystem: 'Meta Ads',
    category: 'Paid media',
    primaryData: 'Spend, delivery, ad performance',
    implementationSurface: 'Data platform ingestion + reporting',
    cadence: 'Direct API for freshest reads; warehouse family refreshed on hourly and daily schedules.',
    status: 'Live source; required for the paid media daily build.',
  },
  {
    sourceSystem: 'Google Ads',
    category: 'Paid media',
    primaryData: 'Spend, campaign splits, search metrics',
    implementationSurface: 'Reporting-side direct API path',
    cadence: 'Direct API for freshest reads; warehouse family refreshed on hourly and daily schedules when configured.',
    status: 'Transformation logic is prepared, but the direct collector is still blocked until MCC approval, the developer token, and manager/client account IDs are populated.',
  },
  {
    sourceSystem: 'Northbeam',
    category: 'Attribution / MTA',
    primaryData: 'Modeled attribution exports, hourly and daily pacing, accrual and cash views',
    implementationSurface: 'Warehouse ingestion + reporting-only exports',
    cadence: 'Parity-style reporting is daily/hourly by report cadence; broad warehouse family is batch-refreshed and not real-time.',
    status: 'Active and important; broad vs parity paths must be treated separately.',
  },
  {
    sourceSystem: 'Triple Whale',
    category: 'Marketing finance / attribution-adjacent',
    primaryData: 'Attributed and aggregated marketing reporting datasets',
    implementationSurface: 'Warehouse ingestion + reporting references',
    cadence: 'Batch-refreshed warehouse datasets; useful for shared rollups, not live platform truth.',
    status: 'Existing source family still referenced in docs and current operating concepts.',
  },
  {
    sourceSystem: 'Axon / AppLovin',
    category: 'Paid media',
    primaryData: 'Spend and advertiser reporting',
    implementationSurface: 'Reporting-side direct API path',
    cadence: 'Direct API is the preferred current-day path; no canonical warehouse cadence is locked yet.',
    status: 'Direct source verified for current reporting work.',
  },
  {
    sourceSystem: 'Klaviyo',
    category: 'CRM / email',
    primaryData: 'Campaigns, flows, subscriber metrics',
    implementationSurface: 'Data platform ingestion + reporting marts',
    cadence: 'Scheduled ingestion plus downstream daily marts.',
    status: 'Active source also used in retention reporting.',
  },
  {
    sourceSystem: 'Postscript',
    category: 'CRM / SMS',
    primaryData: 'SMS events, subscriber data, campaign data',
    implementationSurface: 'Ingestion + webhook + reporting marts',
    cadence: 'Webhook and scheduled ingestion with downstream daily marts.',
    status: 'Active source used in retention and messaging reporting.',
  },
  {
    sourceSystem: 'Gorgias',
    category: 'CX / support',
    primaryData: 'Tickets, messages, events',
    implementationSurface: 'Ingestion + materialization + dashboarding',
    cadence: 'Hourly/daily ingestion depending on stream plus daily materializations.',
    status: 'Active source with dedicated dashboard work.',
  },
  {
    sourceSystem: 'Junip',
    category: 'Reviews / UGC',
    primaryData: 'Reviews, testimonials, reviewer workflows',
    implementationSurface: 'Ingestion + orchestration + reporting',
    cadence: 'Daily ingestion and daily derived reporting jobs.',
    status: 'Active source for review and testimonial workflows.',
  },
  {
    sourceSystem: 'Stay AI',
    category: 'Subscriptions',
    primaryData: 'Subscription lifecycle and recurring-order data',
    implementationSurface: 'Ingestion + reporting',
    cadence: 'Daily ingestion and daily reporting outputs.',
    status: 'Active source and now the canonical Brain source for subscriber metrics.',
  },
  {
    sourceSystem: 'HAM',
    category: 'Subscriptions / growth',
    primaryData: 'Subscription growth and lifecycle metrics',
    implementationSurface: 'Ingestion + Athena refresh + reporting',
    cadence: 'Daily ingest followed by daily Athena refresh.',
    status: 'Active source that remains useful operationally, but no longer the default Brain subscriber truth layer.',
  },
  {
    sourceSystem: 'ProsperStack',
    category: 'Retention / save flows',
    primaryData: 'Save-flow sessions and outcomes',
    implementationSurface: 'Ingestion + reporting',
    cadence: 'Scheduled ingestion with downstream reporting refreshes.',
    status: 'Active source for retention and saves context.',
  },
  {
    sourceSystem: 'Recharge',
    category: 'Subscriptions / billing',
    primaryData: 'Subscription and billing webhooks',
    implementationSurface: 'Webhook ingestion',
    cadence: 'Event-driven.',
    status: 'Present in infra; not a major reporting focus in this workstream.',
  },
  {
    sourceSystem: 'ShipHero',
    category: 'Fulfillment / WMS',
    primaryData: 'Warehouse products, shipments',
    implementationSurface: 'Ingestion + reporting',
    cadence: 'Daily ingestion and daily warehouse refreshes.',
    status: 'Active fulfillment source.',
  },
  {
    sourceSystem: 'TrackingMore',
    category: 'Fulfillment / shipment tracking',
    primaryData: 'Package status, delivery tracking, backfills, webhook events',
    implementationSurface: 'Ingestion + webhook + backfill',
    cadence: 'Webhook-driven plus hourly pollers and daily backfills.',
    status: 'Active logistics source.',
  },
  {
    sourceSystem: 'Bill.com',
    category: 'Finance / AP-AR',
    primaryData: 'Bills, invoices, payments, daily finance snapshots',
    implementationSurface: 'Ingestion + reporting',
    cadence: 'Daily when enabled.',
    status: 'Active finance source.',
  },
  {
    sourceSystem: 'Teller',
    category: 'Finance / banking',
    primaryData: 'Transactions, balances, bank metrics',
    implementationSurface: 'Ingestion + reporting',
    cadence: 'Daily.',
    status: 'Active finance source.',
  },
  {
    sourceSystem: 'KNO Commerce / NoCommerce',
    category: 'Survey / incrementality',
    primaryData: 'Post-purchase survey responses',
    implementationSurface: 'Reporting-planned source',
    cadence: 'Not yet locked.',
    status: 'Planned / partial integration; destination still needs to be locked.',
  },
  {
    sourceSystem: 'Haus',
    category: 'Incrementality / modeling',
    primaryData: 'Geo holdout and incrementality outputs',
    implementationSurface: 'Future-state modeling source',
    cadence: 'Not currently an active operational pipeline.',
    status: 'Not an active build item right now.',
  },
  {
    sourceSystem: 'Tatari',
    category: 'Paid media / CTV',
    primaryData: 'Linear spend, impressions, and spot-level CTV metrics',
    implementationSurface: 'Lake landing + ECS-backed Brain reporting refresh',
    cadence: 'Daily file-share landing later in the day; intended read is yesterday if coverage has landed.',
    status: 'Live ingest via S3 data share. Current share is treated as US-only for country-aware logic until explicit geography fields exist.',
  },
  {
    sourceSystem: 'Podscale / Podscribe',
    category: 'Podcast / offline media',
    primaryData: 'Campaign reporting, measurement, and potential podcast spend context',
    implementationSurface: 'Source contract still unresolved',
    cadence: 'Unknown until the final API or export path is confirmed.',
    status: 'Podscale public API could not be verified. Podscribe has public API docs, but the spend contract still needs confirmation before ingestion is built.',
  },
  {
    sourceSystem: 'Redo',
    category: 'Subscription / retention operations',
    primaryData: 'Operational source discussed in retention flows',
    implementationSurface: 'Business-context reference only',
    cadence: 'Unknown / not cataloged as an active pipeline here.',
    status: 'Relevant context, but not a first-class pipeline in this repo pass.',
  },
  {
    sourceSystem: 'Slack',
    category: 'Internal context',
    primaryData: 'Messages and conversation context',
    implementationSurface: 'Ingestion + context pipeline',
    cadence: 'Event-driven.',
    status: 'Internal source for context, not an external vendor fact source.',
  },
  {
    sourceSystem: 'Mission Control',
    category: 'Internal operating system',
    primaryData: 'Internal operating and package-tracking snapshots',
    implementationSurface: 'Refresh and reporting',
    cadence: 'Hourly and twice-daily supporting refresh jobs depending on dataset.',
    status: 'Internal source family.',
  },
  {
    sourceSystem: 'Video Brief / creative systems',
    category: 'Internal operating system',
    primaryData: 'Creative workflow artifacts',
    implementationSurface: 'Reporting and supporting infra',
    cadence: 'Varies by supporting workflow.',
    status: 'Internal supporting system with no canonical analytics table yet.',
  },
];

const operationalJobs: OperationalJob[] = [
  {
    job: 'marsmen-brain-reporting-runtime-ecs-prod / RefreshPhase',
    domain: 'Platform',
    businessOutput: 'First ECS task inside the live Standard Step Functions runtime. Refreshes the Brain source marts, metric values, baselines, Tatari ingest, Shopify first-order truth, and direct known-channel spend.',
    cadence: 'Daily 09:00 CT.',
  },
  {
    job: 'marsmen-brain-reporting-runtime-ecs-prod / PublishPhase',
    domain: 'Platform',
    businessOutput: 'Second ECS task inside the live Standard Step Functions runtime. Publishes the Brain daily, weekly, and monthly packs plus latest runtime artifacts after refresh succeeds.',
    cadence: 'Daily immediately after RefreshPhase.',
  },
  {
    job: 'marsmen-brain-anomaly-runtime-prod',
    domain: 'Platform',
    businessOutput: 'Reads the refreshed Brain baselines and publishes anomaly and analysis payloads after the daily report is ready.',
    cadence: 'Daily 10:00 CT.',
  },
  {
    job: 'marsmen-brain-reporting-runtime-prod',
    domain: 'Platform',
    businessOutput: 'Legacy pre-ECS Brain reporting state machine. Still present in AWS, but its scheduler is disabled and it is no longer the canonical morning path.',
    cadence: 'Disabled legacy scheduler at 15:00 CT.',
  },
  {
    job: 'marsmen-analytics-brain-production',
    domain: 'Platform',
    businessOutput: 'Athena-backed Brain API Lambda that serves `/analytics/brain-dashboard/v1/status`, `/dashboard`, `/metrics`, `/anomalies`, and `/chat` to the Brain frontend.',
    cadence: 'On-demand API runtime, not a scheduled refresh job.',
  },
  {
    job: 'growth-reporting-refresh',
    domain: 'Growth',
    businessOutput: 'Growth reporting datasets powering hourly spend, sales, orders, refunds, and COGS reporting.',
    cadence: 'Hourly plus daily ads-only refresh.',
  },
  {
    job: 'growth-reporting-spend-reconciliation',
    domain: 'Growth',
    businessOutput: 'Detects spend drift versus Triple Whale and sends reconciliation results.',
    cadence: 'Weekly Monday 16:00 UTC.',
  },
  {
    job: 'marsmen-data-quality-checker',
    domain: 'Platform',
    businessOutput: 'Data freshness and gap checks across ingestion and caches.',
    cadence: 'Hourly.',
  },
  {
    job: 'marsmen-prod-growth-daily-brief',
    domain: 'Growth',
    businessOutput: 'Legacy daily growth KPI brief. This EventBridge rule is still present in AWS but currently disabled and no longer the primary morning leadership path.',
    cadence: 'Disabled legacy schedule at 13:05 UTC.',
  },
  {
    job: 'marsmen-brain-orchestrator-schedule-prod',
    domain: 'Platform',
    businessOutput: 'Separate Brain chatbot/orchestrator loop for interactive actions and adset workflow support. Distinct from the daily Brain reporting runtime.',
    cadence: 'Daily at 14:00, 18:00, and 22:00 UTC.',
  },
  {
    job: 'marsmen-prod-billcom-sync',
    domain: 'Finance',
    businessOutput: 'Direct Bill.com sync path for AP/AR source landing. The rule still exists in AWS, but it is currently disabled while finance remains ACT-led in the Brain.',
    cadence: 'Disabled in AWS.',
  },
  {
    job: 'northbeam-daily-region-metrics',
    domain: 'Attribution',
    businessOutput: 'Direct Northbeam daily export into the operations workbook that downstream daily and creative surfaces still depend on.',
    cadence: 'Daily report orchestration.',
  },
  {
    job: 'northbeam-hourly-pacing',
    domain: 'Attribution',
    businessOutput: 'Direct Northbeam hourly export into the operations workbook for pacing, monitoring, and downstream hourly views.',
    cadence: 'Every 15 minutes.',
  },
  {
    job: 'marsmen-northbeam-daily-refresh',
    domain: 'Attribution',
    businessOutput: 'Refreshes the split Northbeam 1DC daily workbook used for daily by-region reporting.',
    cadence: 'Daily 11:45 UTC.',
  },
  {
    job: 'marsmen-northbeam-hourly-refresh',
    domain: 'Attribution',
    businessOutput: 'Refreshes the split Northbeam 1DC hourly workbook used for intraday regional monitoring.',
    cadence: 'Every 15 minutes.',
  },
  {
    job: 'marsmen-northbeam-creative-refresh',
    domain: 'Attribution',
    businessOutput: 'Refreshes the creative workbook authoritative raw YTD tab from the curated Northbeam export files.',
    cadence: 'Hourly at :35 UTC.',
  },
  {
    job: 'marsmen-paid-media-dashboard-daily',
    domain: 'Growth',
    businessOutput: 'Current-day hourly paid-media workbook refresh with direct API blocks for Meta, AppLovin, Shopify, and Stay-backed retention summaries.',
    cadence: 'Enabled every 10 minutes in AWS; still source-QA constrained while Google Ads / YT / CTV remain open.',
  },
  {
    job: 'marsmen-prod-notion-creative-tracker-snapshot',
    domain: 'Growth',
    businessOutput: 'Daily creative pipeline snapshot from Notion.',
    cadence: 'Daily 13:20 UTC.',
  },
  {
    job: 'marsmen-slack-context-ingest',
    domain: 'Platform',
    businessOutput: 'Captures human-entered growth context for downstream briefs.',
    cadence: 'Event-driven.',
  },
  {
    job: 'gorgias-materializations',
    domain: 'CX / Ops',
    businessOutput: 'Support analytics tables for ticket volumes, SLAs, and issue tagging.',
    cadence: 'Daily around 03:00 CST.',
  },
  {
    job: 'marsmen-gorgias-tickets-ingestor',
    domain: 'CX / Ops',
    businessOutput: 'Shipping and support ticket dataset.',
    cadence: 'Daily 06:00 UTC.',
  },
  {
    job: 'marsmen-gorgias-messages-ingestor',
    domain: 'CX / Ops',
    businessOutput: 'Ticket message timeline for response-time analysis.',
    cadence: 'Hourly.',
  },
  {
    job: 'marsmen-prod-junip-reviews',
    domain: 'CX / Ops',
    businessOutput: 'Review content and ratings feed.',
    cadence: 'Daily.',
  },
  {
    job: 'marsmen-prod-junip-testimonials-schedule',
    domain: 'CX / Ops',
    businessOutput: 'Daily shortlist of standout reviews and testimonial assets.',
    cadence: 'Daily 09:00 CT.',
  },
  {
    job: 'marsmen-prod-billcom-sync',
    domain: 'Finance',
    businessOutput: 'AP and AR source data plus finance rollup metrics.',
    cadence: 'Daily when enabled.',
  },
  {
    job: 'marsmen-prod-teller-metrics',
    domain: 'Finance',
    businessOutput: 'Cash and transaction rollups for finance visibility.',
    cadence: 'Daily.',
  },
  {
    job: 'marsmen-recharge-event-processor',
    domain: 'Retention',
    businessOutput: 'Subscription lifecycle and billing events for retention analytics.',
    cadence: 'Event-driven.',
  },
  {
    job: 'marsmen-trackingmore-webhook',
    domain: 'CX / Ops',
    businessOutput: 'Shipment tracking event stream used in ops and CX reporting.',
    cadence: 'Event-driven.',
  },
  {
    job: 'marsmen-prod-shopify-trackingmore-backfill-daily',
    domain: 'CX / Ops',
    businessOutput: 'Daily catch-up for tracking statuses to improve delivery and exception completeness.',
    cadence: 'Daily 06:00 UTC.',
  },
  {
    job: 'marsmen-shopify-order-processor',
    domain: 'Growth',
    businessOutput: 'Near-real-time Shopify order events for analytics and caches.',
    cadence: 'Event-driven.',
  },
  {
    job: 'shopify-orders-enriched',
    domain: 'Growth',
    businessOutput: 'Analytics-friendly orders dataset for profitability and reporting.',
    cadence: 'Daily 06:00 UTC plus on-demand backfills.',
  },
  {
    job: 'marsmen-daily-curated-transform-production',
    domain: 'Retention',
    businessOutput: 'Clean curated orders layer for churn and cohort analytics.',
    cadence: 'Daily 04:00 UTC.',
  },
  {
    job: 'marsmen-daily-metrics-production',
    domain: 'Retention',
    businessOutput: 'Retention and LTV rollups for churn dashboards.',
    cadence: 'Daily 04:30 UTC.',
  },
  {
    job: 'marsmen-prod-shiphero-shipments',
    domain: 'CX / Ops',
    businessOutput: 'Shipment record feed for fulfillment analytics.',
    cadence: 'Daily 03:15 CT.',
  },
  {
    job: 'marsmen-prod-fulfillment-athena-refresh-schedule',
    domain: 'CX / Ops',
    businessOutput: 'Materialized fulfillment operations tables that reduce dashboard query load.',
    cadence: 'Daily 07:00 UTC.',
  },
  {
    job: 'marsmen-production-mission-control-refresh',
    domain: 'CX / Ops',
    businessOutput: 'Operational mission-control datasets for open shipments, exceptions, and order details.',
    cadence: 'Twice daily at 08:00 UTC and 20:00 UTC.',
  },
  {
    job: 'marsmen-mission-control-tracking-status-poller-production',
    domain: 'CX / Ops',
    businessOutput: 'Keeps Mission Control delivery statuses current.',
    cadence: 'Hourly.',
  },
  {
    job: 'marsmen-prod-carrier-review-schedule',
    domain: 'CX / Ops',
    businessOutput: 'Weekly carrier performance snapshot.',
    cadence: 'Weekly Monday 09:00 CT.',
  },
  {
    job: 'marsmen-prod-cohort-profitability-refresh-schedule',
    domain: 'Growth',
    businessOutput: 'Cohort payback and profitability metrics.',
    cadence: 'Weekly Monday 12:00 UTC.',
  },
  {
    job: 'marsmen-prod-stay-ai-ingestion-schedule',
    domain: 'Retention',
    businessOutput: 'Stay AI subscription and recurring-order datasets.',
    cadence: 'Daily 07:30 UTC.',
  },
  {
    job: 'marsmen-shopify-revenue-metrics',
    domain: 'Growth',
    businessOutput: 'Rolling 7-day revenue health metric.',
    cadence: 'Hourly.',
  },
  {
    job: 'marsmen-prod-ham-ingest-schedule',
    domain: 'Retention',
    businessOutput: 'Daily ingestion of HAM dashboard data for reporting.',
    cadence: 'Daily.',
  },
  {
    job: 'marsmen-prod-ham-athena-refresh-schedule',
    domain: 'Retention',
    businessOutput: 'Makes HAM data queryable and refreshes latest views.',
    cadence: 'Daily after ingest.',
  },
];

const slackSurfaces: SlackSurface[] = [
  {
    surface: 'marsmen-brain-reporting-runtime-ecs-prod',
    domain: 'Platform',
    purpose: 'Morning Brain pack delivery and runtime publication path for the Daily Brief, All Metrics, and related Slack-visible outputs.',
    cadence: 'Daily 09:00 CT refresh with publish immediately after.',
  },
  {
    surface: 'marsmen-brain-anomaly-runtime-prod',
    domain: 'Platform',
    purpose: 'Brain anomaly and analysis delivery path that reads the refreshed baselines and posts analysis after the morning report is ready.',
    cadence: 'Daily 10:00 CT.',
  },
  {
    surface: 'growth-reporting-spend-reconciliation',
    domain: 'Growth',
    purpose: 'Spend drift guardrail that posts reconciliation alerts and results.',
    cadence: 'Weekly Monday 16:00 UTC.',
  },
  {
    surface: 'marsmen-prod-growth-daily-brief',
    domain: 'Growth',
    purpose: 'Legacy daily KPI brief surface. The EventBridge rule still exists but is currently disabled in AWS.',
    cadence: 'Disabled legacy schedule.',
  },
  {
    surface: 'marsmen-slack-context-ingest',
    domain: 'Platform',
    purpose: 'Slash-command and modal path for capturing human growth context.',
    cadence: 'Event-driven.',
  },
  {
    surface: 'marsmen-prod-junip-testimonials-schedule',
    domain: 'CX / Ops',
    purpose: 'Daily shortlist of standout reviews and testimonial cards.',
    cadence: 'Daily 09:00 CT.',
  },
  {
    surface: 'marsmen-prod-stepfunction-alerts',
    domain: 'Platform',
    purpose: 'Operational failure alerts for Step Functions, Glue, and Lambda.',
    cadence: 'Event-driven.',
  },
  {
    surface: 'marsmen-slack-notifier',
    domain: 'Platform',
    purpose: 'Reusable Slack delivery utility for operational reports and alerts.',
    cadence: 'Event-driven.',
  },
  {
    surface: 'marsmen-mission-control-delay-analyzer-daily-production',
    domain: 'CX / Ops',
    purpose: 'Daily delay analysis for late shipments and aging buckets.',
    cadence: 'Daily 09:00 UTC.',
  },
  {
    surface: 'marsmen-prod-carrier-review-schedule',
    domain: 'CX / Ops',
    purpose: 'Weekly carrier performance summary.',
    cadence: 'Weekly Monday 09:00 CT.',
  },
  {
    surface: 'marsmen-prod-gorgias-impact-schedule',
    domain: 'CX / Ops',
    purpose: 'Weekly support impact summary.',
    cadence: 'Weekly Monday.',
  },
];

const morningReadinessRows: MorningReadinessRow[] = [
  {
    sourceOrPipeline: 'Brain reporting runtime (ECS)',
    expectedWindow: '09:00 CT daily',
    safeByNine: 'Conditional',
    notes: 'This is the publication control plane itself. Fresh data appears after the 09:00 CT refresh completes, not before.',
  },
  {
    sourceOrPipeline: 'Brain anomaly runtime',
    expectedWindow: '10:00 CT daily',
    safeByNine: 'No',
    notes: 'Analysis intentionally runs after the reporting job because it depends on refreshed baselines and published metric values.',
  },
  {
    sourceOrPipeline: 'Shopify direct-truth marts',
    expectedWindow: 'Available for yesterday before the 09:00 CT Brain run',
    safeByNine: 'Yes',
    notes: 'These now back first-order truth and direct AOV logic. Treat them as yesterday-safe, not same-day intraday truth.',
  },
  {
    sourceOrPipeline: 'Northbeam daily / hourly surfaces',
    expectedWindow: 'Daily workbook refresh at 11:45 UTC and hourly workbook refresh every 15 minutes',
    safeByNine: 'Yes',
    notes: 'The daily 1DC workbook should be ready before the Brain morning window, while the hourly workbook stays fresher throughout the day.',
  },
  {
    sourceOrPipeline: 'Tatari data share',
    expectedWindow: 'Yesterday-safe, with shared files usually landing later in the prior day / overnight cycle',
    safeByNine: 'Conditional',
    notes: 'Use only when the latest shared file actually covers the selected business day. Treat Tatari as yesterday-only and US-only for now.',
  },
  {
    sourceOrPipeline: 'Finance ACT / actuals',
    expectedWindow: 'Variable sheet-driven timing',
    safeByNine: 'Conditional',
    notes: 'The Brain now warns instead of hard-failing when ACT is stale. Morning reports can publish partial, but finance freshness still needs explicit monitoring.',
  },
  {
    sourceOrPipeline: 'Mission Control refresh',
    expectedWindow: '08:00 UTC and 20:00 UTC',
    safeByNine: 'Yes',
    notes: 'Operational shipment and exception datasets should be fresh for the morning read because the first daily run lands before 09:00 CT.',
  },
  {
    sourceOrPipeline: 'Paid Media Dashboard Daily',
    expectedWindow: 'Every 10 minutes in AWS',
    safeByNine: 'Conditional',
    notes: 'The scheduler is live, but Google / YouTube / CTV remain the main direct-source gap. Treat the workbook as live with source caveats, not perfect completeness.',
  },
];

const sourceFamilyCards: SourceFamilyCard[] = [
  {
    title: 'Paid media direct sources',
    systems: ['Meta Ads', 'Google Ads', 'Amazon Ads', 'Axon / AppLovin', 'Tatari'],
    warehouse: ['Paid media warehouse family (`growth_report_*`)', 'Paid media daily and hourly marts'],
    useFor: 'Platform-truth spend, intraday monitoring, and cross-platform marketing-finance rollups.',
  },
  {
    title: 'Attribution and modeled performance',
    systems: ['Northbeam', 'Triple Whale'],
    warehouse: [
      'Northbeam report-safe family (`northbeam_parity_*`)',
      'Northbeam exploratory family (`northbeam_growth_report_*`)',
    ],
    useFor: '1DC daily/hourly pacing, modeled attribution, report parity with Northbeam surfaces, and temporary blended rollups while direct-source replacements are still closing.',
  },
  {
    title: 'Commerce and retention',
    systems: ['Shopify', 'Amazon SP-API', 'Klaviyo', 'Postscript', 'Stay AI', 'HAM', 'ProsperStack', 'Recharge'],
    warehouse: [
      'Shopify commerce marts',
      'Brain Shopify first-order truth marts',
      'Retention and subscription marts',
      'Stay AI and HAM daily marts',
    ],
    useFor: 'Orders, gross sales, refunds, subscription lifecycle, retention, LTV, and messaging performance.',
  },
  {
    title: 'CTV and offline media',
    systems: ['Tatari', 'Podscale', 'Podscribe'],
    warehouse: [
      'Tatari data-share landing tables',
      'Tatari daily spend marts',
      'Podcast contract still pending',
    ],
    useFor: 'CTV, offline, and podcast media where freshness and geography contracts must be called out explicitly.',
  },
  {
    title: 'CX and operations',
    systems: ['Gorgias', 'TrackingMore', 'ShipHero', 'Mission Control'],
    warehouse: [
      'CX support marts',
      'Shipment and tracking marts',
      'Mission Control operating views',
    ],
    useFor: 'Package tracking, shipment exceptions, support SLAs, warehouse visibility, and operational follow-up.',
  },
  {
    title: 'Finance and banking',
    systems: ['Bill.com', 'Teller', 'Shopify payments'],
    warehouse: [
      'Finance and banking marts',
      'Payment and dispute reporting tables',
    ],
    useFor: 'Finance daily dashboards, AP/AR monitoring, cash visibility, payment fees, and dispute tracking.',
  },
];

const aggregatedSourceProfiles: AggregatedSourceProfile[] = [
  {
    title: 'Brain direct-source operating marts',
    sourceType: 'Warehouse-managed direct-source layer',
    contains: 'Explicit direct-source marts such as `shopify_growth_order_truth_daily_latest`, `shopify_growth_first_order_aov_daily_latest`, `growth_country_spend_known_channels_daily_latest`, and related Brain-specific readiness views.',
    bestFor: 'Operating metrics that need a default business-truth path in the Brain without falling back to attribution or aggregator logic.',
    freshness: 'Refreshed by the ECS-backed Brain reporting runtime in the morning daily run, with source-specific caveats for finance, Tatari, Amazon, and other lagging systems.',
    notFor: 'Assuming complete cross-channel coverage before the remaining numerator gaps, especially Google MCC and podcast, are closed.',
  },
  {
    title: 'Triple Whale and the broad growth-report family',
    sourceType: 'Aggregator / marketing-finance style rollup',
    contains: 'Cross-platform ad-performance rollups, attributed or aggregated marketing-finance style datasets, and warehouse-friendly paid media datasets such as `growth_report_hourly_parquet` and `growth_report_ads_parquet`.',
    bestFor: 'Cross-platform daily and hourly reporting, shared warehouse rollups, and transitional blended metrics while direct-source replacements are still closing.',
    freshness: 'Batch-refreshed, not live. The current growth-report refresh path runs hourly for the main dataset and a daily ads-only refresh in the morning UTC window.',
    notFor: 'Minute-by-minute platform truth, exact ad-platform reconciliation, or permanent ownership of metrics that now have a direct-source replacement path.',
  },
  {
    title: 'Northbeam parity family',
    sourceType: 'Modeled attribution / report-safe warehouse family',
    contains: 'Northbeam outputs intended to mirror report-style one-day-click views closely, especially the `northbeam_parity_ads_parquet` and `northbeam_parity_daily_parquet` tables used for Daniel/Hunter-style 1DC reporting.',
    bestFor: 'Northbeam UI-parity reporting, 1DC daily by region, hourly pacing by region, and attribution questions where report parity matters more than exploratory flexibility.',
    freshness: 'Batch-refreshed on reporting cadences, not live event data. Treat daily parity outputs as daily-refreshed and hourly pacing views as hourly-refreshed.',
    notFor: 'Raw platform-truth questions like exact Meta spend right now or exact Shopify sales right now.',
  },
  {
    title: 'Northbeam broad family',
    sourceType: 'Modeled attribution / exploratory warehouse family',
    contains: 'Generic Northbeam exports across multiple windows, accounting modes, and broader breakdowns through `northbeam_growth_report_ads_parquet` and `northbeam_growth_report_daily_parquet`.',
    bestFor: 'Exploratory analysis, broader modeled slicing, and cases where you need multiple windows or accounting modes in one landed family.',
    freshness: 'Batch-refreshed modeled data. Useful for analysis, but not the fastest surface and not assumed to match the Northbeam UI exactly.',
    notFor: 'Exact report parity or operational decisions that depend on the cleanest current 1DC number.',
  },
];

const dataPathCards: DataPathCard[] = [
  {
    title: 'Paid media direct platforms',
    examples: ['Meta', 'Google Ads', 'Amazon Ads', 'Axon'],
    bestFor: 'Current-day spend, platform truth, and intraday monitoring.',
    tradeoff: 'Fastest answer, but weaker for blended interpretation.',
    notIdealFor: 'Attribution or credit-assignment questions.',
  },
  {
    title: 'Commerce direct platforms',
    examples: ['Shopify', 'Amazon SP-API'],
    bestFor: 'Gross sales, orders, refunds, and customer-state truth.',
    tradeoff: 'Best order truth, but usually needs more source-specific business logic.',
    notIdealFor: 'Modeled attribution or cross-channel credit assignment.',
  },
  {
    title: 'Lifecycle and retention systems',
    examples: ['Klaviyo', 'Postscript', 'Stay AI', 'HAM', 'Recharge'],
    bestFor: 'Messaging performance, retention, subscriptions, and LTV support.',
    tradeoff: 'Mixes direct systems and derived marts, so you still need the canonical table.',
    notIdealFor: 'Platform-truth paid media questions or raw attribution.',
  },
  {
    title: 'CX and operations systems',
    examples: ['Gorgias', 'TrackingMore', 'ShipHero', 'Mission Control'],
    bestFor: 'Package tracking, shipment issues, support SLAs, and operational follow-up.',
    tradeoff: 'Freshness varies by surface because some views are derived.',
    notIdealFor: 'Finance truth or paid media attribution.',
  },
  {
    title: 'Triple Whale rollups',
    examples: ['Triple Whale', 'growth_report family'],
    bestFor: 'Blended marketing-finance interpretation and cross-channel rollups.',
    tradeoff: 'Useful aggregation layer, but not direct platform truth.',
    notIdealFor: 'Exact intraday monitoring or source-of-record finance answers.',
  },
  {
    title: 'Northbeam attribution',
    examples: ['Northbeam parity', 'Northbeam broad', '1DC daily / hourly'],
    bestFor: 'One-day-click attribution, pacing by attributed orders, and regional monitoring.',
    tradeoff: 'Modeled attribution, not raw platform truth.',
    notIdealFor: 'Questions like "what did Meta spend right now?" or "what did Shopify sell today?"',
  },
  {
    title: 'Warehouse and delivery surfaces',
    examples: ['Athena', 'Superset', 'Sheets', 'Slack', 'Internal apps'],
    bestFor: 'Reusable queries and team-facing reporting once the landed contract is understood.',
    tradeoff: 'Freshness depends on ingestion cadence and upstream contracts.',
    notIdealFor: 'Assuming every warehouse table is canonical or real-time.',
  },
  {
    title: 'Brain source-policy layer',
    examples: ['Direct source', 'Holistic blended source', 'Attribution source'],
    bestFor: 'Metrics that need one explicit default truth path while still showing the source family and caveats in the UI.',
    tradeoff: 'Requires deliberate policy by metric instead of letting every dashboard or analyst improvise.',
    notIdealFor: 'Unstructured exploration where every source is equally acceptable.',
  },
];

const warehouseCards: WarehouseCard[] = [
  {
    title: 'Northbeam broad warehouse family',
    tables: ['northbeam_growth_report_ads_parquet', 'northbeam_growth_report_daily_parquet'],
    purpose: 'General-purpose Northbeam warehouse landing for broad analysis and exploratory work.',
    useWhen: 'You are exploring trends, slicing multiple windows or modes, or doing flexible analysis.',
    avoidWhen: 'You need to mirror the Northbeam UI/report exactly.',
  },
  {
    title: 'Northbeam parity warehouse family',
    tables: ['northbeam_parity_ads_parquet', 'northbeam_parity_daily_parquet'],
    purpose: 'Parity-safe Northbeam family intended to track the report-style export that matches the in-platform one-day-click views.',
    useWhen: 'You need Daniel/Hunter-style 1DC daily or hourly parity reporting.',
    avoidWhen: 'You want broad exploratory analysis across many models or windows.',
  },
  {
    title: 'Paid media warehouse family',
    tables: ['growth_report_ads_parquet', 'growth_report_hourly_parquet'],
    purpose: 'Broad paid media warehouse family for direct-source ad performance and intraday aggregation work.',
    useWhen: 'You need warehouse-backed platform spend and direct-source rollups.',
    avoidWhen: 'You want attribution logic or modeled re-assignment of credit.',
  },
  {
    title: 'Brain Shopify direct-truth family',
    tables: [
      'shopify_growth_order_truth_daily_latest',
      'shopify_growth_order_truth_cac_orders_latest',
      'shopify_growth_order_truth_country_daily_latest',
      'shopify_growth_first_order_aov_daily_latest',
    ],
    purpose: 'Brain-specific Shopify truth layer for first-order eligibility, country CAC denominator logic, and direct new-customer AOV.',
    useWhen: 'You need Brain-safe first-order counts, exclusion-aware Shopify truth, or a direct replacement for Triple Whale AOV logic.',
    avoidWhen: 'You need blended company revenue or a metric that still depends on non-Shopify sources.',
  },
  {
    title: 'Brain direct country-spend family',
    tables: [
      'growth_country_spend_known_channels_daily_latest',
      'growth_country_spend_known_channels_country_daily_latest',
      'growth_country_cac_known_channels_daily_latest',
      'growth_country_cac_known_channels_breakout_latest',
    ],
    purpose: 'Direct spend and CAC scaffolding for the Brain country CAC rebuild, using known-channel numerator coverage plus Shopify first-order denominator logic.',
    useWhen: 'You want to trace the direct-source country CAC path or inspect which channels are already covered.',
    avoidWhen: 'You need a final all-channel country CAC answer before Google MCC and podcast are resolved.',
  },
];

const accessCards: AccessCard[] = [
  {
    title: 'What the read-only analytics role should allow',
    items: [
      'Athena query execution for SELECT-style analysis',
      'Glue catalog read access',
      'S3 read access for data lake prefixes',
      'S3 write access only to the Athena query-results bucket',
      'Local repo access so Claude Code can inspect code and docs',
    ],
  },
  {
    title: 'What it should not allow by default',
    items: [
      'Broad Secrets Manager read access',
      'CloudFormation, Lambda, ECS, or infra mutation',
      'Production data lake writes',
      'Source-system credential access unless explicitly needed',
    ],
  },
  {
    title: 'When Secrets Manager access is justified',
    items: [
      'Debugging a specific source integration or pipeline configuration',
      'Validating the exact client ID, token, or endpoint a job is using',
      'Comparing production and non-production integration settings',
      'Reading only explicitly approved secret paths, not wildcard access',
    ],
  },
  {
    title: 'What to tell the team',
    items: [
      'Yes, this role can still make Athena calls.',
      'No, it should not be a broad engineering admin role.',
      'Use direct sources for platform truth and operational truth.',
      'Use parity-safe warehouse tables when the question is Northbeam report parity.',
    ],
  },
];

const secretFamilyRows: SecretFamilyRow[] = [
  {
    family: 'Paid media platform credentials',
    examples: 'marsmen/meta-api, growth-reporting/google-ads, growth-reporting/google-ads-service-account, growth-reporting/amazon-ads, marsmen/axon-api/production',
    usedFor: 'Direct-source paid media pulls, token rotation, API debugging, and environment validation.',
    whenNeeded: 'Only when Claude Code is working on a source integration or validating which production credential path a job is using.',
    notes: 'Do not use this as the first stop for marketing-finance questions. Query the landed tables first.',
  },
  {
    family: 'Commerce credentials',
    examples: 'marsmen/shopify-api/production, shopify-api-credentials-marsmen-production, marsmen/amazon/spapi',
    usedFor: 'Order, refund, product, and marketplace ingestion pipelines.',
    whenNeeded: 'When debugging a commerce ingestion issue or checking which store/account a job points at.',
    notes: 'Commerce truth should still be validated against direct platform data after config checks.',
  },
  {
    family: 'Attribution and reporting credentials',
    examples: 'growth-reporting/northbeam, growth-reporting/triple-whale, marsmen/google-oauth/client',
    usedFor: 'Attribution exports, parity-safe reporting, and modeled rollups.',
    whenNeeded: 'When validating report configuration, attribution model settings, or secret-backed export parameters.',
    notes: 'This is where parity issues often surface, but the warehouse contract still matters more than the secret alone.',
  },
  {
    family: 'Lifecycle and retention credentials',
    examples: 'marsmen/klaviyo-read-only, marsmen/klaviyo-write, marsmen/stay/api, marsmen/ham/api, marsmen/recharge/webhook',
    usedFor: 'Messaging, retention, subscription, and customer-lifecycle jobs.',
    whenNeeded: 'When a retention or messaging pipeline is failing or appears pointed at the wrong account.',
    notes: 'Many business questions can be answered from marts without secret access.',
  },
  {
    family: 'CX and operations credentials',
    examples: 'marsmen/gorgias/api, marsmen/shiphero/api, marsmen-tracking-api-keys-production',
    usedFor: 'Support, tracking, fulfillment, and operational monitoring jobs.',
    whenNeeded: 'When support or shipment data is stale and you need to verify the upstream integration contract.',
    notes: 'Mission Control and Slack drops should usually be checked before escalating to secrets.',
  },
  {
    family: 'Delivery and collaboration secrets',
    examples: 'marsmen/slack-webhook, mars-men-monitoring-slack-webhook, notion-integration-token, marsmen/kieai/callback-token-production',
    usedFor: 'Slack delivery jobs, internal notifications, wiki sync, and workflow callbacks.',
    whenNeeded: 'When a delivery surface is posting to the wrong place or not authenticating correctly.',
    notes: 'These are configuration secrets, not data sources.',
  },
  {
    family: 'AI and creative vendor credentials',
    examples: 'marsmen/openrouter-api, marsmen/kieai/api-key-production, marsmen/longform/wavespeed-video-production, marsmen/google-gemini-api-key',
    usedFor: 'Creative generation, AI workflows, and experimental media tooling.',
    whenNeeded: 'When a model-backed workflow is failing auth or an environment is pointed at the wrong provider account.',
    notes: 'Keep these separate from analytics access. They should never be part of the default read-only role.',
  },
];

const linkSections: LinkSection[] = [
  {
    title: 'AWS',
    description: 'Primary AWS surfaces for querying and tracing the reporting stack.',
    links: [
      {
        title: 'Mars Men AWS Console',
        description: 'Primary AWS account surface for the Mars Men data and reporting stack.',
        url: 'https://631046354185.signin.aws.amazon.com/console',
        label: 'Open AWS Console',
      },
    ],
  },
  {
    title: 'Sheets',
    description: 'Google Sheet operating surfaces, split into stable paid-media views, intraday views, and Northbeam consumer vs raw operations workbooks.',
    links: [
      {
        title: 'Paid Media Dashboard',
        description: 'Stable multi-day paid-media workbook with regional dashboards, raw tabs, and the finance-lens environment.',
        url: 'https://docs.google.com/spreadsheets/d/1HtpIGwsjWS-iB5vkQs0hM_oJ00YnultHe_nEZWaao0k/edit',
        label: 'Open Stable Workbook',
      },
      {
        title: 'Paid Media Dashboard Daily',
        description: 'Current-day hourly Central-time workbook for intraday pacing. Meta, AppLovin, Shopify, and Stay-backed retention summaries are live; Google / YT / CTV are still pending.',
        url: 'https://docs.google.com/spreadsheets/d/1UIuj-Lk0W2-hz6GoUhuR9dY6qILGzMQvcTrTy1Pfu78/edit?gid=188892209#gid=188892209',
        label: 'Open Intraday Workbook',
      },
      {
        title: 'Northbeam Daily 1DC Workbook',
        description: 'Split daily attribution workbook for the new 1DC by-region Northbeam view. Keep this as the daily reporting surface under QA, not the current raw export destination. The live split-workbook refresh runs daily at 11:45 UTC.',
        url: 'https://docs.google.com/spreadsheets/d/1yFKl_DEY6ea1nvoKLNnAqceo9DeS2O9KQbUr5YUZw9A/edit?gid=0#gid=0',
        label: 'Open Daily Workbook',
      },
      {
        title: 'Northbeam Hourly 1DC Workbook',
        description: 'Split hourly attribution monitoring workbook with regional views. Its raw Northbeam inputs still land in the primary operations workbook, and the live refresh rule runs every 15 minutes.',
        url: 'https://docs.google.com/spreadsheets/d/1XEZc3DyMsoVLdiQtBuwM-XnYRq8rq5FTfQlaaRc-D5s/edit?gid=0#gid=0',
        label: 'Open Hourly Workbook',
      },
      {
        title: 'Northbeam Operations Workbook',
        description: 'Current live workbook for direct Northbeam daily and hourly exports, including the raw tabs that downstream hourly and creative views still depend on.',
        url: 'https://docs.google.com/spreadsheets/d/1tBtzgCXQqSzILbOeM4roGfc9l1D09XGO6tFvF3TF4ps/edit?gid=1079106200#gid=1079106200',
        label: 'Open Operations Workbook',
      },
      {
        title: 'NB Meta Creative Optimization Sheet',
        description: 'Creative optimization workbook that uses Northbeam-derived outputs. Its consumer views now sit on top of an authoritative YTD raw tab, and the supporting refresh rule runs hourly at :35 UTC.',
        url: 'https://docs.google.com/spreadsheets/d/1w8MLMh-kJRzlGHqNG9LcSp6dedPLooysFAJTZdKcUNs/edit?gid=793568703#gid=793568703',
        label: 'Open Creative Sheet',
      },
      {
        title: 'NB Meta Creative Raw YTD',
        description: 'Direct link to the authoritative YTD raw tab inside the creative workbook.',
        url: 'https://docs.google.com/spreadsheets/d/1w8MLMh-kJRzlGHqNG9LcSp6dedPLooysFAJTZdKcUNs/edit?gid=1318622644#gid=1318622644',
        label: 'Open Raw YTD Tab',
      },
    ],
  },
  {
    title: 'Dashboards',
    description: 'Primary visual surfaces for operational exploration and dashboarding.',
    links: [
      {
        title: 'Brain Dashboard',
        description: 'Cross-department daily brief, metrics health, and anomaly surface backed by the ECS Brain runtime.',
        url: 'https://automations.mengotomars.com/brain-dashboard',
        label: 'Open Brain Dashboard',
      },
      {
        title: 'Mission Control',
        description: 'Internal operating dashboard for package tracking, shipment visibility, and ops issue management.',
        url: 'https://automations.mengotomars.com/mission-control',
        label: 'Open Mission Control',
      },
      {
        title: 'Mars Men Superset',
        description: 'Interactive reporting surface for operational dashboards and deeper analysis.',
        url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/',
        label: 'Open Superset',
      },
    ],
  },
  {
    title: 'Docs',
    description: 'Current planning docs, trackers, and technical references behind the reporting rebuild and workbook cleanup.',
    links: [
      {
        title: 'llms.txt',
        description: 'Public machine-readable index for the /data-platform handbook and related resources.',
        url: '/llms.txt',
        label: 'Open llms.txt',
      },
      {
        title: 'llms-full.txt',
        description: 'Longer machine-readable handbook for assistants, copilots, and retrieval tools.',
        url: '/llms-full.txt',
        label: 'Open llms-full.txt',
      },
      {
        title: 'Data Source Catalog',
        description: 'Team and technical catalog of source systems, warehouse families, jobs, and delivery surfaces.',
        url: '/downloads/data-platform/DATA_SOURCE_CATALOG_SUMMARY.md',
        label: 'Open public copy',
      },
      {
        title: 'Reporting Program Tracker',
        description: 'Program-level tracker for the current reporting surfaces, gaps, and next actions across paid media, Northbeam, and related work.',
        url: '/downloads/data-platform/REPORTING_PROGRAM_TRACKER_SUMMARY.md',
        label: 'Open public copy',
      },
      {
        title: 'Brain Direct-Source Tracker',
        description: 'Working tracker for the Brain direct-source migration, source decisions, Tatari policy, Stay vs HAM resolution, and country CAC rebuild.',
        url: '/downloads/data-platform/BRAIN_DIRECT_SOURCE_TRACKER_SUMMARY.md',
        label: 'Open public copy',
      },
      {
        title: 'Northbeam Recovery Plan',
        description: 'Main working plan for the workbook map, naming, cadence, slicing strategy, and execution order across the Northbeam and paid-media surfaces.',
        url: '/downloads/data-platform/NORTHBEAM_RECOVERY_PLAN_SUMMARY.md',
        label: 'Open public copy',
      },
      {
        title: 'Reporting Surface Execution Checklist',
        description: 'Implementation checklist covering the stable paid-media workbook, intraday workbook, Northbeam daily/hourly, and creative workstream.',
        url: '/downloads/data-platform/REPORTING_SURFACE_EXECUTION_CHECKLIST_SUMMARY.md',
        label: 'Open public copy',
      },
      {
        title: 'Hunter / Raheel Analytics Dashboard Review',
        description: 'Detailed review of the paid media daily workbook contract, source scope, and direct-API rebuild criteria.',
        url: '/downloads/data-platform/ANALYTICS_DASHBOARD_REVIEW_SUMMARY.md',
        label: 'Open public copy',
      },
      {
        title: 'AWS Data Consumption Agent Guide',
        description: 'Use the AI COE resource library on this page for the readable version of the repo-level AWS and agent guidance.',
        url: '/data-platform',
        label: 'Open AI resource library',
        tab: 'ai',
        sectionId: 'ai-resource-library',
      },
      {
        title: 'AWS Secrets Manager Inventory Appendix',
        description: 'Use the on-page Secrets Manager families section for the human-readable version before going to engineering-only appendices.',
        url: '/data-platform',
        label: 'Open on-page secrets section',
        tab: 'technical',
        sectionId: 'technical-secrets-manager-families',
      },
    ],
  },
];

const supersetDashboards: SupersetDashboard[] = [
  {
    title: 'Bill.com Payables Overview',
    owner: 'Jacob Karmel',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/billcom-payables/',
    slug: 'billcom-payables',
    sensitive: true,
    group: 'Finance',
  },
  {
    title: 'Cohort Payback & Profitability',
    owner: 'Jacob Karmel',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/cohort-profitability/',
    slug: 'cohort-profitability',
    sensitive: true,
    group: 'Finance',
  },
  {
    title: 'Daily Command Center',
    owner: 'Daniel Wallock',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/daily-command-center/',
    slug: 'daily-command-center',
    sensitive: false,
    group: 'Operations',
  },
  {
    title: 'Executive Summary',
    owner: 'Chelsea (Avita) Ramdass',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/executive-summary/',
    slug: 'executive-summary',
    sensitive: false,
    group: 'Executive',
  },
  {
    title: 'Fulfillment Operations',
    owner: 'Michael Jacobs',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/fulfillment-operations/',
    slug: 'fulfillment-operations',
    sensitive: false,
    group: 'Operations',
  },
  {
    title: 'Gorgias CX Dashboard',
    owner: 'Chelsea (Avita) Ramdass',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/gorgias-cx/',
    slug: 'gorgias-cx',
    sensitive: false,
    group: 'Operations',
  },
  {
    title: 'HAM Subscription Retention',
    owner: 'Chad Ross',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/ham-retention/',
    slug: 'ham-retention',
    sensitive: false,
    group: 'Retention',
  },
  {
    title: 'Junip Reviews',
    owner: 'Chelsea (Avita) Ramdass',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/junip-reviews/',
    slug: 'junip-reviews',
    sensitive: false,
    group: 'Retention',
  },
  {
    title: 'Marketing Channel Performance',
    owner: 'Daniel Wallock',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/marketing-channel-performance/',
    slug: 'marketing-channel-performance',
    sensitive: false,
    group: 'Marketing',
  },
  {
    title: 'Regional Comparison',
    owner: 'Daniel Wallock',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/regional-comparison/',
    slug: 'regional-comparison',
    sensitive: false,
    group: 'Marketing',
  },
  {
    title: 'Regional Deep Dive',
    owner: 'Daniel Wallock',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/regional-deep-dive/',
    slug: 'regional-deep-dive',
    sensitive: false,
    group: 'Marketing',
  },
  {
    title: 'Teller Finance Overview',
    owner: 'Jacob Karmel',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/teller-finance/',
    slug: 'teller-finance',
    sensitive: true,
    group: 'Finance',
  },
  {
    title: 'Trend Analysis',
    owner: 'Daniel Wallock',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/trend-analysis/',
    slug: 'trend-analysis',
    sensitive: false,
    group: 'Marketing',
  },
  {
    title: 'Weekly Creative Performance',
    owner: 'Brennan Zielinski',
    url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/superset/dashboard/weekly-creative-performance/',
    slug: 'weekly-creative-performance',
    sensitive: false,
    group: 'Marketing',
  },
];

const supersetGroupOrder: SupersetDashboard['group'][] = ['Finance', 'Operations', 'Marketing', 'Retention', 'Executive'];

const roleCards: RoleCard[] = [
  {
    role: 'Executive / leadership',
    useFor: 'Morning operating read across growth, finance, retention, CX, and exceptions without jumping across six tools.',
    startWith: 'Brain Dashboard and its Daily Brief, All Metrics, and Deep Analysis views.',
    avoid: 'Using the Brain as a silent source chooser without checking the source-family tooltip and caveats.',
    linkedReport: 'Brain Dashboard',
  },
  {
    role: 'Finance',
    useFor: 'Daily financial truth, pacing, and order/revenue accountability.',
    startWith: 'Finance Daily Dashboard and direct-source reporting surfaces.',
    avoid: 'Attribution tools as the answer for finance truth.',
    linkedReport: 'Finance Daily Dashboard',
  },
  {
    role: 'Growth / marketing finance',
    useFor: 'Platform spend, gross sales, CAC, and intraday performance reading.',
    startWith: 'Paid Media Dashboard, Paid Media Dashboard Daily, direct APIs, and paid-media warehouse tables.',
    avoid: 'Using Northbeam or Triple Whale to answer platform-truth questions.',
    linkedReport: 'Paid Media Dashboard',
  },
  {
    role: 'Attribution / scaling',
    useFor: '1DC daily and hourly monitoring by region, pacing by channel, and Northbeam interpretation.',
    startWith: 'Northbeam parity workbooks and parity warehouse tables.',
    avoid: 'Using broad Northbeam warehouse tables when exact parity matters.',
    linkedReport: 'Northbeam 1DC Hourly by Region',
  },
  {
    role: 'Creative / strategy',
    useFor: 'Meta creative optimization, contribution reads, and mix interpretation.',
    startWith: 'NB Meta Creative Optimization sheet and the attribution layer.',
    avoid: 'Treating creative optimization sheets as direct platform truth.',
    linkedReport: 'NB Meta Creative Optimization API Sheet',
  },
];

const ruleCards: RuleCard[] = [
  {
    title: 'Direct truth beats modeled truth',
    body: 'If the question is what actually happened in Meta, Google Ads, Shopify, or Axon, go to the direct platform source first.',
  },
  {
    title: 'One metric should have one default source policy',
    body: 'Especially in the Brain, each metric should default to one approved truth layer. Do not let every consumer choose between direct, blended, and attribution paths ad hoc.',
  },
  {
    title: 'Northbeam has two warehouse families for a reason',
    body: 'Use the parity family when mirroring a Northbeam report. Use the broad family for exploration. Do not treat them as interchangeable.',
  },
  {
    title: 'Morning reports need readiness gates, not just a cron',
    body: 'A 9:00 AM report is only trustworthy when the upstream sources for that metric have already landed. Freshness and readiness rules matter as much as the schedule itself.',
  },
  {
    title: 'A warehouse table is not automatically canonical',
    body: 'Some warehouse tables are broad landings, not the final answer. Always ask what upstream contract they represent before trusting them.',
  },
  {
    title: 'Claude Code should query, not mutate',
    body: 'The default team package should be read-only analytics access: Athena, Glue read, S3 read, and query results only.',
  },
];

const caveatCards: CaveatCard[] = [
  {
    title: 'Brain reporting now runs on ECS with a morning schedule',
    severity: 'Important',
    body: 'The Brain reporting runtime has moved off the old single-Lambda pattern onto a two-phase ECS workflow. The active Scheduler target is the ECS-backed state machine at 09:00 CT, and the anomaly runtime follows at 10:00 CT.',
  },
  {
    title: 'Northbeam has two distinct data families',
    severity: 'Important',
    body: 'We have separate Northbeam warehouse sources for different purposes. `northbeam_parity_*` is the report-safe family intended for UI-parity 1DC reporting. `northbeam_growth_report_*` is the broader exploratory family. They are both valid, but they are not interchangeable.',
  },
  {
    title: 'Google MCC still blocks the final direct country-CAC cutover',
    severity: 'In progress',
    body: 'The Shopify denominator path, Tatari ingest, and known-channel spend scaffolding are now in place, but the final direct country-CAC numerator still depends on Google MCC approval plus the developer token and manager/client account IDs being populated.',
  },
  {
    title: 'The old growth daily brief is no longer the main morning path',
    severity: 'Important',
    body: 'The legacy `marsmen-prod-growth-daily-brief` EventBridge rule is disabled. The morning executive reporting path is now the Brain reporting runtime plus the Brain anomaly runtime, not the old one-off brief schedule.',
  },
  {
    title: 'The legacy Brain runtime still exists in AWS, but its scheduler is disabled',
    severity: 'Important',
    body: 'The old `marsmen-brain-reporting-runtime-prod` state machine and schedule still exist for rollback safety, but the active Scheduler target is `marsmen-brain-reporting-runtime-ecs-prod`. The guide should treat ECS as the live path.',
  },
  {
    title: 'Tatari is live, but treated as US-only for now',
    severity: 'Important',
    body: 'Tatari data now lands through the S3 data share and is queryable in Athena. The current share does not expose explicit geography fields, so country-aware logic should treat Tatari as US-only until Tatari provides a real market contract.',
  },
  {
    title: 'Podcast remains an open source contract',
    severity: 'Planned',
    body: 'Podscale public API support could not be verified. Podscribe exposes public API docs, but the actual spend and day-level reporting contract still needs confirmation before podcast can join the direct-source CAC path.',
  },
  {
    title: 'Aggregated and modeled sources are not the fastest truth',
    severity: 'Important',
    body: 'Triple Whale and Northbeam are useful because they aggregate or model data, but they are not the fastest source for current-day platform truth. If the question is “what happened right now,” the direct source should still win.',
  },
];

const claudeSteps: ClaudeStep[] = [
  {
    step: '01',
    title: 'Start with the question',
    body: 'Be explicit about whether you need platform truth, marketing finance, attribution, or exploratory analysis before asking Claude Code to query anything.',
  },
  {
    step: '02',
    title: 'Pick the right source family',
    body: 'Tell Claude Code which direct source or warehouse family should be used so it does not infer the wrong truth surface.',
  },
  {
    step: '03',
    title: 'Query Athena safely',
    body: 'A read-only analytics role is enough for Athena calls, Glue reads, and S3 query-result access. That is the standard team access package.',
  },
  {
    step: '04',
    title: 'Validate before operationalizing',
    body: 'If the result is going into a daily workbook, dashboard, or Slack job, confirm the warehouse path actually matches the business definition you care about.',
  },
];

const claudeSurfaces: ClaudeSurface[] = [
  {
    title: 'Claude',
    useFor: 'General chat, summarization, drafting, and lightweight reasoning when you do not need the local repo or terminal.',
    notFor: 'Do not assume it can inspect this codebase, run commands, or query AWS unless those capabilities are explicitly connected.',
  },
  {
    title: 'Claude CoWork',
    useFor: 'Shared team context, collaborative discussion, and connector-backed lookups inside a collaborative workspace.',
    notFor: 'Do not treat it like a local engineering runtime with repo access, shell access, or direct deployment control.',
  },
  {
    title: 'Claude Code',
    useFor: 'Local repo work, shell commands, Athena queries through the configured AWS profile, tracing jobs, and making controlled code changes.',
    notFor: 'Do not give it broad production write access by default. Start with read-only analytics access and escalate only when needed.',
  },
  {
    title: 'MCPs',
    useFor: 'The connector layer that lets an agent reach systems outside the repo, such as AWS helpers, Superset, Notion, Slack, browser tooling, and internal tools.',
    notFor: 'They are not the data source itself. They are the access path that lets an agent inspect or act on an external system.',
  },
];

const tabs: { id: TabId; label: string }[] = [
  { id: 'start', label: 'Start Here' },
  { id: 'sources', label: 'Sources & Truth' },
  { id: 'dashboards', label: 'Dashboards & Reports' },
  { id: 'ai', label: 'AI Center of Excellence' },
  { id: 'technical', label: 'Technical Guide' },
];

/* ── Search index: collect all searchable text fragments with their tab ── */
type SearchEntry = { tab: TabId; label: string; text: string };

const searchIndex: SearchEntry[] = [
  ...roleCards.map((c) => ({ tab: 'start' as TabId, label: c.role, text: `${c.role} ${c.useFor} ${c.startWith} ${c.avoid}` })),
  ...questionCards.map((c) => ({ tab: 'start' as TabId, label: c.question, text: `${c.question} ${c.source} ${c.guidance}` })),
  ...ruleCards.map((c) => ({ tab: 'start' as TabId, label: c.title, text: `${c.title} ${c.body}` })),
  ...overviewCards.map((c) => ({ tab: 'start' as TabId, label: c.title, text: `${c.title} ${c.description}` })),
  ...sourceFamilyCards.map((c) => ({
    tab: 'sources' as TabId,
    label: c.title,
    text: `${c.title} ${c.systems.join(' ')} ${c.warehouse.join(' ')} ${c.useFor}`,
  })),
  ...aggregatedSourceProfiles.map((c) => ({
    tab: 'sources' as TabId,
    label: c.title,
    text: `${c.title} ${c.sourceType} ${c.contains} ${c.bestFor} ${c.freshness} ${c.notFor}`,
  })),
  ...dataPathCards.map((c) => ({ tab: 'sources' as TabId, label: c.title, text: `${c.title} ${c.bestFor} ${c.tradeoff} ${c.notIdealFor}` })),
  ...sourceRows.map((c) => ({
    tab: 'sources' as TabId,
    label: c.source,
    text: `${c.source} ${c.direct} ${c.intermediary} ${c.freshness} ${c.intradayTrust} ${c.canonicalUse}`,
  })),
  ...canonicalSourceRows.map((c) => ({
    tab: 'sources' as TabId,
    label: c.sourceSystem,
    text: `${c.sourceSystem} ${c.steward} ${c.notes} ${c.canonicalTables.join(' ')}`,
  })),
  ...detailedSourceRows.map((c) => ({
    tab: 'sources' as TabId,
    label: c.sourceSystem,
    text: `${c.sourceSystem} ${c.category} ${c.primaryData} ${c.implementationSurface} ${c.cadence} ${c.status}`,
  })),
  ...reportCards.map((c) => ({ tab: 'dashboards' as TabId, label: c.title, text: `${c.title} ${c.category} ${c.purpose} ${c.owner} ${c.status} ${c.platform}` })),
  ...supersetDashboards.map((c) => ({ tab: 'dashboards' as TabId, label: c.title, text: `${c.title} ${c.owner} ${c.group} superset` })),
  ...linkSections.flatMap((s) => s.links.map((c) => ({ tab: 'dashboards' as TabId, label: c.title, text: `${c.title} ${c.description}` }))),
  ...aiPillars.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.body}` })),
  ...unifiedToolCards.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.plainEnglish} ${c.bestFor} ${c.connectedSystems} ${c.avoidFor}` })),
  ...aiWorkflowCards.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.steps.join(' ')}` })),
  ...aiOnboardingTracks.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.audience} ${c.steps.join(' ')} ${c.outcome}` })),
  ...aiDownloadArtifacts.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.description}` })),
  ...aiOnboardingForms.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.description}` })),
  ...aiFaqCards.map((c) => ({ tab: 'ai' as TabId, label: c.question, text: `${c.question} ${c.answer}` })),
  ...businessResources.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.description}` })),
  ...developerResources.map((c) => ({ tab: 'ai' as TabId, label: c.title, text: `${c.title} ${c.description}` })),
  ...awsCards.map((c) => ({ tab: 'technical' as TabId, label: c.title, text: `${c.title} ${c.body}` })),
  ...warehouseCards.map((c) => ({ tab: 'technical' as TabId, label: c.title, text: `${c.title} ${c.purpose} ${c.useWhen} ${c.avoidWhen} ${c.tables.join(' ')}` })),
  ...operationalJobs.map((c) => ({
    tab: 'technical' as TabId,
    label: c.job,
    text: `${c.job} ${c.domain} ${c.businessOutput} ${c.cadence}`,
  })),
  ...slackSurfaces.map((c) => ({
    tab: 'technical' as TabId,
    label: c.surface,
    text: `${c.surface} ${c.domain} ${c.purpose} ${c.cadence}`,
  })),
  ...claudeSurfaces.map((c) => ({ tab: 'technical' as TabId, label: c.title, text: `${c.title} ${c.useFor} ${c.notFor}` })),
  ...claudeSteps.map((c) => ({ tab: 'technical' as TabId, label: c.title, text: `${c.title} ${c.body}` })),
  ...accessCards.map((c) => ({ tab: 'technical' as TabId, label: c.title, text: `${c.title} ${c.items.join(' ')}` })),
];

const defaultFooterLinks = [
  { label: 'Easy Street', href: 'https://easystreetbrands.com' },
  { label: 'Mars Men', href: 'https://mengotomars.com' },
  { label: 'Hollow Socks', href: 'https://hollowsocks.com' },
];

const renderResourcePreview = (content: string): React.ReactNode[] => {
  const lines = content.split('\n');
  const nodes: React.ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      nodes.push(
        <pre key={`code-${nodes.length}`} className="data-guide-resource-code">
          <code>{codeLines.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    if (trimmed.startsWith('# ')) {
      nodes.push(<h4 key={`h1-${nodes.length}`} className="data-guide-resource-title">{trimmed.slice(2)}</h4>);
      index += 1;
      continue;
    }

    if (trimmed.startsWith('## ')) {
      nodes.push(<h5 key={`h2-${nodes.length}`} className="data-guide-resource-heading">{trimmed.slice(3)}</h5>);
      index += 1;
      continue;
    }

    if (trimmed.startsWith('- ')) {
      const items: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith('- ')) {
        items.push(lines[index].trim().slice(2));
        index += 1;
      }
      nodes.push(
        <ul key={`ul-${nodes.length}`} className="data-guide-resource-list">
          {items.map((item) => <li key={item}>{item}</li>)}
        </ul>,
      );
      continue;
    }

    const paragraphLines: string[] = [];
    while (index < lines.length) {
      const current = lines[index].trim();
      if (!current || current.startsWith('#') || current.startsWith('- ') || current.startsWith('```')) {
        break;
      }
      paragraphLines.push(lines[index].trim());
      index += 1;
    }
    nodes.push(
      <p key={`p-${nodes.length}`} className="data-guide-resource-paragraph">
        {paragraphLines.join(' ')}
      </p>,
    );
  }

  return nodes;
};

const buildChatAssistantPrompt = (context: AiLaunchContext): string => [
  'You are helping with the Mars Men data platform and AI operating guide.',
  'Use the public handbook links below as the primary context instead of guessing.',
  '',
  `Page: ${context.pageUrl}`,
  `llms.txt: ${context.llmsUrl}`,
  `llms-full.txt: ${context.llmsFullUrl}`,
  `AI quickstart: ${context.aiQuickstartUrl}`,
  `AWS access guide: ${context.awsGuideUrl}`,
  `Data source catalog: ${context.dataCatalogUrl}`,
  `Reporting tracker: ${context.reportingTrackerUrl}`,
  `Brain direct-source tracker: ${context.brainTrackerUrl}`,
  '',
  'When you answer:',
  '- distinguish direct source truth, blended business truth, and attribution truth',
  '- call out freshness or source caveats explicitly',
  '- cite which of the linked public docs you used',
  '',
  'Task:',
  '[Paste your question or request here]',
].join('\n');

const buildCodingAgentPrompt = (context: AiLaunchContext): string => [
  'Use the public Mars Men handbook below as external context before working on the task.',
  '',
  `Start here: ${context.pageUrl}`,
  `Machine-readable index: ${context.llmsUrl}`,
  `Full handbook: ${context.llmsFullUrl}`,
  `Data source catalog: ${context.dataCatalogUrl}`,
  `Reporting tracker: ${context.reportingTrackerUrl}`,
  `Brain direct-source tracker: ${context.brainTrackerUrl}`,
  '',
  'Operating rules:',
  '- do not guess which source is canonical',
  '- distinguish warehouse truth, direct platform truth, and attribution truth',
  '- call out freshness expectations before using a source in a decision',
  '- if repo access exists, use the docs above as the public operating context',
  '',
  'Task:',
  '[Paste the repo, data, or automation task here]',
].join('\n');

const DataPlatformGuide: React.FC<DataPlatformGuideProps> = ({
  theme = 'portal',
  headerSlot,
  heroKicker = 'Data Platform',
  heroTitle = 'Where the team should go for data',
  heroSubtitle = 'The plain-English map of the Easy Street reporting stack: what the data lake does, which sources are truth, how the Brain and reporting runtimes fit together, and how to use Claude Code safely.',
  footerText = '© 2026 Easy Street Brands. Internal data and automation guide.',
  footerLinks = defaultFooterLinks,
  aiCenterNavigationHandlers,
}) => {
  const { user, apiBaseUrl } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const hash = window.location.hash.replace('#', '') as TabId;
    return tabs.some((t) => t.id === hash) ? hash : 'start';
  });
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set(['tool-claude-code']));
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState(
    () => new URLSearchParams(window.location.search).get('q') || '',
  );
  const [automationIdeas, setAutomationIdeas] = useState<AutomationBoardItem[]>(defaultAutomationIdeas);
  const [automationBoardLoading, setAutomationBoardLoading] = useState(false);
  const [automationBoardError, setAutomationBoardError] = useState('');
  const [automationBoardUpdatedAt, setAutomationBoardUpdatedAt] = useState('');
  const [automationBoardSource, setAutomationBoardSource] = useState<'notion' | 'fallback'>('fallback');
  const [automationSubmitting, setAutomationSubmitting] = useState(false);
  const [automationSubmitMessage, setAutomationSubmitMessage] = useState('');
  const [schemaCatalog, setSchemaCatalog] = useState<SchemaCatalogResponse | null>(null);
  const [schemaCatalogLoading, setSchemaCatalogLoading] = useState(false);
  const [schemaCatalogError, setSchemaCatalogError] = useState('');
  const [schemaSearchQuery, setSchemaSearchQuery] = useState('');
  const [schemaDatabaseFilter, setSchemaDatabaseFilter] = useState('');
  const [schemaSourceFamilyFilter, setSchemaSourceFamilyFilter] = useState('');
  const [schemaAskQuestion, setSchemaAskQuestion] = useState('');
  const [schemaAskLoading, setSchemaAskLoading] = useState(false);
  const [schemaAskError, setSchemaAskError] = useState('');
  const [schemaAskResponse, setSchemaAskResponse] = useState<SchemaAskResponse | null>(null);
  const [aiLauncherMessage, setAiLauncherMessage] = useState('');
  const [aiLauncherActiveId, setAiLauncherActiveId] = useState<AiLaunchCard['id'] | null>(null);
  const [resourceContents, setResourceContents] = useState<Record<string, string>>({});
  const [resourceLoadError, setResourceLoadError] = useState('');
  const [automationIdeaDraft, setAutomationIdeaDraft] = useState<AutomationRequestDraft>({
    name: '',
    brand: 'Mars Men',
    department: 'Marketing',
    priority: 'Medium',
    task: '',
    currentProcess: '',
    desiredOutcome: '',
  });
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);
  const [onboardingSubmitMessage, setOnboardingSubmitMessage] = useState('');
  const [onboardingName, setOnboardingName] = useState('');
  const [onboardingTeam, setOnboardingTeam] = useState('');
  const [onboardingManager, setOnboardingManager] = useState('');
  const [onboardingSurfaces, setOnboardingSurfaces] = useState<Set<string>>(new Set());
  const [onboardingUseCase, setOnboardingUseCase] = useState('');
  const [onboardingAccess, setOnboardingAccess] = useState<Set<string>>(new Set());
  const [onboardingJustification, setOnboardingJustification] = useState('');
  const [colorMode, setColorMode] = useState<'light' | 'dark'>(() => {
    try {
      const stored = localStorage.getItem('data-guide-color-mode');
      if (stored === 'light' || stored === 'dark') return stored;
    } catch { /* ignore */ }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const tabBarRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const aiLaunchContext = useMemo<AiLaunchContext>(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const buildUrl = (path: string) => (origin ? new URL(path, origin).toString() : path);
    return {
      pageUrl: buildUrl('/data-platform#ai'),
      llmsUrl: buildUrl('/llms.txt'),
      llmsFullUrl: buildUrl('/llms-full.txt'),
      aiQuickstartUrl: buildUrl('/downloads/ai-coe/NONTECHNICAL_AI_QUICKSTART.md'),
      awsGuideUrl: buildUrl('/downloads/ai-coe/AWS_ACCESS_ONBOARDING_GUIDE.md'),
      dataCatalogUrl: buildUrl('/downloads/data-platform/DATA_SOURCE_CATALOG_SUMMARY.md'),
      reportingTrackerUrl: buildUrl('/downloads/data-platform/REPORTING_PROGRAM_TRACKER_SUMMARY.md'),
      brainTrackerUrl: buildUrl('/downloads/data-platform/BRAIN_DIRECT_SOURCE_TRACKER_SUMMARY.md'),
    };
  }, []);

  const aiLaunchCards = useMemo<AiLaunchCard[]>(() => [
    {
      id: 'chatgpt',
      title: 'Open in ChatGPT',
      category: 'Web assistant',
      description: 'Copies the page context pack, then opens ChatGPT in a new tab so someone can paste the prompt immediately.',
      helper: 'This mirrors the docs-site pattern: package the context first, then open the chat surface.',
      actionLabel: 'Copy prompt and open ChatGPT',
      completionLabel: 'Prompt copied. ChatGPT opened in a new tab.',
      openUrl: 'https://chatgpt.com/',
      buildPrompt: buildChatAssistantPrompt,
    },
    {
      id: 'claude',
      title: 'Open in Claude',
      category: 'Web assistant',
      description: 'Copies the same public context pack, then opens Claude so the conversation starts from the handbook instead of a blank page.',
      helper: 'Best for policy, documentation, operating-model, and cross-functional Q&A work.',
      actionLabel: 'Copy prompt and open Claude',
      completionLabel: 'Prompt copied. Claude opened in a new tab.',
      openUrl: 'https://claude.ai/new',
      buildPrompt: buildChatAssistantPrompt,
    },
    {
      id: 'claude-code',
      title: 'Claude Code',
      category: 'Coding agent',
      description: 'Prepares a repo-aware prompt pack for a local Claude Code session using the public llms and handbook files as context.',
      helper: 'There is no stable public browser deep link for local Claude Code, so the best practice is copy-first.',
      actionLabel: 'Copy prompt for Claude Code',
      completionLabel: 'Claude Code prompt copied. Paste it into your local session.',
      buildPrompt: buildCodingAgentPrompt,
    },
    {
      id: 'codex',
      title: 'Codex',
      category: 'Coding agent',
      description: 'Prepares the same context pack for Codex so engineering or analytics tasks start from the operating docs and source rules.',
      helper: 'This follows the same copy-first pattern used by docs sites when no reliable local-agent deep link exists.',
      actionLabel: 'Copy prompt for Codex',
      completionLabel: 'Codex prompt copied. Paste it into your local session.',
      buildPrompt: buildCodingAgentPrompt,
    },
  ], []);

  const toggleColorMode = useCallback(() => {
    setColorMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem('data-guide-color-mode', next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /* Sync color mode class to the nearest .app ancestor or body so it can override brand-level dark styles */
  useEffect(() => {
    const shell = document.querySelector('.data-guide-shell');
    const appWrapper = shell?.closest('.app') ?? document.body;
    if (colorMode === 'light') {
      appWrapper.classList.add('data-guide-force-light');
      appWrapper.classList.remove('data-guide-force-dark');
    } else {
      appWrapper.classList.add('data-guide-force-dark');
      appWrapper.classList.remove('data-guide-force-light');
    }
    return () => {
      appWrapper.classList.remove('data-guide-force-light', 'data-guide-force-dark');
    };
  }, [colorMode]);

  /* ── URL hash sync ── */
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '') as TabId;
      if (tabs.some((t) => t.id === hash)) setActiveTab(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const toggleAccordion = useCallback((key: string) => {
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleStatusFilter = useCallback((status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const filteredReports = statusFilters.size === 0
    ? reportCards
    : reportCards.filter((r) => statusFilters.has(r.status));

  const handleTabClick = useCallback((id: TabId) => {
    setActiveTab(id);
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleAutomationIdeaChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setAutomationIdeaDraft((prev) => ({ ...prev, [name]: value }));
  }, []);

  const loadSchemaCatalog = useCallback(async (forceRefresh = false) => {
    if (!apiBaseUrl) {
      setSchemaCatalogError('The shared API base URL is not configured yet, so the live schema explorer cannot load.');
      return;
    }
    setSchemaCatalogLoading(true);
    setSchemaCatalogError('');
    try {
      const payload = await fetchSchemaCatalog(apiBaseUrl, { refresh: forceRefresh });
      setSchemaCatalog(payload);
    } catch (error) {
      console.warn('Failed to load schema catalog', error);
      setSchemaCatalogError(error instanceof Error ? error.message : 'Failed to load the live schema catalog.');
    } finally {
      setSchemaCatalogLoading(false);
    }
  }, [apiBaseUrl]);

  const handleSchemaAskSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const question = schemaAskQuestion.trim();
    if (!question) return;
    if (!apiBaseUrl) {
      setSchemaAskError('The shared API base URL is not configured yet, so the schema assistant cannot run.');
      return;
    }
    setSchemaAskLoading(true);
    setSchemaAskError('');
    setSchemaAskResponse(null);
    try {
      const response = await askSchemaExplorer(apiBaseUrl, {
        question,
        database: schemaDatabaseFilter || undefined,
        sourceFamily: schemaSourceFamilyFilter || undefined,
      });
      setSchemaAskResponse(response);
    } catch (error) {
      console.warn('Failed to ask schema explorer', error);
      setSchemaAskError(error instanceof Error ? error.message : 'Failed to ask the schema explorer.');
    } finally {
      setSchemaAskLoading(false);
    }
  }, [apiBaseUrl, schemaAskQuestion, schemaDatabaseFilter, schemaSourceFamilyFilter]);

  const handleAiLaunch = useCallback(async (card: AiLaunchCard) => {
    const prompt = card.buildPrompt(aiLaunchContext);
    let copied = false;

    try {
      await navigator.clipboard.writeText(prompt);
      copied = true;
    } catch (error) {
      console.warn('Failed to copy AI launcher prompt', error);
    }

    if (card.openUrl) {
      window.open(card.openUrl, '_blank', 'noopener,noreferrer');
    }

    setAiLauncherActiveId(card.id);
    setAiLauncherMessage(
      copied
        ? card.completionLabel
        : `The prompt could not be copied automatically. You can still use ${aiLaunchContext.llmsFullUrl} and ${aiLaunchContext.pageUrl} as the starting context.`,
    );

    window.setTimeout(() => {
      setAiLauncherActiveId((current) => (current === card.id ? null : current));
      setAiLauncherMessage((current) => (
        current === card.completionLabel
          || current.startsWith('The prompt could not be copied automatically.')
          ? ''
          : current
      ));
    }, 4000);
  }, [aiLaunchContext]);

  const loadAutomationBoard = useCallback(async () => {
    if (!apiBaseUrl) {
      setAutomationBoardSource('fallback');
      setAutomationBoardError('Shared API base URL is not configured yet, so the page is showing example requests instead of the live board.');
      setAutomationIdeas(defaultAutomationIdeas);
      return;
    }

    setAutomationBoardLoading(true);
    setAutomationBoardError('');
    try {
      const payload = await fetchAutomationBoard(apiBaseUrl);
      setAutomationIdeas(payload.items);
      setAutomationBoardUpdatedAt(payload.updatedAt);
      setAutomationBoardSource('notion');
    } catch (error) {
      console.warn('Failed to load automation board', error);
      setAutomationBoardSource('fallback');
      setAutomationBoardError(error instanceof Error ? error.message : 'Failed to load the live automation board.');
      setAutomationIdeas(defaultAutomationIdeas);
    } finally {
      setAutomationBoardLoading(false);
    }
  }, [apiBaseUrl]);

  const handleAutomationIdeaSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const task = automationIdeaDraft.task.trim();
    const currentProcess = automationIdeaDraft.currentProcess.trim();
    const desiredOutcome = automationIdeaDraft.desiredOutcome.trim();
    if (!task || !currentProcess || !desiredOutcome) return;

    if (!apiBaseUrl) {
      setAutomationSubmitMessage('The shared API base URL is not configured, so this request cannot be sent to the live board yet.');
      return;
    }

    setAutomationSubmitting(true);
    setAutomationSubmitMessage('');
    try {
      const response = await submitAutomationRequest(apiBaseUrl, {
        ...automationIdeaDraft,
        task,
        currentProcess,
        desiredOutcome,
      });
      setAutomationSubmitMessage(response.message);
      setAutomationIdeaDraft((prev) => ({
        ...prev,
        task: '',
        currentProcess: '',
        desiredOutcome: '',
      }));
      await loadAutomationBoard();
    } catch (error) {
      console.warn('Failed to submit automation request', error);
      setAutomationSubmitMessage(error instanceof Error ? error.message : 'Failed to submit automation request.');
    } finally {
      setAutomationSubmitting(false);
    }
  }, [apiBaseUrl, automationIdeaDraft, loadAutomationBoard]);

  const handleOnboardingSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = onboardingName.trim();
    const team = onboardingTeam.trim();
    const useCase = onboardingUseCase.trim();
    if (!name || !team || !useCase) return;

    setOnboardingSubmitting(true);
    setOnboardingSubmitMessage('');
    try {
      console.log('AI Onboarding intake submitted:', {
        name,
        team,
        manager: onboardingManager.trim(),
        surfaces: Array.from(onboardingSurfaces),
        useCase,
        access: Array.from(onboardingAccess),
        justification: onboardingJustification.trim(),
      });
      setOnboardingSubmitMessage('Onboarding request received. The team will review your request and follow up with next steps.');
      setOnboardingName('');
      setOnboardingTeam('');
      setOnboardingManager('');
      setOnboardingSurfaces(new Set());
      setOnboardingUseCase('');
      setOnboardingAccess(new Set());
      setOnboardingJustification('');
    } catch (error) {
      console.warn('Failed to submit onboarding request', error);
      setOnboardingSubmitMessage(error instanceof Error ? error.message : 'Failed to submit onboarding request.');
    } finally {
      setOnboardingSubmitting(false);
    }
  }, [onboardingName, onboardingTeam, onboardingManager, onboardingSurfaces, onboardingUseCase, onboardingAccess, onboardingJustification]);

  const toggleOnboardingSurface = useCallback((surface: string) => {
    setOnboardingSurfaces((prev) => {
      const next = new Set(prev);
      if (next.has(surface)) next.delete(surface);
      else next.add(surface);
      return next;
    });
  }, []);

  const toggleOnboardingAccess = useCallback((access: string) => {
    setOnboardingAccess((prev) => {
      const next = new Set(prev);
      if (next.has(access)) next.delete(access);
      else next.add(access);
      return next;
    });
  }, []);

  useEffect(() => {
    if (user?.name) {
      setAutomationIdeaDraft((prev) => (prev.name ? prev : { ...prev, name: user.name }));
      setOnboardingName((prev) => prev || user.name);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const loadReadableResources = async () => {
      try {
        const entries = await Promise.all(
          portalReadableResources.map(async (resource) => {
            const response = await fetch(resource.href, { headers: { Accept: 'text/plain' } });
            if (!response.ok) {
              throw new Error(`Failed to load ${resource.title}`);
            }
            const content = await response.text();
            return [resource.id, content] as const;
          }),
        );
        if (!cancelled) {
          setResourceContents(Object.fromEntries(entries));
          setResourceLoadError('');
        }
      } catch (error) {
        console.warn('Failed to load portal resource previews', error);
        if (!cancelled) {
          setResourceLoadError('Some embedded resource previews could not be loaded. The download links still work.');
        }
      }
    };

    void loadReadableResources();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void loadAutomationBoard();
  }, [loadAutomationBoard]);

  useEffect(() => {
    if (activeTab === 'sources' && !schemaCatalog && !schemaCatalogLoading) {
      void loadSchemaCatalog();
    }
  }, [activeTab, loadSchemaCatalog, schemaCatalog, schemaCatalogLoading]);

  /* ── Keyboard nav for tabs ── */
  const handleTabKeyDown = useCallback((e: React.KeyboardEvent) => {
    const idx = tabs.findIndex((t) => t.id === activeTab);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = tabs[(idx + 1) % tabs.length];
      handleTabClick(next.id);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = tabs[(idx - 1 + tabs.length) % tabs.length];
      handleTabClick(prev.id);
    }
  }, [activeTab, handleTabClick]);

  /* ── Deep link: role card -> dashboards tab ── */
  const goToReport = useCallback((reportTitle: string) => {
    setActiveTab('dashboards');
    setSearchQuery('');
    window.location.hash = 'dashboards';
    // Slight delay to let React render the tab, then highlight
    setTimeout(() => {
      const el = document.getElementById(`report-${reportTitle.replace(/\s+/g, '-').toLowerCase()}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('data-guide-highlight-flash');
        setTimeout(() => el.classList.remove('data-guide-highlight-flash'), 1500);
      }
    }, 100);
  }, []);

  const goToSection = useCallback((tab: TabId, sectionId: string) => {
    setActiveTab(tab);
    setSearchQuery('');
    window.location.hash = tab;
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('data-guide-highlight-flash');
        setTimeout(() => el.classList.remove('data-guide-highlight-flash'), 1500);
      }
    }, 100);
  }, []);

  /* ── Search ── */
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const matches = searchIndex.filter((entry) => entry.text.toLowerCase().includes(q));
    const grouped: Record<TabId, SearchEntry[]> = { start: [], sources: [], dashboards: [], ai: [], technical: [] };
    matches.forEach((m) => grouped[m.tab].push(m));
    return grouped;
  }, [searchQuery]);

  const isSearching = searchResults !== null;
  const totalResults = isSearching ? Object.values(searchResults).reduce((n, arr) => n + arr.length, 0) : 0;

  /* ── Superset grouped ── */
  const groupedSuperset = useMemo(() => {
    const map: Record<string, SupersetDashboard[]> = {};
    supersetDashboards.forEach((d) => {
      if (!map[d.group]) map[d.group] = [];
      map[d.group].push(d);
    });
    return supersetGroupOrder.filter((g) => map[g]).map((g) => ({ group: g, dashboards: map[g] }));
  }, []);

  const automationBoardStatus = useMemo(() => {
    if (automationBoardLoading) return 'Syncing with the shared Notion automation board...';
    if (automationBoardSource === 'notion') {
      return automationBoardUpdatedAt
        ? `Live board synced ${new Date(automationBoardUpdatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}.`
        : 'Live board synced from Notion.';
    }
    return 'Showing fallback examples because the live board could not be loaded.';
  }, [automationBoardLoading, automationBoardSource, automationBoardUpdatedAt]);

  const automationCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of automationIdeas) {
      const status = item.automationStatus || 'Not started';
      counts[status] = (counts[status] || 0) + 1;
    }
    return counts;
  }, [automationIdeas]);

  const automationLanes = useMemo(() => {
    const known = automationStatusOrder.map((status) => ({
      status,
      items: automationIdeas.filter((item) => item.automationStatus === status),
    }));
    const extras = Array.from(new Set(automationIdeas.map((item) => item.automationStatus).filter((status) => status && !automationStatusOrder.includes(status))))
      .sort()
      .map((status) => ({
        status,
        items: automationIdeas.filter((item) => item.automationStatus === status),
    }));
    return [...known, ...extras].filter((lane) => lane.items.length > 0 || automationStatusOrder.includes(lane.status));
  }, [automationIdeas]);

  const filteredSchemaTables = useMemo(() => {
    const tables = schemaCatalog?.tables || [];
    const queryTerms = schemaSearchQuery
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);

    return tables.filter((table) => {
      if (schemaDatabaseFilter && table.database !== schemaDatabaseFilter) {
        return false;
      }
      if (schemaSourceFamilyFilter && table.sourceFamily !== schemaSourceFamilyFilter) {
        return false;
      }
      if (!queryTerms.length) {
        return true;
      }
      const haystack = [
        table.database,
        table.table,
        table.sourceFamily,
        table.description,
        ...table.columns.map((column) => `${column.name} ${column.type} ${column.kind}`),
      ].join(' ').toLowerCase();
      return queryTerms.every((term) => haystack.includes(term));
    });
  }, [schemaCatalog, schemaDatabaseFilter, schemaSearchQuery, schemaSourceFamilyFilter]);

  const visibleSchemaTables = useMemo(() => filteredSchemaTables.slice(0, 24), [filteredSchemaTables]);

  const Accordion: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
    <div className={`data-guide-accordion ${openAccordions.has(id) ? 'open' : ''}`}>
      <button className="data-guide-accordion-trigger" onClick={() => toggleAccordion(id)}>
        <span>{title}</span>
        <span className="data-guide-accordion-icon">{openAccordions.has(id) ? '\u2212' : '+'}</span>
      </button>
      {openAccordions.has(id) && (
        <div className="data-guide-accordion-body">{children}</div>
      )}
    </div>
  );

  return (
    <div className={`dashboard data-guide-shell data-guide-shell-${theme}${colorMode === 'dark' ? ' data-guide-dark' : ''}`}>
      {headerSlot ?? (theme === 'portal' ? <Header /> : null)}

      <section className="data-guide-hero">
        <div className="dashboard-container">
          <div className="data-guide-hero-top">
            <div>
              <p className="data-guide-kicker">{heroKicker}</p>
              <h1 className="data-guide-title">{heroTitle}</h1>
              <p className="data-guide-subtitle">{heroSubtitle}</p>
            </div>
            <div className="data-guide-hero-actions">
              <span className="data-guide-last-updated">Last reviewed {PAGE_LAST_REVIEWED}</span>
              <button
                className="data-guide-color-toggle"
                onClick={toggleColorMode}
                aria-label={`Switch to ${colorMode === 'light' ? 'dark' : 'light'} mode`}
                title={`Switch to ${colorMode === 'light' ? 'dark' : 'light'} mode`}
              >
                <span className="data-guide-color-toggle-icon">{colorMode === 'light' ? '\u263E' : '\u2600'}</span>
                {colorMode === 'light' ? 'Dark' : 'Light'}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="data-guide-search-bar">
            <input
              ref={searchInputRef}
              type="text"
              className="data-guide-search-input"
              placeholder="Search across all tabs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="data-guide-search-clear" onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}>
                Clear
              </button>
            )}
          </div>

          {/* Tab bar (sticky) */}
          <nav className="data-guide-tab-bar" ref={tabBarRef} role="tablist" onKeyDown={handleTabKeyDown}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                tabIndex={activeTab === tab.id ? 0 : -1}
                className={`data-guide-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabClick(tab.id)}
              >
                {tab.label}
                {isSearching && searchResults[tab.id].length > 0 && (
                  <span className="data-guide-tab-badge">{searchResults[tab.id].length}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </section>

      <main className="dashboard-main">
        <div className="dashboard-container data-guide-container">

          {/* ── SEARCH RESULTS ── */}
          {isSearching && (
            <section className="data-guide-section">
              <div className="section-header data-guide-section-header">
                <h2>{totalResults} result{totalResults !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;</h2>
              </div>
              {totalResults === 0 && (
                <p className="data-guide-section-copy">No matches found. Try a different search term.</p>
              )}
              {tabs.filter((t) => searchResults[t.id].length > 0).map((t) => (
                <div key={t.id} className="data-guide-search-group">
                  <h3 className="data-guide-search-group-label">{t.label}</h3>
                  <div className="data-guide-search-results">
                    {searchResults[t.id].map((entry, i) => (
                      <button
                        key={`${entry.label}-${i}`}
                        className="data-guide-search-result"
                        onClick={() => { setSearchQuery(''); handleTabClick(t.id); }}
                      >
                        <strong>{entry.label}</strong>
                        <span className="data-guide-search-result-tab">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* ── START HERE ── */}
          {!isSearching && activeTab === 'start' && (
            <>
              <section className="data-guide-section" id="major-source-ecosystem">
                <div className="section-header data-guide-section-header">
                  <h2>Major source ecosystem</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the quickest visual way to understand what feeds the stack. The business event happens in the source systems on the left, AWS is the shared operating layer in the middle, and the team consumes the outputs through Sheets, Superset, Slack, and internal apps.
                </p>
                <div className="data-guide-ecosystem-layout">
                  <article className="data-guide-ecosystem-map">
                    <div className="data-guide-ecosystem-source-grid">
                      {sourceEcosystemGroups.map((group) => (
                        <div key={group.title} className="data-guide-ecosystem-group">
                          <h3>{group.title}</h3>
                          <p>{group.blurb}</p>
                          <div className="data-guide-ecosystem-tags">
                            {group.sources.map((source) => (
                              <span key={source} className="data-guide-ecosystem-tag">{source}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="data-guide-ecosystem-bridge">
                      <div className="data-guide-ecosystem-arrow">&rarr;</div>
                      <div className="data-guide-ecosystem-hub">
                        <strong>AWS data lake + warehouse</strong>
                        <p>S3, Glue, Athena, Lambda, ECS, EventBridge</p>
                      </div>
                      <div className="data-guide-ecosystem-arrow">&rarr;</div>
                    </div>
                    <div className="data-guide-ecosystem-output">
                      <strong>Team-facing outputs</strong>
                      <p>Sheets, Superset, Slack reports, portal apps, Mission Control</p>
                    </div>
                  </article>

                  <article className="data-guide-ecosystem-cloud-card">
                    <h3>Major systems we pull from</h3>
                    <p className="data-guide-ecosystem-copy">
                      Real marks where we have them cleanly; branded wordmark tiles where we do not. This is a stack overview, not an exhaustive partner directory.
                    </p>
                    <div className="data-guide-source-cloud">
                      {sourceMarks.map((mark) => (
                        <button
                          key={mark.label}
                          className={`data-guide-source-mark ${mark.kind.toLowerCase().replace(/\s+/g, '-')}`}
                          style={
                            theme === 'portal'
                              ? { backgroundColor: mark.accent, color: mark.textColor ?? 'var(--primary-black)' }
                              : undefined
                          }
                          onClick={() => goToSection(mark.targetTab, mark.targetSection)}
                        >
                          <span className="data-guide-source-kind">{mark.kind}</span>
                          {mark.image ? (
                            <img src={mark.image} alt={`${mark.label} logo`} className="data-guide-source-logo" />
                          ) : (
                            <span className="data-guide-source-wordmark">{mark.label}</span>
                          )}
                          <span className="data-guide-source-label">{mark.label}</span>
                        </button>
                      ))}
                    </div>
                  </article>
                </div>
              </section>

              <section className="data-guide-section" id="source-chooser">
                <div className="section-header data-guide-section-header">
                  <h2>Start here by role</h2>
                </div>
                <p className="data-guide-section-copy">
                  Find your role and follow the recommended starting point. Each card tells you what to use, where to start, and what to avoid.
                </p>
                <div className="data-guide-role-grid">
                  {roleCards.map((card) => (
                    <article key={card.role} className="data-guide-role-card">
                      <p className="data-guide-role-name">{card.role}</p>
                      <div className="data-guide-role-copy">
                        <p><strong>Use this for:</strong> {card.useFor}</p>
                        <p>
                          <strong>Start with:</strong>{' '}
                          {card.linkedReport ? (
                            <>
                              <button
                                className="data-guide-inline-link"
                                onClick={() => goToReport(card.linkedReport!)}
                              >
                                {card.linkedReport}
                              </button>
                              {card.startWith.includes(',') && (
                                <span> and related surfaces.</span>
                              )}
                            </>
                          ) : (
                            card.startWith
                          )}
                        </p>
                        <p><strong>Avoid:</strong> {card.avoid}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="modeled-sources">
                <div className="section-header data-guide-section-header">
                  <h2>Canonical source by question type</h2>
                </div>
                <div className="data-guide-question-grid">
                  {questionCards.map((card) => (
                    <article key={card.question} className="data-guide-question-card">
                      <p className="data-guide-question">{card.question}</p>
                      <h3>{card.source}</h3>
                      <p>{card.guidance}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="direct-vs-intermediary-section">
                <div className="section-header data-guide-section-header">
                  <h2>Rules that keep us out of trouble</h2>
                </div>
                <div className="data-guide-rule-grid">
                  {ruleCards.map((card) => (
                    <article key={card.title} className="data-guide-rule-card">
                      <h3>{card.title}</h3>
                      <p>{card.body}</p>
                    </article>
                  ))}
                </div>
              </section>

              <Accordion id="overview" title="What all of this means">
                <div className="data-guide-section" style={{ paddingTop: '0.5rem' }}>
                  <div className="data-guide-overview-grid">
                    {overviewCards.map((card) => (
                      <article key={card.title} className="data-guide-overview-card">
                        <span className="data-guide-overview-accent" style={{ backgroundColor: card.accent }} />
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                      </article>
                    ))}
                  </div>
                  <div className="data-guide-flow-grid">
                    <article className="data-guide-flow-step">
                      <span>1</span>
                      <h3>Source systems</h3>
                      <p>Platforms where the event actually happened. Example: Meta spend, Shopify order, Axon spend, KNO survey response.</p>
                    </article>
                    <article className="data-guide-flow-step">
                      <span>2</span>
                      <h3>Data lake</h3>
                      <p>A storage layer in AWS where raw or semi-modeled exports land so we do not depend on manual downloads and one-off spreadsheets.</p>
                    </article>
                    <article className="data-guide-flow-step">
                      <span>3</span>
                      <h3>Warehouse</h3>
                      <p>Queryable Athena and Glue tables built on top of the lake so the team and Claude Code can ask repeatable questions against a shared layer.</p>
                    </article>
                    <article className="data-guide-flow-step">
                      <span>4</span>
                      <h3>Delivery surfaces</h3>
                      <p>Google Sheets, Superset, Slack drops, and internal tools that expose the right answer to the right team without forcing everyone into raw queries.</p>
                    </article>
                  </div>
                </div>
              </Accordion>

              <Accordion id="visual-map" title="Visual map: how data moves and truth layers">
                <div className="data-guide-diagram-grid" style={{ paddingTop: '0.5rem' }}>
                  <article className="data-guide-diagram-card">
                    <h3>How data moves through the stack</h3>
                    <div className="data-guide-diagram-flow">
                      <div className="data-guide-diagram-node">
                        <span className="data-guide-diagram-label">1</span>
                        <strong>Direct sources</strong>
                        <p>Meta, Google Ads, Shopify, Axon, Amazon, Northbeam, KNO</p>
                      </div>
                      <div className="data-guide-diagram-arrow">&rarr;</div>
                      <div className="data-guide-diagram-node">
                        <span className="data-guide-diagram-label">2</span>
                        <strong>Lake + warehouse</strong>
                        <p>S3, Athena, Glue, modeled tables, job outputs</p>
                      </div>
                      <div className="data-guide-diagram-arrow">&rarr;</div>
                      <div className="data-guide-diagram-node">
                        <span className="data-guide-diagram-label">3</span>
                        <strong>Delivery surfaces</strong>
                        <p>Sheets, Superset, Slack jobs, portal apps</p>
                      </div>
                    </div>
                  </article>
                  <article className="data-guide-diagram-card">
                    <h3>How to think about truth layers</h3>
                    <div className="data-guide-truth-stack">
                      <div className="data-guide-truth-band finance">Finance / operational truth</div>
                      <div className="data-guide-truth-band platform">Marketing finance / platform API views</div>
                      <div className="data-guide-truth-band attribution">Modeled attribution / Northbeam parity</div>
                      <div className="data-guide-truth-band exploration">Broad warehouse / exploratory analysis</div>
                    </div>
                    <p className="data-guide-diagram-note">
                      The same business question can exist in multiple layers. The mistake is assuming those layers mean the same thing.
                    </p>
                  </article>
                </div>
              </Accordion>

              <section className="data-guide-section" id="canonical-source-lookup-section">
                <div className="section-header data-guide-section-header">
                  <h2>Operating maps by domain</h2>
                </div>
                <p className="data-guide-section-copy">
                  These diagrams show how the major source families move through AWS and into the team-facing reporting surfaces. Use them when the question is not just &ldquo;what is the source?&rdquo; but &ldquo;how does this data actually move through the stack?&rdquo;
                </p>
                <div className="data-guide-architecture-grid">
                  <article className="data-guide-architecture-card">
                    <h3>Paid media and attribution</h3>
                    <div className="data-guide-architecture-flow">
                      <div className="data-guide-architecture-node">
                        <strong>Direct sources</strong>
                        <p>Meta, Google Ads, Amazon Ads, Axon</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Warehouse families</strong>
                        <p>`growth_report_*` + `northbeam_*`</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Surfaces</strong>
                        <p>Paid Media Dash, Paid Media Dash Daily, Northbeam 1DC Daily/Hourly, Superset</p>
                      </div>
                    </div>
                  </article>

                  <article className="data-guide-architecture-card">
                    <h3>Commerce and retention</h3>
                    <div className="data-guide-architecture-flow">
                      <div className="data-guide-architecture-node">
                        <strong>Direct sources</strong>
                        <p>Shopify, Amazon SP-API, Klaviyo, Postscript, Stay AI, HAM</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Warehouse families</strong>
                        <p>`shopify_orders_enriched`, retention marts, Stay and HAM marts</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Surfaces</strong>
                        <p>Finance views, retention views, executive dashboards, future KNO and incrementality views</p>
                      </div>
                    </div>
                  </article>

                  <article className="data-guide-architecture-card">
                    <h3>CX and operations</h3>
                    <div className="data-guide-architecture-flow">
                      <div className="data-guide-architecture-node">
                        <strong>Direct sources</strong>
                        <p>Gorgias, TrackingMore, ShipHero, Shopify webhooks</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Warehouse and apps</strong>
                        <p>Materialized CX tables, tracking status tables, Mission Control datasets</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Surfaces</strong>
                        <p>Mission Control, Fulfillment dashboards, problem-order and stalled-order Slack reports</p>
                      </div>
                    </div>
                  </article>

                  <article className="data-guide-architecture-card">
                    <h3>Finance and banking</h3>
                    <div className="data-guide-architecture-flow">
                      <div className="data-guide-architecture-node">
                        <strong>Direct sources</strong>
                        <p>Bill.com, Teller, Shopify payments</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Warehouse families</strong>
                        <p>Bill.com marts, Teller daily metrics, Shopify payment and dispute tables</p>
                      </div>
                      <div className="data-guide-architecture-arrow">&rarr;</div>
                      <div className="data-guide-architecture-node">
                        <strong>Surfaces</strong>
                        <p>Finance Daily Dashboard, payables dashboards, executive reporting</p>
                      </div>
                    </div>
                  </article>
                </div>
              </section>
            </>
          )}

          {/* ── SOURCES & TRUTH ── */}
          {!isSearching && activeTab === 'sources' && (
            <>
              <section className="data-guide-section" id="detailed-source-inventory-section">
                <div className="section-header data-guide-section-header">
                  <h2>Source families at a glance</h2>
                </div>
                <p className="data-guide-section-copy">
                  Start here if you are trying to figure out which part of the stack a question belongs to. Each card groups related systems and the common reporting layer people usually touch after the raw platform itself.
                </p>
                <div className="data-guide-family-grid">
                  {sourceFamilyCards.map((card) => (
                    <article key={card.title} className="data-guide-family-card">
                      <h3>{card.title}</h3>
                      <div className="data-guide-card-block">
                        <span className="data-guide-card-label">Systems</span>
                        <ul className="data-guide-tag-list">
                          {card.systems.map((system) => (
                            <li key={system}>{system}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="data-guide-card-block">
                        <span className="data-guide-card-label">Usually lands in</span>
                        <ul className="data-guide-tag-list">
                          {card.warehouse.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="data-guide-card-block">
                        <span className="data-guide-card-label">Best for</span>
                        <p>{card.useFor}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="known-caveats-section">
                <div className="section-header data-guide-section-header">
                  <h2>When to use direct data versus modeled or aggregated data</h2>
                </div>
                <p className="data-guide-section-copy">
                  The easiest way to think about this is speed versus interpretation. Direct sources are usually the freshest and closest to what actually happened. Intermediary tools like Triple Whale and Northbeam are useful because they aggregate, normalize, or assign credit, but that comes with modeling and usually slower freshness.
                </p>
                <p className="data-guide-section-copy">
                  This section is a chooser, not the full source catalog. It groups the major truth layers and source families people actually decide between. The full inventory is further down on this tab.
                </p>
                <div className="data-guide-callout">
                  <h3>Simple rule</h3>
                  <p>
                    If the question is <strong>what happened right now</strong>, go direct to the source. If the question is <strong>who gets credit</strong> or <strong>how do channels compare in a modeled view</strong>, use the attribution or aggregation layer intentionally.
                  </p>
                </div>
                <div className="data-guide-choice-grid">
                  {dataPathCards.map((card) => (
                    <article key={card.title} className="data-guide-choice-card">
                      <h3>{card.title}</h3>
                      <ul className="data-guide-tag-list">
                        {card.examples.map((example) => (
                          <li key={example}>{example}</li>
                        ))}
                      </ul>
                      <div className="data-guide-card-block">
                        <span className="data-guide-card-label">Best for</span>
                        <p>{card.bestFor}</p>
                      </div>
                      <div className="data-guide-card-block">
                        <span className="data-guide-card-label">Tradeoff</span>
                        <p>{card.tradeoff}</p>
                      </div>
                      <div className="data-guide-card-block">
                        <span className="data-guide-card-label">Not ideal for</span>
                        <p>{card.notIdealFor}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="growth-reporting-map-section">
                <div className="section-header data-guide-section-header">
                  <h2>What is actually inside the aggregated and modeled sources</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the missing piece that trips people up. Triple Whale and Northbeam are not just alternate APIs for the same platform truth. They are modeling or aggregation layers with their own outputs and their own refresh cadence. If someone is deciding whether a source is &ldquo;fresh enough&rdquo; or &ldquo;close enough to platform truth,&rdquo; start here.
                </p>
                <div className="data-guide-profile-grid">
                  {aggregatedSourceProfiles.map((profile) => (
                    <article key={profile.title} className="data-guide-profile-card">
                      <div className="data-guide-profile-topline">
                        <span className="data-guide-report-category">{profile.sourceType}</span>
                      </div>
                      <h3>{profile.title}</h3>
                      <p><strong>Contains:</strong> {profile.contains}</p>
                      <p><strong>Best for:</strong> {profile.bestFor}</p>
                      <p><strong>Freshness / update pattern:</strong> {profile.freshness}</p>
                      <p><strong>Not for:</strong> {profile.notFor}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="current-report-surfaces-section">
                <div className="section-header data-guide-section-header">
                  <h2>Direct vs intermediary source paths</h2>
                </div>
                <p className="data-guide-section-copy">
                  Multiple systems can answer the same business question, but they do not always mean the same thing. Direct sources are preferred when the question is platform or operational truth.
                  Intermediary sources are useful when the question is modeled attribution, interpretation, or mix analysis.
                </p>
                <Accordion id="source-table" title="View full source comparison table">
                  <div className="data-guide-table-wrap">
                    <table className="data-guide-table">
                      <thead>
                        <tr>
                          <th>Source family</th>
                          <th>Direct source</th>
                          <th>Intermediary / modeled source</th>
                          <th>Freshness / update pattern</th>
                          <th>Intraday trust</th>
                          <th>Canonical use</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceRows.map((row) => (
                          <tr key={row.source}>
                            <td>{row.source}</td>
                            <td>{row.direct}</td>
                            <td>{row.intermediary}</td>
                            <td>{row.freshness}</td>
                            <td>
                              <span className={`data-guide-trust-badge ${row.intradayTrust.toLowerCase()}`}>
                                {row.intradayTrust}
                              </span>
                            </td>
                            <td>{row.canonicalUse}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Accordion>
              </section>

              <section className="data-guide-section" id="superset-dashboards-section">
                <div className="section-header data-guide-section-header">
                  <h2>Source stewards and canonical warehouse tables</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the operating lookup table for the team. If someone asks &ldquo;where should I start for this source?&rdquo; this is the fastest answer. Direct APIs are still the source of truth where noted, but these are the warehouse tables or views most people should open first when they need a reusable analytics surface.
                </p>
                <Accordion id="canonical-source-table" title={`View canonical source lookup (${canonicalSourceRows.length} source families)`}>
                  <div className="data-guide-table-wrap">
                    <table className="data-guide-table">
                      <thead>
                        <tr>
                          <th>Source system</th>
                          <th>Business steward</th>
                          <th>Canonical warehouse table(s)</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {canonicalSourceRows.map((row) => (
                          <tr key={row.sourceSystem}>
                            <td>{row.sourceSystem}</td>
                            <td>{row.steward}</td>
                            <td>
                              <ul className="data-guide-inline-list">
                                {row.canonicalTables.map((table) => (
                                  <li key={table}>{table}</li>
                                ))}
                              </ul>
                            </td>
                            <td>{row.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Accordion>
              </section>

              <section className="data-guide-section" id="slack-reports-section">
                <div className="section-header data-guide-section-header">
                  <h2>Detailed source inventory</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the fuller source catalog behind the shorter truth-layer guidance above. It is useful when someone needs to trace a source family end to end, understand what data it produces, or figure out whether the source is active, planned, or only partially landed today.
                </p>
                <Accordion id="detailed-source-inventory" title={`View detailed source inventory (${detailedSourceRows.length} sources)`}>
                  <div className="data-guide-table-wrap">
                    <table className="data-guide-table">
                      <thead>
                        <tr>
                          <th>Source system</th>
                          <th>Category</th>
                          <th>Primary data</th>
                          <th>Implementation surface</th>
                          <th>Known update cadence</th>
                          <th>Status / notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedSourceRows.map((row) => (
                          <tr key={row.sourceSystem}>
                            <td>{row.sourceSystem}</td>
                            <td>{row.category}</td>
                            <td>{row.primaryData}</td>
                            <td>{row.implementationSurface}</td>
                            <td>{row.cadence}</td>
                            <td>{row.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Accordion>
              </section>

              <section className="data-guide-section" id="schema-explorer-section">
                <div className="section-header data-guide-section-header">
                  <h2>Schema explorer and AI field search</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the exact warehouse property explorer for the current Glue catalog. Use it when someone needs to know which tables and fields actually exist, then use the AI search to explain the closest matches in plain English without dumping the entire warehouse into the model context.
                </p>
                <div className="data-guide-callout">
                  <h3>How this works</h3>
                  <p>
                    The explorer loads the exact Glue catalog for <code>marsmen_analytics_production</code>, <code>growth_reporting</code>, and <code>marsmen_raw_production</code>. The AI search does not see the whole warehouse. It only gets the top-matching tables and columns after the exact catalog has already narrowed the search space.
                  </p>
                </div>
                <div className="data-guide-stat-row">
                  <article className="data-guide-stat-card">
                    <span className="data-guide-stat-value">{schemaCatalog?.summary.tableCount ?? '—'}</span>
                    <span className="data-guide-stat-label">Tables indexed</span>
                  </article>
                  <article className="data-guide-stat-card">
                    <span className="data-guide-stat-value">{schemaCatalog?.summary.columnCount ?? '—'}</span>
                    <span className="data-guide-stat-label">Columns indexed</span>
                  </article>
                  <article className="data-guide-stat-card">
                    <span className="data-guide-stat-value">{filteredSchemaTables.length}</span>
                    <span className="data-guide-stat-label">Filtered results</span>
                  </article>
                </div>
                <div className="data-guide-idea-form-grid">
                  <label className="data-guide-idea-field">
                    <span>Search fields or tables</span>
                    <input
                      type="text"
                      value={schemaSearchQuery}
                      onChange={(event) => setSchemaSearchQuery(event.target.value)}
                      placeholder="Example: first order revenue, country, gorgias, spend"
                    />
                  </label>
                  <label className="data-guide-idea-field">
                    <span>Database</span>
                    <select
                      value={schemaDatabaseFilter}
                      onChange={(event) => setSchemaDatabaseFilter(event.target.value)}
                    >
                      <option value="">All databases</option>
                      {(schemaCatalog?.filters.databases || []).map((database) => (
                        <option key={database} value={database}>{database}</option>
                      ))}
                    </select>
                  </label>
                  <label className="data-guide-idea-field">
                    <span>Source family</span>
                    <select
                      value={schemaSourceFamilyFilter}
                      onChange={(event) => setSchemaSourceFamilyFilter(event.target.value)}
                    >
                      <option value="">All source families</option>
                      {(schemaCatalog?.filters.sourceFamilies || []).map((sourceFamily) => (
                        <option key={sourceFamily} value={sourceFamily}>{sourceFamily}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="data-guide-resource-meta">
                  <button
                    type="button"
                    className="data-guide-link"
                    onClick={() => void loadSchemaCatalog(true)}
                    disabled={schemaCatalogLoading}
                  >
                    {schemaCatalogLoading ? 'Refreshing catalog...' : 'Refresh schema catalog'}
                  </button>
                  <span className="data-guide-resource-usage">
                    <strong>Catalog generated:</strong>{' '}
                    {schemaCatalog?.generatedAt
                      ? new Date(schemaCatalog.generatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                      : 'Not loaded yet'}
                  </span>
                </div>
                {schemaCatalogError && (
                  <div className="data-guide-callout">
                    <h3>Schema catalog note</h3>
                    <p>{schemaCatalogError}</p>
                  </div>
                )}
                <form className="data-guide-callout" onSubmit={handleSchemaAskSubmit}>
                  <h3>Ask the schema assistant</h3>
                  <p className="data-guide-section-copy">
                    Ask in plain English. The assistant will search the exact catalog first, then Bedrock will explain the top schema matches.
                  </p>
                  <label className="data-guide-idea-field">
                    <span>Question</span>
                    <textarea
                      value={schemaAskQuestion}
                      onChange={(event) => setSchemaAskQuestion(event.target.value)}
                      placeholder="Example: Which tables and fields should I use for first-order revenue and order count?"
                      rows={3}
                    />
                  </label>
                  <button type="submit" className="data-guide-idea-submit" disabled={schemaAskLoading}>
                    {schemaAskLoading ? 'Searching the schema...' : 'Ask the schema assistant'}
                  </button>
                </form>
                {schemaAskError && (
                  <div className="data-guide-callout">
                    <h3>Schema assistant note</h3>
                    <p>{schemaAskError}</p>
                  </div>
                )}
                {schemaAskResponse && (
                  <div className="data-guide-link-sections">
                    <section className="data-guide-link-section">
                      <div className="data-guide-link-section-header">
                        <h3>AI answer</h3>
                        <p>The model answer is grounded in the top exact schema matches returned by the catalog.</p>
                      </div>
                      <div className="data-guide-callout">
                        <h3>{schemaAskResponse.confidence.toUpperCase()} confidence</h3>
                        <p>{schemaAskResponse.answer}</p>
                        {!!schemaAskResponse.caveats.length && (
                          <ul className="data-guide-resource-list">
                            {schemaAskResponse.caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}
                          </ul>
                        )}
                      </div>
                    </section>
                    <section className="data-guide-link-section">
                      <div className="data-guide-link-section-header">
                        <h3>Recommended tables and fields</h3>
                        <p>These are the exact matches Bedrock was allowed to use.</p>
                      </div>
                      <div className="data-guide-links-grid">
                        {schemaAskResponse.matchedTables.slice(0, 6).map((table) => (
                          <article key={table.id} className="data-guide-link-card">
                            <div className="data-guide-resource-meta">
                              <span className="data-guide-automation-pill">{table.sourceFamily}</span>
                              <span className="data-guide-automation-pill">{table.columnCount} columns</span>
                            </div>
                            <h3>{table.id}</h3>
                            <p>Closest schema match with score {table.score}.</p>
                          </article>
                        ))}
                      </div>
                      {!!schemaAskResponse.recommendedColumns.length && (
                        <div className="data-guide-schema-column-list">
                          {schemaAskResponse.recommendedColumns.map((column) => (
                            <div key={`${column.tableId}-${column.column}`} className="data-guide-schema-column-pill">
                              <strong>{column.column}</strong>
                              <span>{column.type}</span>
                              <span>{column.tableId}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                )}
                <div className="data-guide-resource-meta">
                  <span className="data-guide-resource-usage">
                    <strong>Showing:</strong> {visibleSchemaTables.length} of {filteredSchemaTables.length} matching tables
                  </span>
                </div>
                <div className="data-guide-links-grid">
                  {visibleSchemaTables.map((table) => (
                    <article key={table.id} className="data-guide-link-card data-guide-schema-table-card">
                      <div className="data-guide-resource-meta">
                        <span className="data-guide-automation-pill">{table.database}</span>
                        <span className="data-guide-automation-pill">{table.sourceFamily}</span>
                        <span className="data-guide-automation-pill">{table.columnCount + table.partitionCount} fields</span>
                      </div>
                      <h3>{table.table}</h3>
                      <p>{table.description || 'No table description is present in Glue yet.'}</p>
                      <p className="data-guide-resource-usage">
                        <strong>Storage:</strong> {table.tableType}{table.classification ? ` • ${table.classification}` : ''}
                      </p>
                      <Accordion id={`schema-${table.id}`} title={`View ${table.columnCount + table.partitionCount} fields`}>
                        <div className="data-guide-schema-column-list">
                          {table.columns.map((column) => (
                            <div key={`${table.id}-${column.kind}-${column.name}`} className="data-guide-schema-column-pill">
                              <strong>{column.name}</strong>
                              <span>{column.type}</span>
                              <span>{column.kind}</span>
                            </div>
                          ))}
                        </div>
                      </Accordion>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="technical-secrets-manager-families">
                <div className="section-header data-guide-section-header">
                  <h2>Known caveats and active gaps</h2>
                </div>
                <p className="data-guide-section-copy">
                  Read this section after you know which source family you are in. These caveats are here to prevent false confidence, not to act as the entry point to the tab.
                </p>
                <div className="data-guide-rule-grid">
                  {caveatCards.map((card) => (
                    <article key={card.title} className="data-guide-rule-card">
                      <div className="data-guide-report-topline">
                        <span className="data-guide-report-category">Caveat</span>
                        <span className={`data-guide-caveat-badge ${card.severity.toLowerCase().replace(/\s+/g, '-')}`}>
                          {card.severity}
                        </span>
                      </div>
                      <h3>{card.title}</h3>
                      <p>{card.body}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Growth reporting map</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the current high-level map of the growth reporting surfaces the team is building and maintaining. It is useful when someone needs to understand how finance, marketing finance, attribution, incrementality, and real-time monitoring fit together.
                </p>
                <figure className="data-guide-figure-card">
                  <img className="data-guide-figure" src={growthReportingMap} alt="Growth reporting map showing finance, paid media, Northbeam daily and hourly attribution, creative optimization, Haus roadmap, and KNO survey data surfaces." />
                  <figcaption className="data-guide-figure-caption">
                    Current growth-reporting map used to explain why each reporting surface exists and how the main finance, marketing finance, attribution, incrementality, and real-time views connect.
                  </figcaption>
                </figure>
              </section>
            </>
          )}

          {/* ── DASHBOARDS & REPORTS ── */}
          {!isSearching && activeTab === 'dashboards' && (
            <>
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Current report surfaces</h2>
                </div>
                <p className="data-guide-section-copy">
                  Owners below are the best current read and the status date shows when that status was last explicitly reviewed on this page.
                </p>
                <div className="data-guide-filter-bar">
                  {(['Live', 'In progress', 'Needs QA'] as const).map((status) => (
                    <button
                      key={status}
                      className={`data-guide-filter-toggle ${statusFilters.has(status) ? 'active' : ''}`}
                      onClick={() => toggleStatusFilter(status)}
                    >
                      <span className={`data-guide-status-dot ${status.toLowerCase().replace(/\s+/g, '-')}`} />
                      {status}
                      <span className="data-guide-filter-count">
                        {reportCards.filter((r) => r.status === status).length}
                      </span>
                    </button>
                  ))}
                  {statusFilters.size > 0 && (
                    <button className="data-guide-filter-clear" onClick={() => setStatusFilters(new Set())}>
                      Clear filters
                    </button>
                  )}
                </div>
                <div className="data-guide-report-grid">
                  {filteredReports.map((report) => (
                    <article
                      key={report.title}
                      id={`report-${report.title.replace(/\s+/g, '-').toLowerCase()}`}
                      className="data-guide-report-card"
                    >
                      <div className="data-guide-report-topline">
                        <div className="data-guide-report-badges">
                          <span className={`data-guide-platform-badge ${report.platform.toLowerCase().replace(/\s+/g, '-')}`}>{report.platform}</span>
                          <span className={`data-guide-status-badge ${report.status.toLowerCase().replace(/\s+/g, '-')}`}>{report.status}</span>
                        </div>
                        <span className="data-guide-report-category">{report.category}</span>
                      </div>
                      <h3>{report.title}</h3>
                      <p>{report.purpose}</p>
                      <div className="data-guide-report-meta">
                        <span>{report.owner}</span>
                      </div>
                      {report.url ? (
                        <a className="data-guide-link" href={report.url} target="_blank" rel="noopener noreferrer">
                          Open in {report.platform}
                        </a>
                      ) : (
                        <span className="data-guide-report-no-link">{report.status === 'In progress' ? 'Still being built' : 'Link not added yet'}</span>
                      )}
                    </article>
                  ))}
                  {filteredReports.length === 0 && (
                    <p className="empty-state">No reports match the selected filters.</p>
                  )}
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Superset dashboards</h2>
                </div>
                <p className="data-guide-section-copy">
                  This is the current dashboard inventory surfaced from the reporting catalog. Use these when the question is dashboard consumption or self-serve operational analysis, not when you need to redefine source truth.
                </p>
                <Accordion id="superset-inventory" title={`All Superset dashboards (${supersetDashboards.length})`}>
                  {groupedSuperset.map(({ group, dashboards }) => (
                    <div key={group} className="data-guide-superset-group">
                      <h4 className="data-guide-superset-group-label">{group}</h4>
                      <div className="data-guide-superset-grid">
                        {dashboards.map((dashboard) => (
                          <article key={dashboard.title} className="data-guide-superset-card">
                            <div className="data-guide-superset-topline">
                              <span className="data-guide-report-category">{group}</span>
                              <span className={`data-guide-sensitivity-badge ${dashboard.sensitive ? 'sensitive' : 'standard'}`}>
                                {dashboard.sensitive ? 'Sensitive' : 'Standard'}
                              </span>
                            </div>
                            <h3>{dashboard.title}</h3>
                            <div className="data-guide-superset-meta">
                              <span><strong>Owner:</strong> {dashboard.owner}</span>
                              {dashboard.slug && <span><strong>Slug:</strong> {dashboard.slug}</span>}
                            </div>
                            <a className="data-guide-link" href={dashboard.url} target="_blank" rel="noreferrer">
                              Open dashboard
                            </a>
                          </article>
                        ))}
                      </div>
                    </div>
                  ))}
                </Accordion>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Slack reports and recurring drops</h2>
                </div>
                <p className="data-guide-section-copy">
                  These are reporting outputs that land in Slack instead of a dashboard URL. They are still part of the reporting surface area and are often the fastest way teams actually consume recurring operational or growth reporting.
                </p>
                <div className="data-guide-report-grid">
                  {slackSurfaces.map((surface) => (
                    <article key={surface.surface} className="data-guide-report-card">
                      <div className="data-guide-report-topline">
                        <span className="data-guide-report-category">{surface.domain}</span>
                        <div className="data-guide-report-badges">
                          <span className="data-guide-platform-badge slack">Slack</span>
                        </div>
                      </div>
                      <h3>{surface.surface}</h3>
                      <p>{surface.purpose}</p>
                      <div className="data-guide-report-meta">
                        <span><strong>Cadence:</strong> {surface.cadence}</span>
                      </div>
                      <span className="data-guide-report-no-link">Delivered in Slack rather than a dashboard URL</span>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section" id="public-reporting-docs-library">
                <div className="section-header data-guide-section-header">
                  <h2>Read the working docs on this page</h2>
                </div>
                <p className="data-guide-section-copy">
                  Most teammates should not need GitHub access just to understand the current reporting plans, trackers, and operating decisions. These portal editions are the readable versions of the private working docs that sit behind the reporting rebuild.
                </p>
                <div className="data-guide-callout">
                  <h3>Why these are here</h3>
                  <p>
                    The repo copies are still useful for engineering and implementation detail, but the versions below are the ones meant for operators, leadership, and anyone who needs the context without a codebase login.
                  </p>
                </div>
                {resourceLoadError && (
                  <div className="data-guide-callout">
                    <h3>Resource preview note</h3>
                    <p>{resourceLoadError}</p>
                  </div>
                )}
                <div className="data-guide-link-sections">
                  {operationalOnPageResources.map((resource) => (
                    <section key={resource.id} id={`resource-${resource.id}`} className="data-guide-link-section">
                      <div className="data-guide-link-section-header">
                        <h3>{resource.title}</h3>
                        <p>{resource.description}</p>
                      </div>
                      <div className="data-guide-resource-meta">
                        <span className="data-guide-automation-pill">{resource.category}</span>
                        <span className="data-guide-automation-pill">{resource.audience}</span>
                        <a href={resource.href} className="data-guide-link" target="_blank" rel="noopener noreferrer">
                          Open public copy
                        </a>
                        <a href={resource.href} download className="data-guide-link">
                          Download
                        </a>
                        <button
                          type="button"
                          className="data-guide-link"
                          onClick={async () => {
                            const content = resourceContents[resource.id];
                            if (content) {
                              await navigator.clipboard.writeText(content);
                            } else {
                              try {
                                const response = await fetch(resource.href);
                                const text = await response.text();
                                await navigator.clipboard.writeText(text);
                              } catch { /* clipboard write failed */ }
                            }
                            const button = document.activeElement as HTMLButtonElement;
                            const original = button.textContent;
                            button.textContent = 'Copied!';
                            setTimeout(() => { button.textContent = original; }, 2000);
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <p className="data-guide-resource-usage">
                        <strong>How to use:</strong> Open the document to read it in your browser, download it to keep a local copy, or copy the full text to paste into Slack, Notion, or a Claude conversation for context.
                      </p>
                      {resourceContents[resource.id] ? (
                        <Accordion id={`reporting-resource-${resource.id}`} title={`Open ${resource.title}`}>
                          <div className="data-guide-resource-preview">
                            {renderResourcePreview(resourceContents[resource.id])}
                          </div>
                        </Accordion>
                      ) : (
                        <div className="data-guide-callout">
                          <h3>Preview loading</h3>
                          <p>The on-page version is still loading. You can still use the public copy above.</p>
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Key links</h2>
                </div>
                <div className="data-guide-link-sections">
                  {linkSections.map((section) => (
                    <section key={section.title} className="data-guide-link-section">
                      <div className="data-guide-link-section-header">
                        <h3>{section.title}</h3>
                        <p>{section.description}</p>
                      </div>
                      <div className="data-guide-links-grid">
                        {section.links.map((card) => (
                          <article key={card.title} className="data-guide-link-card">
                            <h3>{card.title}</h3>
                            <p>{card.description}</p>
                            {card.tab && card.sectionId ? (
                              <button className="data-guide-inline-link" onClick={() => goToSection(card.tab!, card.sectionId!)}>
                                {card.label}
                              </button>
                            ) : (
                              <a href={card.url} target="_blank" rel="noopener noreferrer" className="data-guide-link">
                                {card.label}
                              </a>
                            )}
                          </article>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* ── AI CENTER OF EXCELLENCE ── */}
          {!isSearching && activeTab === 'ai' && (
            <>
              {theme === 'marsmen' && (
                <section className="data-guide-section">
                  <div className="data-guide-callout">
                    <h3>Mars Men now has a dedicated AI Center</h3>
                    <p>
                      The standalone AI Center is now the canonical home for Mars Men setup guides, department playbooks,
                      builder patterns, downloads, permissions guidance, and secure development practices. Use this tab as the quick
                      summary inside the Data Platform, then move to the dedicated AI Center for the full operating playbook.
                    </p>
                    <div className="data-guide-tool-card__actions">
                      {aiCenterNavigationHandlers?.openHome ? (
                        <button type="button" className="data-guide-link" onClick={aiCenterNavigationHandlers.openHome}>
                          Open AI Center
                        </button>
                      ) : (
                        <a href="/ai-center" className="data-guide-link">
                          Open AI Center
                        </a>
                      )}
                      {aiCenterNavigationHandlers?.openDepartments ? (
                        <button type="button" className="data-guide-link" onClick={aiCenterNavigationHandlers.openDepartments}>
                          Open department playbooks
                        </button>
                      ) : (
                        <a href="/ai-center/departments" className="data-guide-link">
                          Open department playbooks
                        </a>
                      )}
                      {aiCenterNavigationHandlers?.openBuilderPatterns ? (
                        <button type="button" className="data-guide-link" onClick={aiCenterNavigationHandlers.openBuilderPatterns}>
                          Open builder patterns
                        </button>
                      ) : (
                        <a href="/ai-center/guides/builder-patterns" className="data-guide-link">
                          Open builder patterns
                        </a>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* ── Section 1: How we think about AI ── */}
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>How we think about AI</h2>
                </div>
                <p className="data-guide-section-copy">
                  These are the four principles that guide how Mars Men uses AI across analysis, reporting, operations, and creative work. The goal is to help non-technical and technical teammates make good tool choices without turning AI into a mystery box or a fake source of truth.
                </p>
                <div className="data-guide-aws-grid">
                  {aiPillars.map((pillar) => (
                    <article key={pillar.title} className="data-guide-aws-card">
                      <h3>{pillar.title}</h3>
                      <p>{pillar.body}</p>
                    </article>
                  ))}
                </div>
              </section>

              {/* ── Section 2: Pick your tool ── */}
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Pick your tool</h2>
                </div>
                <p className="data-guide-section-copy">
                  Different AI surfaces solve different kinds of work. The question is not which model is smartest. It is which surface fits the work you are trying to do.
                </p>
                <div className="data-guide-callout">
                  <h3>Quick decision helper</h3>
                  <p>
                    Just need to write or think? <strong>Claude</strong>.
                    Need dashboards and Slack? <strong>CoWork</strong>.
                    Need code and AWS? <strong>Claude Code</strong>.
                    Building an integration? <strong>Bedrock</strong>.
                  </p>
                </div>
                <div className="data-guide-tool-grid">
                  {unifiedToolCards.map((tool) => (
                    <article key={tool.id} className="data-guide-tool-card">
                      <div className="data-guide-tool-card__header">
                        <h3>{tool.title}</h3>
                        <span className={`data-guide-tool-card__tag ${tool.tagline === 'Everyone has access' ? 'data-guide-tool-card__tag--available' : ''}`}>
                          {tool.tagline}
                        </span>
                      </div>
                      <p className="data-guide-tool-card__description">{tool.plainEnglish}</p>
                      <div className="data-guide-tool-card__field">
                        <span className="data-guide-tool-card__label">Best for</span>
                        <p>{tool.bestFor}</p>
                      </div>
                      <div className="data-guide-tool-card__access">
                        <p>{tool.accessNote}</p>
                      </div>
                      {tool.howToStart && (
                        <div className="data-guide-tool-card__field">
                          <span className="data-guide-tool-card__label">Setup</span>
                          <p>{tool.howToStart}</p>
                        </div>
                      )}
                      <div className="data-guide-tool-card__actions">
                        {tool.launchUrl ? (
                          <a href={tool.launchUrl} className="data-guide-link" target="_blank" rel="noopener noreferrer">
                            {tool.launchLabel}
                          </a>
                        ) : (
                          <button
                            type="button"
                            className="data-guide-link"
                            onClick={() => {
                              const matchingLaunchCard = aiLaunchCards.find((c) => c.id === tool.id);
                              if (matchingLaunchCard) void handleAiLaunch(matchingLaunchCard);
                            }}
                          >
                            {tool.launchLabel}
                          </button>
                        )}
                      </div>
                      <Accordion id={`tool-${tool.id}`} title="Technical details">
                        <div className="data-guide-tool-card__field">
                          <span className="data-guide-tool-card__label">Connected systems</span>
                          <p>{tool.connectedSystems}</p>
                        </div>
                        <div className="data-guide-tool-card__field">
                          <span className="data-guide-tool-card__label">Default package</span>
                          <p>{tool.defaultPackage}</p>
                        </div>
                        <div className="data-guide-tool-card__field">
                          <span className="data-guide-tool-card__label">Avoid for</span>
                          <p>{tool.avoidFor}</p>
                        </div>
                      </Accordion>
                    </article>
                  ))}
                </div>
              </section>

              {/* ── Section 3: Get started ── */}
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Get started</h2>
                </div>
                <p className="data-guide-section-copy">
                  Not everyone needs the same package. The cleanest operating model is to onboard people into the smallest AI surface that still lets them do the work, then expand access only when the workflow clearly requires it.
                </p>
                <div className="data-guide-onboarding-grid">
                  {aiOnboardingTracks.map((track, index) => (
                    <article key={track.title} className="data-guide-onboarding-card">
                      <div className="data-guide-onboarding-card__header">
                        <span className="data-guide-onboarding-card__number">Track {index + 1}</span>
                        <h3>{track.title}</h3>
                      </div>
                      <p className="data-guide-onboarding-card__audience">{track.audience}</p>
                      <div className="data-guide-onboarding-card__steps">
                        <span className="data-guide-onboarding-card__label">Getting started</span>
                        <ol>
                          {track.steps.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                      </div>
                      <div className="data-guide-onboarding-card__outcome">
                        <span className="data-guide-onboarding-card__label">Outcome</span>
                        <p>{track.outcome}</p>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="data-guide-onboarding-form-wrapper">
                  <div className="data-guide-onboarding-form-header">
                    <h3>Request AI access</h3>
                    <p>Use this form to request AI access for yourself or a teammate. The request will be reviewed and followed up on by the team.</p>
                  </div>
                  {onboardingSubmitMessage && (
                    <div className="data-guide-callout">
                      <h3>Submission update</h3>
                      <p>{onboardingSubmitMessage}</p>
                    </div>
                  )}
                  <form className="data-guide-onboarding-form" onSubmit={handleOnboardingSubmit}>
                    <div className="data-guide-onboarding-form__row">
                      <label className="data-guide-onboarding-form__field">
                        <span>Name</span>
                        <input
                          type="text"
                          value={onboardingName}
                          onChange={(e) => setOnboardingName(e.target.value)}
                          placeholder="Your name"
                        />
                      </label>
                      <label className="data-guide-onboarding-form__field">
                        <span>Team</span>
                        <input
                          type="text"
                          value={onboardingTeam}
                          onChange={(e) => setOnboardingTeam(e.target.value)}
                          placeholder="Your team or department"
                        />
                      </label>
                      <label className="data-guide-onboarding-form__field">
                        <span>Manager</span>
                        <input
                          type="text"
                          value={onboardingManager}
                          onChange={(e) => setOnboardingManager(e.target.value)}
                          placeholder="Optional"
                        />
                      </label>
                    </div>
                    <fieldset className="data-guide-onboarding-form__fieldset">
                      <legend>What AI surfaces do you need?</legend>
                      <div className="data-guide-onboarding-form__checkboxes">
                        {onboardingSurfaceOptions.map((surface) => (
                          <label key={surface} className="data-guide-onboarding-form__checkbox">
                            <input
                              type="checkbox"
                              checked={onboardingSurfaces.has(surface)}
                              onChange={() => toggleOnboardingSurface(surface)}
                            />
                            <span>{surface}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <label className="data-guide-onboarding-form__field">
                      <span>What do you plan to use AI for?</span>
                      <textarea
                        value={onboardingUseCase}
                        onChange={(e) => setOnboardingUseCase(e.target.value)}
                        placeholder="Describe your use case — e.g., summarizing meeting notes, querying Athena, building automations..."
                        rows={3}
                      />
                    </label>
                    <fieldset className="data-guide-onboarding-form__fieldset">
                      <legend>Do you need any additional access?</legend>
                      <p className="data-guide-onboarding-form__hint">Only check these if your use case requires them. Most people only need the default.</p>
                      <div className="data-guide-onboarding-form__checkboxes">
                        {onboardingAccessOptions.map((access) => (
                          <label key={access} className="data-guide-onboarding-form__checkbox">
                            <input
                              type="checkbox"
                              checked={onboardingAccess.has(access)}
                              onChange={() => toggleOnboardingAccess(access)}
                            />
                            <span>{access}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {onboardingAccess.size > 0 && (
                      <label className="data-guide-onboarding-form__field">
                        <span>Why do you need access beyond the default?</span>
                        <textarea
                          value={onboardingJustification}
                          onChange={(e) => setOnboardingJustification(e.target.value)}
                          placeholder="Briefly explain what workflow requires this access."
                          rows={2}
                        />
                      </label>
                    )}
                    <button type="submit" className="data-guide-onboarding-form__submit" disabled={onboardingSubmitting}>
                      {onboardingSubmitting ? 'Submitting...' : 'Submit request'}
                    </button>
                  </form>
                </div>
              </section>

              {/* ── Section 4: Request an automation ── */}
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Request an automation</h2>
                </div>
                <p className="data-guide-section-copy">
                  If a task feels manual, repetitive, or annoying, describe it here and we will figure out if it can be automated.
                </p>

                <div className="section-header data-guide-section-header" style={{ marginTop: '1rem' }}>
                  <h3>Examples of what we can automate</h3>
                </div>
                <div className="data-guide-claude-grid">
                  {aiWorkflowCards.map((workflow) => (
                    <article key={workflow.title} className="data-guide-claude-step">
                      <h3>{workflow.title}</h3>
                      <ul>
                        {workflow.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>

                <div className="data-guide-callout" style={{ marginTop: '2rem' }}>
                  <h3>How the pipeline works</h3>
                  <p>
                    The form below creates a real request in the shared automation backlog. The board on this page reads back from that same Notion database, so the intake view and the working Kanban are meant to stay synchronized instead of drifting apart.
                  </p>
                </div>
                <div className="data-guide-callout">
                  <h3>What happens after you submit?</h3>
                  <p>
                    A good next step is to review whether the request is really an automation, a dashboard, a better report, a better process, or something that should stay manual. The form is meant to capture the work clearly enough that the team can make that call.
                  </p>
                  <p>
                    A simple triage pattern is: repetitive manual work {'->'} source systems involved {'->'} sensitivity or approvals {'->'} desired outcome {'->'} whether the answer should be a workflow, a daily report, or a one-time fix.
                  </p>
                </div>
                <div className="data-guide-stat-row">
                  {automationStatusOrder.map((status) => (
                    <article key={status} className="data-guide-stat-card">
                      <span className="data-guide-stat-value">{automationCounts[status] || 0}</span>
                      <span className="data-guide-stat-label">{status}</span>
                    </article>
                  ))}
                </div>
                <div className="data-guide-automation-toolbar">
                  <div className="data-guide-automation-sync">
                    <strong>Board sync</strong>
                    <span>{automationBoardStatus}</span>
                  </div>
                  <div className="data-guide-automation-sync">
                    <strong>Source</strong>
                    <span>{automationBoardSource === 'notion' ? 'Shared Notion board' : 'Fallback example cards'}</span>
                  </div>
                </div>
                {automationBoardError && (
                  <div className="data-guide-callout">
                    <h3>Board sync note</h3>
                    <p>{automationBoardError}</p>
                  </div>
                )}
                {automationSubmitMessage && (
                  <div className="data-guide-callout">
                    <h3>Submission update</h3>
                    <p>{automationSubmitMessage}</p>
                  </div>
                )}
                <form className="data-guide-idea-form" onSubmit={handleAutomationIdeaSubmit}>
                  <div className="data-guide-idea-form-grid">
                    <label className="data-guide-idea-field">
                      <span>Your name</span>
                      <input
                        name="name"
                        type="text"
                        value={automationIdeaDraft.name}
                        onChange={handleAutomationIdeaChange}
                        placeholder="Optional"
                      />
                    </label>
                    <label className="data-guide-idea-field">
                      <span>Brand</span>
                      <select
                        name="brand"
                        value={automationIdeaDraft.brand}
                        onChange={handleAutomationIdeaChange}
                      >
                        {automationBrandOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <label className="data-guide-idea-field">
                      <span>Department</span>
                      <select
                        name="department"
                        value={automationIdeaDraft.department}
                        onChange={handleAutomationIdeaChange}
                      >
                        {automationDepartmentOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <label className="data-guide-idea-field">
                      <span>Priority</span>
                      <select
                        name="priority"
                        value={automationIdeaDraft.priority}
                        onChange={handleAutomationIdeaChange}
                      >
                        {automationPriorityOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="data-guide-idea-field">
                    <span>What do you wish was automated?</span>
                    <textarea
                      name="task"
                      value={automationIdeaDraft.task}
                      onChange={handleAutomationIdeaChange}
                      placeholder="Example: Every morning I rebuild the same update for leadership."
                      rows={2}
                    />
                  </label>
                  <label className="data-guide-idea-field">
                    <span>How is it done today?</span>
                    <textarea
                      name="currentProcess"
                      value={automationIdeaDraft.currentProcess}
                      onChange={handleAutomationIdeaChange}
                      placeholder="Example: I pull numbers from three sheets, copy them to Slack, and write notes by hand."
                      rows={2}
                    />
                  </label>
                  <label className="data-guide-idea-field">
                    <span>What would a better result look like?</span>
                    <textarea
                      name="desiredOutcome"
                      value={automationIdeaDraft.desiredOutcome}
                      onChange={handleAutomationIdeaChange}
                      placeholder="Example: A daily summary with the right metrics, source notes, and freshness checks."
                      rows={2}
                    />
                  </label>
                  <button type="submit" className="data-guide-idea-submit" disabled={automationSubmitting}>
                    {automationSubmitting ? 'Sending to automation board...' : 'Send to automation board'}
                  </button>
                </form>
                <div className="data-guide-automation-board">
                  {automationLanes.map((lane) => (
                    <section key={lane.status} className="data-guide-automation-lane">
                      <div className="data-guide-automation-lane-header">
                        <h3>{lane.status}</h3>
                        <span>{lane.items.length}</span>
                      </div>
                      <p className="data-guide-automation-lane-copy">
                        {lane.status === 'Not started' && 'Fresh requests and ideas that still need triage or design.'}
                        {lane.status === 'In progress' && 'Requests that are actively being specified, built, or operationalized.'}
                        {lane.status === 'Live' && 'Shipped automations that people can learn from or reuse.'}
                      </p>
                      <div className="data-guide-automation-lane-stack">
                        {lane.items.length === 0 && (
                          <article className="data-guide-automation-card data-guide-automation-card-empty">
                            <p>No cards in this lane right now.</p>
                          </article>
                        )}
                        {lane.items.map((idea) => (
                          <article key={idea.id} className="data-guide-automation-card data-guide-automation-card--compact">
                            <div className="data-guide-automation-card__top">
                              <h4>{idea.title}</h4>
                              {idea.url && (
                                <a className="data-guide-automation-card__link" href={idea.url} target="_blank" rel="noopener noreferrer" title="Open in Notion">
                                  ↗
                                </a>
                              )}
                            </div>
                            <div className="data-guide-automation-meta">
                              <span className="data-guide-automation-pill">{idea.department}</span>
                              <span className="data-guide-automation-pill">{idea.priority}</span>
                              {idea.brands.map((brand) => (
                                <span key={`${idea.id}-${brand}`} className="data-guide-automation-pill">{brand}</span>
                              ))}
                            </div>
                            {(idea.summary || idea.overview) && (
                              <p className="data-guide-automation-card__summary">{idea.summary || idea.overview}</p>
                            )}
                          </article>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </section>

              {/* ── Section 5: Guardrails ── */}
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Guardrails</h2>
                </div>
                <div className="data-guide-callout">
                  <h3>Do not let AI blur the truth layers</h3>
                  <p>
                    The highest-risk failure mode is when AI answers a direct-truth question with a modeled source, or uses a warehouse table without checking freshness and lineage. The second failure mode is giving a coding agent broad production power before the team has agreed on access boundaries.
                  </p>
                </div>
                <div className="data-guide-rule-grid">
                  <article className="data-guide-rule-card">
                    <h3>Do not use AI as a silent decision-maker</h3>
                    <p>AI should support spend decisions, attribution analysis, operational debugging, and reporting design. It should not silently choose the source of truth or make production business decisions without review.</p>
                  </article>
                  <article className="data-guide-rule-card">
                    <h3>Do not grant more access than the workflow needs</h3>
                    <p>Start with read-only AWS and query access. Add write or secret access only when the exact workflow justifies it.</p>
                  </article>
                  <article className="data-guide-rule-card">
                    <h3>Do not confuse connected AI with source truth</h3>
                    <p>Even when Claude is connected to Slack, Notion, Superset, Sheets, and AWS, the systems underneath still define what the answer means.</p>
                  </article>
                </div>
              </section>

              {/* ── Section 6: Resources & FAQ ── */}
              <section className="data-guide-section" id="ai-resource-library">
                <div className="section-header data-guide-section-header">
                  <h2>Resources and FAQ</h2>
                </div>

                <div className="section-header data-guide-section-header" style={{ marginTop: '1rem' }}>
                  <h3>Questions people usually ask</h3>
                </div>
                <div className="data-guide-rule-grid">
                  {aiFaqCards.map((card) => (
                    <article key={card.question} className="data-guide-rule-card">
                      <h3>{card.question}</h3>
                      <p>{card.answer}</p>
                    </article>
                  ))}
                </div>

                <div className="section-header data-guide-section-header" style={{ marginTop: '2rem' }}>
                  <h3>Learning resources</h3>
                </div>
                <p className="data-guide-section-copy">
                  Curated courses, webinars, and docs from Anthropic and AWS. Start with the &ldquo;for everyone&rdquo; tier. Developer resources are in the accordion below.
                </p>

                <div className="data-guide-link-section-header" style={{ marginTop: '1rem' }}>
                  <h4>For everyone</h4>
                  <p>Business-friendly courses, beginner guides, and plain-English explainers.</p>
                </div>
                <div className="data-guide-links-grid">
                  {businessResources.map((resource) => (
                    <article key={resource.title} className="data-guide-link-card">
                      <h3>{resource.title}</h3>
                      <p>{resource.description}</p>
                      <a href={resource.url} className="data-guide-link" target="_blank" rel="noopener noreferrer">
                        Open resource
                      </a>
                    </article>
                  ))}
                </div>

                <Accordion id="developer-resources" title="For developers">
                  <p className="data-guide-section-copy" style={{ marginBottom: '1rem' }}>
                    API courses, MCP protocol docs, Bedrock IAM setup, AWS workshops, and advanced webinars.
                  </p>
                  <div className="data-guide-links-grid">
                    {developerResources.map((resource) => (
                      <article key={resource.title} className="data-guide-link-card">
                        <h3>{resource.title}</h3>
                        <p>{resource.description}</p>
                        <a href={resource.url} className="data-guide-link" target="_blank" rel="noopener noreferrer">
                          Open resource
                        </a>
                      </article>
                    ))}
                  </div>
                </Accordion>

                <div className="section-header data-guide-section-header" style={{ marginTop: '2rem' }}>
                  <h3>Downloadable starter artifacts</h3>
                </div>
                <p className="data-guide-section-copy">
                  Starter files, machine-readable indexes, and quick references for teams setting up AI ways of working.
                </p>
                <div className="data-guide-link-sections">
                  <section className="data-guide-link-section">
                    <div className="data-guide-links-grid">
                      {aiDownloadArtifacts.map((artifact) => (
                        <article key={artifact.title} className="data-guide-link-card">
                          <h3>{artifact.title}</h3>
                          <p>{artifact.description}</p>
                          <a href={artifact.href} download className="data-guide-link">
                            {artifact.label}
                          </a>
                        </article>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="section-header data-guide-section-header" style={{ marginTop: '2rem' }}>
                  <h3>How Claude connects to the rest of the stack</h3>
                </div>
                <p className="data-guide-section-copy">
                  Claude becomes meaningfully more useful when it is connected to the same systems the team already uses. MCPs and connectors are the glue layer that makes those systems available without forcing people to copy and paste everything manually.
                </p>
                <div className="data-guide-diagram-grid">
                  <article className="data-guide-diagram-card">
                    <h3>Connected AI workflow</h3>
                    <div className="data-guide-diagram-flow">
                      <div className="data-guide-diagram-node">
                        <strong>Claude surfaces</strong>
                        <p>Claude, Claude CoWork, Claude Code</p>
                      </div>
                      <div className="data-guide-diagram-arrow">&rarr;</div>
                      <div className="data-guide-diagram-node">
                        <strong>MCPs and connectors</strong>
                        <p>Slack, Notion, browser workflows, Superset, Sheets, internal apps</p>
                      </div>
                      <div className="data-guide-diagram-arrow">&rarr;</div>
                      <div className="data-guide-diagram-node">
                        <strong>Mars Men systems</strong>
                        <p>AWS, dashboards, docs, code, source systems, reporting surfaces</p>
                      </div>
                    </div>
                  </article>

                  <article className="data-guide-diagram-card">
                    <h3>Operating principle</h3>
                    <div className="data-guide-truth-stack">
                      <div className="data-guide-truth-band platform">Ask the right business question first</div>
                      <div className="data-guide-truth-band attribution">Connect Claude to the right system second</div>
                      <div className="data-guide-truth-band finance">Let humans own the final business decision</div>
                    </div>
                    <p className="data-guide-diagram-note">
                      The mistake is treating AI as a source of truth. The useful pattern is to let AI inspect, query, summarize, and draft while the source systems and business owners still define the truth.
                    </p>
                  </article>
                </div>
              </section>
            </>
          )}

          {/* ── TECHNICAL GUIDE ── */}
          {!isSearching && activeTab === 'technical' && (
            <>
              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>The role AWS plays in this stack</h2>
                </div>
                <p className="data-guide-section-copy">
                  AWS is the shared data and automation layer. It is where data is landed, cataloged, queried, and delivered. It is not usually the system of record by itself. The system of record is still the direct platform unless the question is explicitly about a modeled or derived view.
                </p>
                <div className="data-guide-callout">
                  <h3>Current morning control plane</h3>
                  <p>
                    As of April 5, 2026, the live Brain path is <strong>EventBridge Scheduler {'->'} Standard Step Functions {'->'} ECS RefreshPhase {'->'} ECS PublishPhase</strong>, followed by the separate anomaly Lambda at 10:00 CT. The old Brain scheduler and state machine still exist in AWS, but they are no longer the active morning route.
                  </p>
                </div>
                <div className="data-guide-callout">
                  <h3>Brain app boundary</h3>
                  <p>
                    The Brain frontend is a separate internal SPA at <code>/brain-dashboard</code>. It does not query Athena directly from the browser. It calls the authenticated Brand API routes under <code>/analytics/brain-dashboard/v1/*</code>, which are backed by the analytics-brain Lambda.
                  </p>
                </div>
                <div className="data-guide-aws-grid">
                  {awsCards.map((card) => (
                    <article key={card.title} className="data-guide-aws-card">
                      <h3>{card.title}</h3>
                      <p>{card.body}</p>
                    </article>
                  ))}
                </div>
                <div className="data-guide-callout">
                  <h3>What people should do first</h3>
                  <p>
                    If you are trying to consume data from AWS, start with Athena and the Glue catalog using a read-only analytics role. Do not start by asking for production secrets or write access. The goal is to inspect what is already landed before expanding privileges.
                  </p>
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Northbeam warehouse families</h2>
                </div>
                <p className="data-guide-section-copy">
                  Northbeam currently has two distinct warehouse families. The team should be explicit about which one they are using because they serve different purposes.
                </p>
                <Accordion id="warehouse-families" title="Warehouse family details">
                  <div className="data-guide-warehouse-grid">
                    {warehouseCards.map((card) => (
                      <article key={card.title} className="data-guide-warehouse-card">
                        <h3>{card.title}</h3>
                        <ul className="data-guide-tag-list">
                          {card.tables.map((table) => (
                            <li key={table}>{table}</li>
                          ))}
                        </ul>
                        <div className="data-guide-warehouse-copy">
                          <p><strong>Purpose:</strong> {card.purpose}</p>
                          <p><strong>Use when:</strong> {card.useWhen}</p>
                          <p><strong>Avoid when:</strong> {card.avoidWhen}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                </Accordion>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Operational jobs and cadence</h2>
                </div>
                <p className="data-guide-section-copy">
                  These are the active ingestion, reporting, and refresh jobs that keep the stack current. This is the section to use when someone asks &ldquo;what job is responsible for this data?&rdquo; or &ldquo;how fresh should I expect this table or report to be?&rdquo;
                </p>
                <div className="data-guide-callout">
                  <h3>Live-check note</h3>
                  <p>
                    This inventory has been tightened against the current EventBridge footprint as of April 5, 2026. It now distinguishes the active Brain morning runtime from older legacy brief schedules that still exist in AWS but are no longer the main reporting path.
                  </p>
                </div>
                <Accordion id="operational-jobs" title={`View operational job inventory (${operationalJobs.length} jobs)`}>
                  <div className="data-guide-table-wrap">
                    <table className="data-guide-table">
                      <thead>
                        <tr>
                          <th>Job / pipeline</th>
                          <th>Domain</th>
                          <th>Business output</th>
                          <th>Cadence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {operationalJobs.map((job) => (
                          <tr key={job.job}>
                            <td>{job.job}</td>
                            <td>{job.domain}</td>
                            <td>{job.businessOutput}</td>
                            <td>{job.cadence}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Accordion>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Morning readiness by source</h2>
                </div>
                <p className="data-guide-section-copy">
                  A 9:00 AM brief only works when the upstream sources behind it are genuinely ready. This matrix is the practical companion to the job inventory above: it calls out which systems are safe by the morning run, which are intentionally lagged, and which should only publish with a partial or freshness warning.
                </p>
                <div className="data-guide-callout">
                  <h3>Readiness rule</h3>
                  <p>
                    Schedules alone are not enough. The Brain should publish on freshness gates, not blind faith that every upstream landed on time. Tatari, finance ACT, and partially direct paid-media reads should all be treated explicitly as conditional when they are late or incomplete.
                  </p>
                </div>
                <div className="data-guide-table-wrap">
                  <table className="data-guide-table">
                    <thead>
                      <tr>
                        <th>Source / pipeline</th>
                        <th>Expected window</th>
                        <th>Safe by 09:00 CT?</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {morningReadinessRows.map((row) => (
                        <tr key={row.sourceOrPipeline}>
                          <td>{row.sourceOrPipeline}</td>
                          <td>{row.expectedWindow}</td>
                          <td>{row.safeByNine}</td>
                          <td>{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Slack-facing jobs and delivery surfaces</h2>
                </div>
                <p className="data-guide-section-copy">
                  Slack is both a destination and an operating surface in this stack. The rows below are the jobs and utilities that either post directly to Slack or exist specifically to support Slack delivery.
                </p>
                <Accordion id="slack-delivery" title={`View Slack delivery inventory (${slackSurfaces.length} jobs and utilities)`}>
                  <div className="data-guide-table-wrap">
                    <table className="data-guide-table">
                      <thead>
                        <tr>
                          <th>Job / surface</th>
                          <th>Domain</th>
                          <th>Purpose</th>
                          <th>Cadence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slackSurfaces.map((surface) => (
                          <tr key={surface.surface}>
                            <td>{surface.surface}</td>
                            <td>{surface.domain}</td>
                            <td>{surface.purpose}</td>
                            <td>{surface.cadence}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Accordion>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Claude Code access model</h2>
                </div>
                <p className="data-guide-section-copy">
                  The right starting point is a read-only analytics role. That gives the team enough access to inspect code, query Athena, and use the warehouse safely without giving them broad production privileges.
                </p>
                <div className="data-guide-callout">
                  <h3>Repo boundary</h3>
                  <p>
                    Raw source landing and lake-first ingestion should keep moving toward the data-platform side of the stack. Brain marts, reporting runtime orchestration, and delivery contracts belong with the reporting layer. That separation is now part of the intended operating model, not just an implementation detail.
                  </p>
                </div>
                <div className="data-guide-callout">
                  <h3>Default Mars Men AWS profile</h3>
                  <p>
                    For Mars Men work, the standard local AWS profile is usually <code>marsmen-direct</code>. That is the profile to use for Athena, Glue, Bedrock, and any approved scoped Secrets Manager reads unless a workflow explicitly requires a different account.
                  </p>
                </div>
                <div className="data-guide-access-grid">
                  {accessCards.map((card) => (
                    <article key={card.title} className="data-guide-access-card">
                      <h3>{card.title}</h3>
                      <ul>
                        {card.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
                <div className="data-guide-callout">
                  <h3>Can this still make Athena calls?</h3>
                  <p>
                    Yes. The read-only analytics role should still allow Athena query execution, Glue reads, and S3 query-result access. The restriction is around platform mutation and secrets access, not around querying the warehouse.
                  </p>
                </div>
                <div className="data-guide-callout">
                  <h3>How to use Secrets Manager with Claude Code</h3>
                  <p>
                    Do not start with Secrets Manager when the question is about data. Start with Athena, the Glue catalog, and the repo. Escalate to Secrets Manager only when the workflow depends on secret-backed configuration, such as checking which client ID, token, or endpoint a production job is actually using.
                  </p>
                  <p>
                    The right access pattern is read-only and scoped to named secrets or narrow prefixes. Avoid broad Secrets Manager access for general exploration. That keeps Claude Code useful for debugging integrations without turning the default analytics role into an admin role.
                  </p>
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Claude surfaces and what they are for</h2>
                </div>
                <p className="data-guide-section-copy">
                  The team uses the word "Claude" loosely, but these are different working surfaces. The distinction matters because each one has a different level of repo access, AWS access, and operational power.
                </p>
                <div className="data-guide-callout">
                  <h3>Recommended model path</h3>
                  <p>
                    For Mars Men work inside AWS, the recommended default is <strong>Claude and other approved models through Amazon Bedrock</strong>. That keeps model access inside the AWS permission model instead of scattering vendor credentials across local-only workflows.
                  </p>
                </div>
                <div className="data-guide-access-grid">
                  {claudeSurfaces.map((surface) => (
                    <article key={surface.title} className="data-guide-access-card">
                      <h3>{surface.title}</h3>
                      <p><strong>Use for:</strong> {surface.useFor}</p>
                      <p><strong>Not for:</strong> {surface.notFor}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>Secrets Manager families people should know exist</h2>
                </div>
                <p className="data-guide-section-copy">
                  Yes, the team should have a map of what kinds of secrets exist. The right level for this page is secret <strong>families and purposes</strong>, not raw secret values. Use this section to understand what lives in Secrets Manager and when Claude Code should ask for scoped access.
                </p>
                <div className="data-guide-callout">
                  <h3>How to read this section</h3>
                  <p>
                    This is an operational inventory, not a live dump of every secret name in AWS. Exact secret names should be accessed only with approved read permissions. Broad listing is not part of the default analytics workflow.
                  </p>
                  <p>
                    The current <code>marsmen-direct</code> inventory includes <strong>56 secrets</strong>. The examples below are drawn from that live namespace so the team can see the real families without turning the page into a full secret browser.
                  </p>
                </div>
                <div className="data-guide-table-wrap">
                  <table className="data-guide-table">
                    <thead>
                      <tr>
                        <th>Secret family</th>
                        <th>Typical examples</th>
                        <th>Used for</th>
                        <th>When Claude Code should use it</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {secretFamilyRows.map((row) => (
                        <tr key={row.family}>
                          <td>{row.family}</td>
                          <td>{row.examples}</td>
                          <td>{row.usedFor}</td>
                          <td>{row.whenNeeded}</td>
                          <td>{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="data-guide-section">
                <div className="section-header data-guide-section-header">
                  <h2>How to use Claude Code with this stack</h2>
                </div>
                <div className="data-guide-claude-grid">
                  {claudeSteps.map((step) => (
                    <article key={step.step} className="data-guide-claude-step">
                      <span className="data-guide-claude-step-number">{step.step}</span>
                      <h3>{step.title}</h3>
                      <p>{step.body}</p>
                    </article>
                  ))}
                </div>
              </section>
            </>
          )}

        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>{footerText}</p>
          <div className="footer-links">
            {footerLinks.map((link, index) => (
              <React.Fragment key={link.label}>
                <a href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
                {index < footerLinks.length - 1 && <span className="separator">&bull;</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataPlatformGuide;
